import { sql, relations } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  integer,
  date,
  decimal,
  timestamp,
  pgEnum,
  boolean,
  json,
  jsonb,
  index,
  uniqueIndex,
  primaryKey
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
// Helpers de telefone moram em shared/mercado.ts (sem drizzle) para o CLIENT poder
// validar o campo do modal sem arrastar o schema inteiro para o bundle.
import { normalizarTelefoneBR, telefoneWhatsappValido } from "./mercado.js";
// O item de registro próprio da carga (Mercado C5a) — tipa o JSONB de
// `mercado_carga_itens.itens`. Mora em shared/mercado-comprador.ts pelo mesmo
// motivo dos helpers de telefone: a tela do comprador monta a tabela de itens e
// não pode arrastar o drizzle para o bundle.
import type { DadosPagamento, ItemRegistrado } from "./mercado-comprador.js";
// Avisos pelo WhatsApp (migração 078) — tipos puros, sem drizzle, que o client
// também usa (editor da Plataforma, Meus avisos).
import type { AvisoChave, AvisoCategoria, AvisoFilaStatus } from "./avisos.js";

// Enums
// GESTOR e LEITURA entram nas migrações 068/069 (PR 2 do permissionamento). A
// matriz de capacidades de cada papel mora em shared/permissoes.ts.
export const userRoleEnum = pgEnum("user_role", ["ADMIN", "GESTOR", "CAMPO", "ENCARREGADO", "LEITURA"]);
export const colheitaStatusEnum = pgEnum("colheita_status", ["NO_PRAZO", "ATRASADA", "ANTECIPADA", "SEM_PLANO"]);
export const cicloStatusEnum = pgEnum("ciclo_status", ["ATIVO", "FECHADO", "FECHADO_MANUAL"]);
export const setorTipoEnum = pgEnum("setor_tipo", ["PRODUTIVO", "SERVICO"]);

// ─── Organizations ────────────────────────────────────────────────────────────
export const organizations = pgTable("organizations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  nome: text("nome").notNull(),
  slug: text("slug").notNull().unique(),
  plano: text("plano").notNull().default("starter"),
  ativo: boolean("ativo").notNull().default(true),
  modulesEnabled: json("modules_enabled").$type<string[]>().notNull().default(sql`'["cacho"]'::json`),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  trialEndsAt: timestamp("trial_ends_at"),
  // ⚠️ `erp_provider` SAIU daqui (Operações v2): não existe mais conector de ERP.
  // A coluna continua no banco até a migração 053 — contrato do expand.
  // Dia do mês em que o FECHAMENTO MENSAL é pago (migração 027). Usado só para
  // calcular e exibir a data do próximo fechamento ("Fecha mês em sáb 05/09").
  // Dia que não existe no mês (31 em fevereiro) cai no último dia — nunca
  // escorrega para o mês seguinte. Ver dataPagamentoDaCompetencia().
  diaPagamentoMensal: integer("dia_pagamento_mensal").default(5),
  // Executor atribuído automaticamente a operações criadas por processo automático
  // (migração 041). ⚠️ ÓRFÃO desde as Operações v2: o único leitor era o pull do
  // Aegro. Fica porque a geração por RECEITA (ciclo 4) é a candidata natural a
  // reusá-lo. NULL = sem atribuição automática, que é o estado de hoje.
  //
  // 🔴 mnj_operacao_executor é JOIN PURO: sem valor, sem quantidade, NUNCA gera
  // tarefa nem diária. Um executor padrão em 13 operações por dia viraria 13× a
  // diária se houvesse qualquer acoplamento — não há, e nada aqui pode introduzir.
  mnjExecutorPadraoFuncionarioId: varchar("mnj_executor_padrao_funcionario_id"),
  // ─── Identidade da organização no Mercado (migrações 054 e 057) ────────────
  // `tipo` nasce 'PRODUTOR' em TODA org. 'COMPRADOR' só passa a existir quando
  // alguém faz o claim de um link (C2) — nada no código promove ninguém sozinho.
  // ⚠️ Era 'CORRETOR' até a 057; ver o comentário de `ORG_TIPO`.
  tipo: text("tipo").notNull().default("PRODUTOR"),
  // CORRETOR | DISTRIBUIDOR — o PERFIL de uma org COMPRADOR (migração 057). NULL
  // em org PRODUTOR. É perfil, não papel: os dois compram do produtor e usam a
  // mesma visão; o que muda é como se apresentam (e, no C5, margem, que é do
  // corretor). O claim pergunta ("Sou corretor" / "Sou distribuidor").
  perfilComprador: text("perfil_comprador"),
  cnpjCpf: text("cnpj_cpf"),
  // Número normalizado (só dígitos, com DDD) para montar o link wa.me. Não confundir
  // com telefone de cadastro em texto livre.
  telefoneWhatsapp: text("telefone_whatsapp"),
  nomeFantasia: text("nome_fantasia"),
  // A cidade da organização (migração 065) — o que distingue "Sávio Nunes de
  // Irecê" de um homônimo de outra praça na lista de Plataforma. `compradores`
  // (a caderneta do produtor) sempre teve; a organização, não.
  cidade: text("cidade"),
  // Praça padrão do Mercado (migração 056) — o filtro que a tela de Cotações abre
  // selecionado. NULL = "não escolheu", e a LEITURA resolve na PRIMEIRA PRAÇA
  // ATIVA do cadastro (`pracaDaOrg`), nunca num slug constante: até a 060 isto
  // caía em `'irece'` literal, que continuou apontando para uma praça já
  // renomeada para Mirorós. Default chumbado na coluna teria o mesmo defeito,
  // congelado mais fundo.
  praca: text("praca"),
  // As praças em que o COMPRADOR atua (migração 058) — outra coisa de `praca`,
  // que é a praça padrão do PRODUTOR. É LISTA porque corretor de Irecê compra em
  // Irecê e em Mirorós no mesmo dia. Vazia = ainda não escolheu, e a tela Praça
  // cai nas praças dos produtores com vínculo ATIVO.
  pracas: text("pracas").array().notNull().default(sql`'{}'::text[]`),
  // Dados bancários de quem EMITE o documento (migração 062) —
  // { pix, banco, agencia, conta, titular }. Cadastro, digitado uma vez.
  //
  // ⚠️ A CARGA tira um SNAPSHOT disto ao fechar (`mercado_cargas.pagamento_snapshot`),
  // e o link público lê o snapshot, nunca esta coluna: trocar a chave PIX amanhã
  // não pode reescrever o que o distribuidor leu no documento de ontem.
  dadosPagamento: jsonb("dados_pagamento").$type<DadosPagamento>(),
  // trialEndsAt = null  → grandfathered (Fazenda Boa Vista) — acesso sempre liberado
  // trialEndsAt = data futura → trial ativo
  // trialEndsAt = data passada + sem stripeSubscriptionId → trial expirado → bloquear
  // (a regra, numa função só: `statusDaOrg` em shared/org-status.ts)
  // ─── Plataforma (migração 067) ──────────────────────────────────────────
  // Nota do Rizoma sobre a conta — nunca visível à org.
  observacoesPlataforma: text("observacoes_plataforma"),
  // SIGNUP (entrou pelo cadastro) | CORTESIA (o Rizoma liberou). Diz POR QUE
  // `trial_ends_at` está como está.
  origemTrial: text("origem_trial").notNull().default("SIGNUP"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export type Organization = typeof organizations.$inferSelect;
export type InsertOrganization = typeof organizations.$inferInsert;

// ─── Culturas ─────────────────────────────────────────────────────────────────
// Entidade de 1ª classe (migração 042 — substitui o pgEnum `cultura` do #135).
// org_id NULL = cultura GLOBAL curada (banana, citros — imutável pela API,
// visível a todas as orgs). org_id preenchido = cultura CUSTOM daquela org.
// slug é a chave que o código consulta (shared/culturas.ts); nome é o rótulo
// exibido, editável pelo usuário nas culturas custom — nunca usar `nome` em
// lógica de comparação/roteamento, só em exibição.
export const culturas = pgTable("culturas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").references(() => organizations.id),
  slug: text("slug").notNull(),
  nome: text("nome").notNull(),
  icone: text("icone"),
  ativa: boolean("ativa").notNull().default(true),
  ordem: integer("ordem").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type Cultura = typeof culturas.$inferSelect;
export type InsertCultura = typeof culturas.$inferInsert;

// Users table
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  // ⚠️ OPCIONAL desde a migração 055 (Mercado C2). Muitos corretores não têm e-mail
  // de uso corrente; para eles o identificador de login é o TELEFONE e o e-mail só
  // serve para recuperação. Continua único — no Postgres um índice único não compara
  // NULLs entre si, então N contas sem e-mail convivem. O CHECK do banco garante que
  // toda conta tenha e-mail OU telefone: sem nenhum dos dois é uma conta em que é
  // impossível entrar.
  email: text("email").unique(),
  password: text("password").notNull(),
  name: text("name").notNull(),
  // Identificador de login do corretor. Só dígitos, com DDD (ver
  // `normalizarTelefoneBR` em shared/mercado.ts) — é a mesma normalização do
  // `destino_telefone` do compartilhamento, e é isso que faz o claim encontrar o
  // histórico daquele número. Único PARCIAL (só entre quem tem telefone).
  telefone: text("telefone"),
  role: userRoleEnum("role").notNull().default("CAMPO"),
  // Qual FUNCIONÁRIO este usuário é (migração 052) — o que permite "Minhas
  // operações" no Manejo. Nullable: sem vínculo o operador vê todas as operações
  // do dia, comportamento aceitável hoje. Vínculo MOLE, como todo o resto entre
  // Manejo e Equipe: não cria diária nem carrega valor.
  //
  // Sem `.references()` no Drizzle porque `funcionarios` é declarada mais abaixo
  // neste arquivo — mesmo caso de `organizations.mnjExecutorPadraoFuncionarioId`.
  // A FK existe no banco (migração 052); aqui é só a coluna.
  funcionarioId: varchar("funcionario_id"),
  // ─── Identidade viva (migração 066) ─────────────────────────────────────
  // Desativar em vez de excluir: o histórico (audit, diária, operação) aponta
  // para users.id e não pode ficar órfão. Inativo não entra, e a sessão viva
  // dele cai em ≤ 60 s (ver server/lib/sessao-cache.ts).
  ativo: boolean("ativo").notNull().default(true),
  // Quem administra o RIZOMA (módulo Plataforma) — não é papel de org.
  plataformaAdmin: boolean("plataforma_admin").notNull().default(false),
  // Módulos que ESTE usuário vê, dentro dos da org. NULL = todos os da org.
  // O efetivo é `modulosEfetivos(org.modulesEnabled, user.modulos)`.
  modulos: json("modulos").$type<string[]>(),
  // O token carrega `sv`; trocar papel/módulos/senha ou desativar incrementa
  // aqui e a sessão antiga passa a ser recusada — efeito imediato, sem esperar
  // o JWT expirar.
  sessaoVersao: integer("sessao_versao").notNull().default(1),
  ultimoLoginEm: timestamp("ultimo_login_em"),
  // Convidado por link (migração 070): nasceu com senha aleatória e ainda não
  // definiu a dele. O login recusa com o mesmo 401 genérico enquanto for true.
  convitePendente: boolean("convite_pendente").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// ─── Tokens de convite e reset (migração 070) ─────────────────────────────
// Só o sha256 do token vai para o banco. Uso único (`usadoEm`); gerar um novo
// do mesmo tipo invalida os anteriores. Ver server/lib/usuario-tokens.ts.
export const usuarioTokens = pgTable("usuario_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  tipo: text("tipo").notNull(), // CONVITE | RESET
  tokenHash: text("token_hash").notNull().unique(),
  expiraEm: timestamp("expira_em").notNull(),
  usadoEm: timestamp("usado_em"),
  criadoPorUserId: varchar("criado_por_user_id").references(() => users.id),
  criadoEm: timestamp("criado_em").defaultNow().notNull(),
});

export type UsuarioToken = typeof usuarioTokens.$inferSelect;

// ─── Acessos de suporte da Plataforma (migração 071) ─────────────────────
// "Entrar como" (leitura): pedido pela plataforma, autorizado pelo admin da org,
// vale por até 72 h. Ver server/lib/plataforma/acessos.ts.
export const plataformaAcessos = pgTable("plataforma_acessos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  solicitanteUserId: varchar("solicitante_user_id").notNull().references(() => users.id),
  motivo: text("motivo").notNull(),
  duracaoHoras: integer("duracao_horas").notNull().default(24),
  status: text("status").notNull().default("PENDENTE"), // PENDENTE | AUTORIZADO | RECUSADO | ENCERRADO | EXPIRADO
  autorizadoPorUserId: varchar("autorizado_por_user_id").references(() => users.id),
  autorizadoEm: timestamp("autorizado_em"),
  expiraEm: timestamp("expira_em"),
  encerradoEm: timestamp("encerrado_em"),
  criadoEm: timestamp("criado_em").defaultNow().notNull(),
});

export type PlataformaAcesso = typeof plataformaAcessos.$inferSelect;
export const STATUS_ACESSO_SUPORTE = ['PENDENTE', 'AUTORIZADO', 'RECUSADO', 'ENCERRADO', 'EXPIRADO'] as const;
export type StatusAcessoSuporte = typeof STATUS_ACESSO_SUPORTE[number];

// Variedades (Banana Varieties) table
export const variedades = pgTable("variedades", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  pesoMedioCacho: decimal("peso_medio_cacho", { precision: 5, scale: 2 }).notNull(), // kg
  // Semanas de maturação por mês (JSON: { "1": 13, "2": 12, ... "12": 14 })
  semanasMaturacaoPorMes: json("semanas_maturacao_por_mes").$type<Record<string, number>>().notNull(),
  ativo: boolean("ativo").notNull().default(true),
  // Obrigatória (migração 042) — toda variedade pertence a uma cultura, global
  // ou custom da própria org. Validado na escrita (server/lib/cultura.ts):
  // referenciar cultura de outra org é 400, nunca silêncio.
  culturaId: varchar("cultura_id").notNull().references(() => culturas.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("variedades_org_nome_idx").on(t.orgId, t.nome),
]);

// Setores (Farm Sectors) table
export const setores = pgTable("setores", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  tipo: setorTipoEnum("tipo").notNull().default("PRODUTIVO"),
  variedadeId: varchar("variedade_id").references(() => variedades.id),
  areaHectares: decimal("area_hectares", { precision: 6, scale: 2 }),
  quantidadeMudas: integer("quantidade_mudas").notNull().default(0),
  dataPlantio: date("data_plantio"),
  origemMudas: text("origem_mudas"),
  ativo: boolean("ativo").notNull().default(true),
  // Único campo trazido pelo módulo Manejo (Rizoma_Arquitetura_Sync_Aegro.md §4.2) —
  // setores é o objeto físico canônico, mnj_talhao_ref não existe mais. Booleano
  // EXPLÍCITO, nunca derivado de `tipo` (PRODUTIVO/SERVICO): nem todo setor produtivo
  // é fertirrigado, e derivar criaria acoplamento frágil entre os dois módulos.
  fertirrigavel: boolean("fertirrigavel").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("setores_org_nome_idx").on(t.orgId, t.nome),
]);

// Cores de Fitas (Ribbon Colors) table
export const coresFitas = pgTable("cores_fitas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  hexColor: text("hex_color").notNull(), // Cor hexadecimal para exibição (#FF0000)
  aliases: json("aliases").$type<string[]>().notNull().default(sql`'[]'::json`), // ["rosa", "pink", "rosa claro"]
  ativo: boolean("ativo").notNull().default(true),
  ordem: integer("ordem").notNull().default(0), // Ordem de exibição definida pelo usuário
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("cores_fitas_org_nome_idx").on(t.orgId, t.nome),
]);

// Ciclos de Produção (Production Cycles) table
export const ciclos = pgTable("ciclos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  codigo: text("codigo").notNull(), // Formato: cor_variedade_mesAno (ex: "verde_limao_prata_nov25")
  corFitaId: varchar("cor_fita_id").notNull().references(() => coresFitas.id),
  variedadeId: varchar("variedade_id").references(() => variedades.id), // Nullable for existing cycles, will be populated on next mangara registration
  mesAno: text("mes_ano").notNull(), // Formato: "jan2025", "fev2025"
  status: cicloStatusEnum("status").notNull().default("ATIVO"),
  totalPlanejado: integer("total_planejado").notNull().default(0),
  totalColhido: integer("total_colhido").notNull().default(0),
  totalPerdido: integer("total_perdido").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  fechadoEm: timestamp("fechado_em"),
  fechadoPorUserId: varchar("fechado_por_user_id").references(() => users.id),
}, (t) => [
  uniqueIndex("ciclos_org_codigo_idx").on(t.orgId, t.codigo),
]);

// Mangarás (Flower Cuts) table
export const mangaras = pgTable("mangaras", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  corFitaId: varchar("cor_fita_id").notNull().references(() => coresFitas.id),
  cicloId: varchar("ciclo_id").notNull().references(() => ciclos.id),
  dataCorteMangara: date("data_corte_mangara").notNull(),
  semanaCorteMangara: integer("semana_corte_mangara"), // Semana ISO do corte (1-53)
  anoSemanaCorteMangara: integer("ano_semana_corte_mangara"), // Ano da semana ISO
  dataPrevistaColheita: date("data_prevista_colheita").notNull(),
  semanaPrevistaColheita: integer("semana_prevista_colheita"), // Semana ISO prevista para colheita (1-53)
  anoSemanaPrevistaColheita: integer("ano_semana_prevista_colheita"), // Ano da semana ISO prevista
  quantidadeCachos: integer("quantidade_cachos").notNull(),
  observacoes: text("observacoes"),
  usuarioId: varchar("usuario_id").notNull().references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Colheitas (Harvests) table
export const colheitas = pgTable("colheitas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  corFitaId: varchar("cor_fita_id").references(() => coresFitas.id), // Pode ser null para "sem fita"
  cicloId: varchar("ciclo_id").references(() => ciclos.id), // Pode ser null para "sem plano"
  dataColheita: date("data_colheita").notNull(),
  semanaColheita: integer("semana_colheita"), // Semana ISO da colheita (1-53)
  anoSemanaColheita: integer("ano_semana_colheita"), // Ano da semana ISO
  quantidadeCachos: integer("quantidade_cachos").notNull(),
  // Carimbo HISTÓRICO da variedade (migração 043). Copiado de setores.variedadeId no
  // momento do registro e nunca reescrito: `setores.variedade_id` é o valor de HOJE, sem
  // histórico, e o rateio é uma tabela RECALCULADA — derivar a variedade do setor faria
  // um replantio (ou uma correção de cadastro) reescrever meses fechados de histórico.
  // Nullable: colheita antiga sem backfill → rateio faz fallback para o setor.
  variedadeId: varchar("variedade_id").references(() => variedades.id),
  status: colheitaStatusEnum("status").notNull(),
  semFita: boolean("sem_fita").notNull().default(false), // Marca colheitas onde a fita foi perdida
  foraTolerancia: boolean("fora_tolerancia").notNull().default(false), // Marca se a colheita foi associada fora da janela de tolerância
  observacoes: text("observacoes"),
  usuarioId: varchar("usuario_id").notNull().references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Motivos de Perda (Loss Reasons) table
export const motivosPerdas = pgTable("motivos_perdas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  descricao: text("descricao"),
  ativo: boolean("ativo").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("motivos_perdas_org_nome_idx").on(t.orgId, t.nome),
]);

// Parâmetros do Sistema (System Parameters) table
export const parametros = pgTable("parametros", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  chave: text("chave").notNull(), // Ex: "TOLERANCIA_DIAS_COLHEITA"
  valor: text("valor").notNull(), // Valor como string (conversão no código)
  descricao: text("descricao"),
  tipo: text("tipo").notNull().default("INTEGER"), // INTEGER, STRING, BOOLEAN, DECIMAL
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("parametros_org_chave_idx").on(t.orgId, t.chave),
]);

// App Version table (global — não é org-scoped)
export const appVersion = pgTable("app_version", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dataVersao: text("data_versao").notNull(), // YYYYMMDD format
  buildNumber: integer("build_number").notNull().default(1),
  versaoCompleta: text("versao_completa").notNull(), // vYYYYMMDD.##
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Perdas (Losses) table
export const perdas = pgTable("perdas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  cicloId: varchar("ciclo_id").notNull().references(() => ciclos.id),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  quantidadeCachos: integer("quantidade_cachos").notNull(),
  motivoPerdaId: varchar("motivo_perda_id").notNull().references(() => motivosPerdas.id),
  observacoes: text("observacoes"),
  usuarioId: varchar("usuario_id").notNull().references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Audit Log — registro imutável de mutações críticas do sistema
export const auditLogs = pgTable("audit_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  userId: varchar("user_id").references(() => users.id), // null em eventos de sistema
  userEmail: text("user_email"), // desnormalizado para leitura rápida mesmo se usuário deletado
  action: text("action").notNull(), // ex: "mangara.create", "ciclo.close", "usuario.create"
  entityType: text("entity_type"), // ex: "mangara", "ciclo", "usuario", "colheita"
  entityId: varchar("entity_id"), // id da entidade afetada
  metadata: json("metadata").$type<Record<string, unknown>>(), // detalhes adicionais
  ip: text("ip"), // IP da request (para auditoria de acesso)
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type AuditLog = typeof auditLogs.$inferSelect;
export type InsertAuditLog = typeof auditLogs.$inferInsert;

// ═══════════════════════════════════════════════════════════════════════════
// MÓDULO EQUIPE
// ═══════════════════════════════════════════════════════════════════════════

export const quinzenaStatusEnum = pgEnum("quinzena_status", ["ABERTA", "FECHADA", "PAGA"]);
export const tarefaStatusEnum = pgEnum("tarefa_status", ["PENDENTE", "EM_ANDAMENTO", "CONCLUIDA", "CANCELADA", "FALTOU"]);
export const diariaTypeEnum = pgEnum("diaria_type", ["NORMAL", "MEIA_DIARIA", "HORA_EXTRA", "DESCONTO", "ADIANTAMENTO", "EMPREITA", "MENSALISTA"]);

export const funcionarios = pgTable("funcionarios", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  apelido: text("apelido"),
  cargo: text("cargo").notNull().default("Diarista"),
  setorPrincipalId: varchar("setor_principal_id").references(() => setores.id),
  pixChave: text("pix_chave"),
  pixTipo: text("pix_tipo"),
  valorDiaria: decimal("valor_diaria", { precision: 8, scale: 2 }).notNull().default("0"),
  ativo: boolean("ativo").notNull().default(true),
  observacoes: text("observacoes"),
  // ─── Regime de remuneração (migração 024) ─────────────────────────────────
  // Todo mundo nasce DIARISTA: a migração não muda o comportamento de ninguém.
  // Só existem DOIS regimes no banco. "Encarregado de Setor" é rótulo DERIVADO
  // (mensalista com vínculo ativo em funcionario_setor) — nunca um 3º valor aqui.
  tipoRemuneracao: text("tipo_remuneracao").notNull().default("DIARISTA"),
  // Obrigatório quando MENSALISTA (validarRegimeFuncionario, abaixo).
  salarioMensal: decimal("salario_mensal", { precision: 10, scale: 2 }),
  // Os quatro campos abaixo NÃO são usados na Fase 1 — existem só no cadastro,
  // para não gerar uma migração de uma coluna só nas Fases 2/3 (adiantamento
  // quinzenal, fechamento mensal, variável, adicional por kg).
  formaPagamento: text("forma_pagamento").notNull().default("MENSAL"),
  adiantamentoPct: decimal("adiantamento_pct", { precision: 5, scale: 2 }).default("50"),
  variavelMaxMensal: decimal("variavel_max_mensal", { precision: 10, scale: 2 }).default("0"),
  valorKgColheita: decimal("valor_kg_colheita", { precision: 6, scale: 4 }),
  // ─── Remuneração — Fase 2 (migração 053) ──────────────────────────────────
  // FIXO = usa salarioMensal digitado (comportamento de hoje). DERIVADO =
  // calcula por hectare via rem_taxa. Nasce FIXO: a migração não muda o
  // pagamento de ninguém — o Hugo vira a chave pessoa a pessoa.
  salarioModo: text("salario_modo").notNull().default("FIXO"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("funcionarios_org_nome_idx").on(t.orgId, t.nome),
]);

export type Funcionario = typeof funcionarios.$inferSelect;
export type InsertFuncionario = typeof funcionarios.$inferInsert;
export const insertFuncionarioSchema = createInsertSchema(funcionarios).omit({ id: true, createdAt: true });

export const tipoRemuneracaoValues = ["DIARISTA", "MENSALISTA"] as const;
export const formaPagamentoValues = ["MENSAL", "QUINZENAL"] as const;
export type TipoRemuneracao = (typeof tipoRemuneracaoValues)[number];
export type FormaPagamento = (typeof formaPagamentoValues)[number];

/**
 * I10 + I14 — valida o regime sobre o ESTADO FINAL do funcionário (o que já está
 * no banco + o patch), nunca só sobre o corpo da request: um PATCH que troca só o
 * regime não reenvia o salário que já existe, e validar o corpo isolado recusaria
 * uma edição legítima.
 *
 * Retorna a mensagem de erro (→ 400) ou null quando o estado é válido.
 */
export function validarRegimeFuncionario(f: {
  tipoRemuneracao?: string | null;
  salarioMensal?: string | number | null;
  formaPagamento?: string | null;
}): string | null {
  const tipo = f.tipoRemuneracao ?? "DIARISTA";
  if (!(tipoRemuneracaoValues as readonly string[]).includes(tipo)) {
    return `tipoRemuneracao inválido: ${tipo}. Use DIARISTA ou MENSALISTA.`;
  }
  const forma = f.formaPagamento ?? "MENSAL";
  if (!(formaPagamentoValues as readonly string[]).includes(forma)) {
    return `formaPagamento inválido: ${forma}. Use MENSAL ou QUINZENAL.`;
  }
  if (tipo === "MENSALISTA") {
    const salario = Number(f.salarioMensal ?? 0);
    if (f.salarioMensal == null || f.salarioMensal === "" || !Number.isFinite(salario) || salario <= 0) {
      return "Mensalista exige salário mensal maior que zero (salarioMensal).";
    }
  }
  return null;
}

/**
 * I14 — `forma_pagamento` só se aplica a MENSALISTA. Para DIARISTA o campo é
 * ignorado: o servidor normaliza de volta para o default em vez de guardar um
 * valor que ninguém vai ler (e que confundiria o fechamento da Fase 2).
 */
export function normalizarRegimeFuncionario<T extends {
  tipoRemuneracao?: string | null;
  formaPagamento?: string | null;
}>(f: T): T {
  const tipo = f.tipoRemuneracao ?? "DIARISTA";
  if (tipo === "DIARISTA" && f.formaPagamento !== undefined) {
    return { ...f, formaPagamento: "MENSAL" };
  }
  return f;
}

/**
 * I15 — só MENSALISTA tem vínculo de setor, validado contra o **ESTADO FINAL da
 * operação** (payload + banco), nunca contra o estado anterior isolado.
 *
 * O que isso conserta: virar Encarregado direto de Diarista era impossível. A tela
 * chamava `POST /api/equipe/funcionario-setor` na hora do "Vincular", e o servidor
 * lia o regime PERSISTIDO — ainda DIARISTA, porque a troca de perfil só existia no
 * estado do formulário. O gestor tinha que salvar como Mensalista, reabrir, e só
 * então vincular. A validação estava certa; o momento em que ela rodava é que
 * estava errado.
 *
 * Um mesmo Salvar pode legitimamente mudar o regime E criar vínculos — é por isso
 * que os dois viajam no mesmo payload e são gravados na mesma transação.
 *
 * Retorna a mensagem de erro (→ 400) ou null.
 */
export function validarVinculosDoRegime(args: {
  tipoRemuneracaoFinal?: string | null;
  qtdVinculosAbertosFinal: number;
}): string | null {
  const tipo = args.tipoRemuneracaoFinal ?? "DIARISTA";
  if (tipo !== "MENSALISTA" && args.qtdVinculosAbertosFinal > 0) {
    return "Diarista não pode ser encarregado de setor. Encerre os setores dele antes de mudar o regime para Diarista.";
  }
  return null;
}

/**
 * Estado DESEJADO dos vínculos abertos, enviado junto com o funcionário. Item com
 * `id` é vínculo que já existe (mantém, talvez com `inicio` corrigido); sem `id` é
 * vínculo novo. Vínculo aberto que o servidor tem e que NÃO está na lista é
 * ENCERRADO (fim), nunca deletado — o histórico é o contrato com a auditoria.
 *
 * Campo ausente no payload = não reconciliar nada. É o que mantém intactos os
 * PATCH que não falam de setor (reativar funcionário, editar PIX, etc.).
 */
export const vinculoSetorDesejadoSchema = z.object({
  id: z.string().optional(),
  setorId: z.string().min(1, "setorId é obrigatório"),
  inicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "inicio deve ser uma data YYYY-MM-DD"),
});
export type VinculoSetorDesejado = z.infer<typeof vinculoSetorDesejadoSchema>;

/**
 * Vínculo funcionário ↔ setor COM VIGÊNCIA DATADA (migração 024).
 *
 * Um mensalista com ≥1 vínculo ativo (fim IS NULL) é rotulado "Encarregado de
 * Setor" na UI — o rótulo é derivado daqui, não de uma coluna de regime.
 *
 * A vigência é datada (e o vínculo NUNCA é deletado, só encerrado) porque é isto
 * que permite responder "quem respondia pelo A2 em agosto/26" quando a auditoria
 * da Fase 2/3 existir. Tirar um encarregado = encerrar a vigência dele, nunca
 * deletar a linha.
 *
 * `inicio` é um FATO DE CAMPO, não a data em que alguém digitou: a dupla assumiu
 * os setores em 15/07/2026 mesmo tendo sido cadastrada em agosto. Por isso é
 * editável e aceita data retroativa.
 *
 * `setores` é o talhão canônico já existente (o mesmo do Cacho e do Manejo).
 */
export const funcionarioSetor = pgTable("funcionario_setor", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  funcionarioId: varchar("funcionario_id").notNull().references(() => funcionarios.id),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  papel: text("papel").notNull().default("ENCARREGADO"),
  inicio: date("inicio").notNull(),
  fim: date("fim"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  // I6 — um setor aceita N encarregados ativos ao mesmo tempo: na fazenda
  // trabalha-se EM DUPLA (Renho e Dáu respondem juntos por A1, A2, B3, C3, C4).
  // O único caso proibido é duplicar o MESMO PAR pessoa↔setor em aberto, que
  // seria só uma linha repetida atrapalhando a auditoria. Migração 025.
  // PARCIAL em `fim IS NULL`: o histórico pode repetir o par à vontade (sair do
  // setor e voltar depois = duas linhas encerradas + uma aberta).
  uniqueIndex("uniq_funcionario_setor_par_ativo")
    .on(t.orgId, t.setorId, t.funcionarioId).where(sql`fim IS NULL`),
  index("idx_funcionario_setor_func").on(t.orgId, t.funcionarioId),
]);

export type FuncionarioSetor = typeof funcionarioSetor.$inferSelect;
export type InsertFuncionarioSetor = typeof funcionarioSetor.$inferInsert;
export const insertFuncionarioSetorSchema = createInsertSchema(funcionarioSetor)
  .omit({ id: true, createdAt: true })
  .extend({
    inicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "inicio deve ser uma data YYYY-MM-DD"),
    fim: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "fim deve ser uma data YYYY-MM-DD").nullable().optional(),
  });

// Categoria da ATIVIDADE (catálogo compartilhado com o Equipe). Define ícone/cor
// do card e se a tela pede calda (`pulverizacao`, `foliar`).
export const ATIVIDADE_CATEGORIA = ["fertirrigacao", "pulverizacao", "foliar", "tratos_culturais", "manutencao", "outro"] as const;
export type AtividadeCategoria = (typeof ATIVIDADE_CATEGORIA)[number];

export const atividades = pgTable("atividades", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  descricao: text("descricao"),
  icone: text("icone").default("shovel"),
  cor: text("cor").default("#2D5016"),
  ativo: boolean("ativo").notNull().default(true),
  acompanharPorSetor: boolean("acompanhar_por_setor").notNull().default(true),
  // Nullable de propósito (migração 042) — null = vale para todas as culturas,
  // comportamento default. Nenhuma tela de trabalho pergunta cultura; este campo
  // só existe no cadastro (Admin > Atividades) e alimenta o radar de
  // relatorios.tsx via server/lib/equipe/radar-aplicabilidade.ts.
  culturaId: varchar("cultura_id").references(() => culturas.id),
  // Categoria de MANEJO (migração 052). Define o ícone/cor do card de operação e
  // se a tela pede volume de calda (`pulverizacao`, `foliar`). Nullable: null =
  // não classificada, que é o estado de todas as atividades já cadastradas.
  // Mora aqui, e não numa tabela do Manejo, porque `atividades` é o catálogo
  // único — duplicá-lo é exatamente o que a Sprint 1 desfez.
  categoria: text("categoria").$type<AtividadeCategoria>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("atividades_org_nome_idx").on(t.orgId, t.nome),
]);

export type Atividade = typeof atividades.$inferSelect;
export type InsertAtividade = typeof atividades.$inferInsert;
export const insertAtividadeSchema = createInsertSchema(atividades).omit({ id: true, createdAt: true }).extend({
  culturaId: z.string().nullable().optional(),
  // Explícito porque `createInsertSchema` traduz `text().$type<...>()` para
  // `z.string()` — sem isto, "Pulverizações" chegaria ao banco e o CHECK da 052
  // viraria um 500 mudo em vez de um 400 com a lista de valores válidos.
  categoria: z.enum(ATIVIDADE_CATEGORIA).nullable().optional(),
});

export const quinzenas = pgTable("quinzenas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  periodoInicio: date("periodo_inicio").notNull(),
  periodoFim: date("periodo_fim").notNull(),
  status: quinzenaStatusEnum("status").notNull().default("ABERTA"),
  totalBruto: decimal("total_bruto", { precision: 10, scale: 2 }).notNull().default("0"),
  fechadoEm: timestamp("fechado_em"),
  fechadoPorUserId: varchar("fechado_por_user_id").references(() => users.id),
  pagoEm: timestamp("pago_em"),
  pagoPorUserId: varchar("pago_por_user_id").references(() => users.id),
  comprovanteUrl: text("comprovante_url"),
  observacoes: text("observacoes"),
  // Quando o dinheiro da quinzena SAI (migração 027). Null = ainda não definida.
  //
  // É o que decide a COMPETÊNCIA do adiantamento do mensalista (D11): a quinzena
  // 18/07–01/08 é paga em 01/08, então o adiantamento dela é de agosto e abate no
  // fechamento de AGOSTO. Sem esta data não dá para gerar adiantamento — o
  // servidor recusa com 409 SEM_DATA_PAGAMENTO em vez de chutar o mês.
  //
  // NÃO confundir com `pago_em`: aquele é o carimbo de quando alguém marcou como
  // paga (fato consumado); este é a data planejada, definida antes.
  dataPagamento: date("data_pagamento"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("quinzenas_org_periodo_idx").on(t.orgId, t.periodoInicio),
]);

export type Quinzena = typeof quinzenas.$inferSelect;
export type InsertQuinzena = typeof quinzenas.$inferInsert;
export const insertQuinzenaSchema = createInsertSchema(quinzenas).omit({ id: true, createdAt: true });

export const diarias = pgTable("diarias", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  quinzenaId: varchar("quinzena_id").notNull().references(() => quinzenas.id),
  funcionarioId: varchar("funcionario_id").notNull().references(() => funcionarios.id),
  data: date("data").notNull(),
  tipo: diariaTypeEnum("tipo").notNull().default("NORMAL"),
  valor: decimal("valor", { precision: 8, scale: 2 }).notNull(),
  comentario: text("comentario"),
  descricao: text("descricao"),
  atividadeId: varchar("atividade_id").references(() => atividades.id),
  setorId: varchar("setor_id").references(() => setores.id),
  registradoPorUserId: varchar("registrado_por_user_id").references(() => users.id),
  tarefaId: varchar("tarefa_id").references(() => tarefas.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type Diaria = typeof diarias.$inferSelect;
export type InsertDiaria = typeof diarias.$inferInsert;
export const insertDiariaSchema = createInsertSchema(diarias).omit({ id: true, createdAt: true });

/**
 * ADIANTAMENTO QUINZENAL do mensalista (migração 027).
 *
 * ⚠️ NÃO CONFUNDIR com a diária de tipo `ADIANTAMENTO`, que já existe e tem
 * sinal OPOSTO: aquela é do DIARISTA e **abate** do líquido dele na quinzena
 * (`calcFormula`: bruto − descontos − adiantamentos). Esta aqui é do MENSALISTA
 * e **soma** no total que sai da quinzena — é dinheiro a pagar, metade do
 * salário adiantada. As duas nunca se encontram: o mensalista não entra em
 * `calcFormula`, e esta tabela não é diária.
 *
 * `valor` é SNAPSHOT: nasce de `salario_mensal × adiantamento_pct / 100`, é
 * editável antes de confirmar e congela depois. Se o salário mudar em novembro,
 * o adiantamento de agosto continua o que foi pago em agosto.
 */
export const adiantamentoQuinzenal = pgTable("adiantamento_quinzenal", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  funcionarioId: varchar("funcionario_id").notNull().references(() => funcionarios.id),
  quinzenaId: varchar("quinzena_id").notNull().references(() => quinzenas.id),
  // 'YYYY-MM' — mês de INÍCIO da quinzena (D11 v2.7), não da data de pagamento nem do resto do período.
  competencia: text("competencia").notNull(),
  valor: decimal("valor", { precision: 10, scale: 2 }).notNull(),
  status: text("status").notNull().default("PREVISTO"),
  pagoEm: timestamp("pago_em", { withTimezone: true }),
  // AUTOMATICO (gatilhos do #129) | MANUAL (diálogo de geração) | EXTERNO (já
  // pago fora do sistema — migração 046). Só EXTERNO aceita quinzena FECHADA.
  origem: text("origem").notNull().default("AUTOMATICO"),
  // Obrigatória quando `origem = 'EXTERNO'`: um abatimento no fechamento que
  // ninguém consegue explicar depois é a mesma dívida que este PR está pagando.
  observacao: text("observacao"),
  createdByUserId: varchar("created_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  // Um adiantamento por mensalista por mês. Duas quinzenas caem na mesma
  // competência quando as duas são pagas no mesmo mês — e aí só uma gera
  // adiantamento, senão o fechamento abateria o dobro.
  uniqueIndex("uniq_adiantamento_func_competencia").on(t.orgId, t.funcionarioId, t.competencia),
  index("idx_adiantamento_quinzena").on(t.orgId, t.quinzenaId),
]);

export type AdiantamentoQuinzenal = typeof adiantamentoQuinzenal.$inferSelect;
export type InsertAdiantamentoQuinzenal = typeof adiantamentoQuinzenal.$inferInsert;

export const adiantamentoStatusValues = ["PREVISTO", "PAGO"] as const;
export const adiantamentoOrigemValues = ["AUTOMATICO", "MANUAL", "EXTERNO"] as const;

/**
 * "Conferi esta competência e não há adiantamento a registrar" (migração 046).
 *
 * Existe para que a ausência de adiantamento seja um FATO REGISTRADO e não a
 * ausência de linha — foi ausência-lida-como-ausência-de-direito que deixou o
 * fechamento de 2026-08 passar pagando salário cheio, e é a mesma raiz do #122.
 * Caso legítimo: pessoa que virou quinzenal no meio do mês.
 */
export const adiantamentoDispensa = pgTable("adiantamento_dispensa", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  funcionarioId: varchar("funcionario_id").notNull().references(() => funcionarios.id),
  competencia: text("competencia").notNull(),
  motivo: text("motivo").notNull(),
  createdByUserId: varchar("created_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uniq_adiantamento_dispensa_func_competencia").on(t.orgId, t.funcionarioId, t.competencia),
]);

export type AdiantamentoDispensa = typeof adiantamentoDispensa.$inferSelect;
export type InsertAdiantamentoDispensa = typeof adiantamentoDispensa.$inferInsert;

/**
 * FECHAMENTO MENSAL do mensalista (migração 027).
 *
 * Tudo aqui é SNAPSHOT: o fechamento de agosto tem de continuar contando a mesma
 * história em dezembro, mesmo que salário, teto e regime da pessoa tenham mudado
 * no meio. Nada é recalculado na leitura.
 *
 * `variavel_apurada` é preenchida à mão nesta fase. Quando a auditoria existir,
 * ela escreve NESTE MESMO campo — não criar estrutura paralela.
 */
export const fechamentoMensal = pgTable("fechamento_mensal", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  funcionarioId: varchar("funcionario_id").notNull().references(() => funcionarios.id),
  competencia: text("competencia").notNull(), // 'YYYY-MM'
  salarioFixo: decimal("salario_fixo", { precision: 10, scale: 2 }),
  formaPagamento: text("forma_pagamento"),
  // Σ dos adiantamentos com status PAGO da competência. Para quem é MENSAL isso
  // dá 0 por construção — e é por isso que a fórmula do líquido não tem ramo.
  adiantamentosPagos: decimal("adiantamentos_pagos", { precision: 10, scale: 2 }).default("0"),
  variavelApurada: decimal("variavel_apurada", { precision: 10, scale: 2 }).default("0"),
  variavelObs: text("variavel_obs"),
  // Contexto da competência, congelado na geração: quantos dias de registro e
  // quantas faltas. `dias_falta` vem de TAREFAS (FALTOU é status de tarefa e
  // remove a diária), `dias_registro` das diárias de tipo MENSALISTA.
  diasRegistro: integer("dias_registro").default(0),
  diasFalta: integer("dias_falta").default(0),
  descontos: decimal("descontos", { precision: 10, scale: 2 }).default("0"),
  descontosObs: text("descontos_obs"),
  liquidoFinal: decimal("liquido_final", { precision: 10, scale: 2 }),
  status: text("status").notNull().default("ABERTO"),
  fechadoEm: timestamp("fechado_em", { withTimezone: true }),
  fechadoPorUserId: varchar("fechado_por_user_id").references(() => users.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uniq_fechamento_func_competencia").on(t.orgId, t.funcionarioId, t.competencia),
  index("idx_fechamento_competencia").on(t.orgId, t.competencia),
]);

export type FechamentoMensal = typeof fechamentoMensal.$inferSelect;
export type InsertFechamentoMensal = typeof fechamentoMensal.$inferInsert;

export const fechamentoStatusValues = ["ABERTO", "FECHADO"] as const;

export const tarefas = pgTable("tarefas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  funcionarioId: varchar("funcionario_id").notNull().references(() => funcionarios.id),
  atividadeId: varchar("atividade_id").references(() => atividades.id),
  setorId: varchar("setor_id").references(() => setores.id),
  // Multi-setor: array de IDs. setorId acima = setorIds[0] (representante, mantido por compat).
  setorIds: json("setor_ids").$type<string[]>().notNull().default(sql`'[]'::json`),
  data: date("data").notNull(),
  status: tarefaStatusEnum("status").notNull().default("PENDENTE"),
  tipo: diariaTypeEnum("tipo").notNull().default("NORMAL"),
  valor: decimal("valor", { precision: 8, scale: 2 }).notNull().default("0"),
  observacoes: text("observacoes"),
  criadoPorUserId: varchar("criado_por_user_id").references(() => users.id),
  concluidoEm: timestamp("concluido_em"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type Tarefa = typeof tarefas.$inferSelect;
export type InsertTarefa = typeof tarefas.$inferInsert;
export const insertTarefaSchema = createInsertSchema(tarefas)
  .omit({ id: true, createdAt: true })
  .extend({ setorIds: z.array(z.string().uuid()).optional() });

/**
 * PAGAMENTO ADICIONAL na tarefa (Fase 3, migração 030) — um valor extra por
 * funcionário, lançado junto com a tarefa, pago na quinzena. Caso principal:
 * colheita por kg.
 *
 * ⚠️ NÃO é diária. Nunca passa pelo consolidador (`recomputarDiariaDoDia`),
 * nunca cria/altera/lê `diarias`, nunca encosta em `uniq_diaria_trabalho_dia`.
 * É uma natureza de pagamento SEPARADA, somada só no total da quinzena — um
 * funcionário pode ter diária NORMAL (R$ 95) + adicional (R$ 193,90) no mesmo
 * dia, e são duas linhas distintas em tabelas distintas. Vale para qualquer
 * regime (diarista, mensalista, encarregado).
 *
 * `kg`/`valorKg` são o caminho de COLHEITA (kg × R$/kg, calculado no
 * servidor); `OUTRO` aceita `valor` digitado direto. `valorKg` é SNAPSHOT do
 * cadastro (`funcionarios.valorKgColheita`) no momento do lançamento — mudar
 * o cadastro depois não altera nada já lançado.
 *
 * Sem UNIQUE de propósito: uma pessoa pode ter vários adicionais na mesma
 * tarefa (casos excepcionais) e vários na mesma quinzena (o cortador colhe
 * várias vezes) — isso é normal, não duplicata.
 */
export const tarefaAdicional = pgTable("tarefa_adicional", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  // NULLABLE desde a 046: desconto não nasce de tarefa nenhuma ("quebrou a
  // ferramenta", "vale adiantado"). O nome da tabela ficou herdado da Fase 3 —
  // hoje ela guarda LANÇAMENTOS da quinzena, com ou sem tarefa por trás.
  tarefaId: varchar("tarefa_id").references(() => tarefas.id),
  funcionarioId: varchar("funcionario_id").notNull().references(() => funcionarios.id),
  // Resolvida pela data da tarefa (I11 — exige quinzena ABERTA), igual à diária.
  quinzenaId: varchar("quinzena_id").notNull().references(() => quinzenas.id),
  data: date("data").notNull(), // denormalizada da tarefa, para relatório sem join
  tipo: text("tipo").notNull().default("COLHEITA"),
  // ADICIONAL (+) | DESCONTO (−) — migração 046. `valor` continua SEMPRE
  // POSITIVO: o sinal mora aqui, e as agregações devolvem as duas naturezas em
  // colunas separadas, nunca numa soma misturada.
  sinal: text("sinal").notNull().default("ADICIONAL"),
  descricao: text("descricao"),
  kg: decimal("kg", { precision: 10, scale: 2 }),
  valorKg: decimal("valor_kg", { precision: 6, scale: 4 }), // snapshot no lançamento
  valor: decimal("valor", { precision: 10, scale: 2 }).notNull(), // snapshot: kg×valorKg, ou digitado
  createdByUserId: varchar("created_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  // As duas leituras quentes: "adicionais desta quinzena" (Financeiro) e
  // "adicionais desta tarefa" (bloco na tela da tarefa).
  index("idx_tarefa_adicional_quinzena").on(t.orgId, t.quinzenaId),
  index("idx_tarefa_adicional_tarefa").on(t.orgId, t.tarefaId),
]);

export type TarefaAdicional = typeof tarefaAdicional.$inferSelect;
export type InsertTarefaAdicional = typeof tarefaAdicional.$inferInsert;

// AUXILIO — Remuneração Fase 2 (migração 053): o auxílio recorrente (ex.:
// combustível) do mês vira um lançamento aqui (sinal=ADICIONAL, tarefaId
// nulo), não uma tabela paralela. Ver rem_auxilio_recorrente, abaixo.
export const tarefaAdicionalTipoValues = ["COLHEITA", "OUTRO", "AUXILIO"] as const;
export const lancamentoSinalValues = ["ADICIONAL", "DESCONTO"] as const;

// ─── Remuneração do encarregado — Fase 2 (migração 053) ────────────────────
// Taxa por hectare + a REGRA do auxílio recorrente, para o cálculo derivado
// do salário fixo/variável. Nunca vê nota — isso é Auditoria/Manejo; aqui só
// entra o percentual publicado por GET /api/auditoria/resultados. Toda taxa
// tem VIGÊNCIA: nunca se edita uma linha existente, sempre se cria outra.
//
// Só TRÊS tabelas, de propósito: o lançamento mensal do auxílio NÃO mora
// aqui — é um tarefa_adicional (tipo='AUXILIO'), reaproveitando as
// agregações e o bloco do Financeiro que já existem (ver §0(b) do documento).

export const remTaxa = pgTable("rem_taxa", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  vigenciaInicio: date("vigencia_inicio").notNull(),
  valorFixoHa: decimal("valor_fixo_ha", { precision: 10, scale: 2 }).notNull(),
  valorVariavelHa: decimal("valor_variavel_ha", { precision: 10, scale: 2 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("rem_taxa_org_vigencia_idx").on(t.orgId, t.vigenciaInicio),
]);

export type RemTaxa = typeof remTaxa.$inferSelect;
export const insertRemTaxaSchema = createInsertSchema(remTaxa).omit({
  id: true, orgId: true, createdAt: true,
});

/**
 * PATCH /taxas/:id — só os VALORES são editáveis, nunca `vigenciaInicio`
 * (trocar a data seria efetivamente criar outra vigência). Corrige um valor
 * digitado errado; não é reajuste. Bloqueado no storage quando a vigência já
 * foi consumida por um fechamento (`TAXA_JA_USADA`).
 */
export const patchRemTaxaSchema = z.object({
  valorFixoHa: z.union([z.string(), z.number()]).transform(v => String(v)).optional(),
  valorVariavelHa: z.union([z.string(), z.number()]).transform(v => String(v)).optional(),
}).refine(b => b.valorFixoHa !== undefined || b.valorVariavelHa !== undefined, {
  message: "Informe ao menos um valor para alterar.",
});

/** Exceção de taxa por setor dentro de uma vigência. NÃO é preenchimento obrigatório dos setores. */
export const remTaxaSetor = pgTable("rem_taxa_setor", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  remTaxaId: varchar("rem_taxa_id").notNull().references(() => remTaxa.id, { onDelete: "cascade" }),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  valorFixoHa: decimal("valor_fixo_ha", { precision: 10, scale: 2 }),
  valorVariavelHa: decimal("valor_variavel_ha", { precision: 10, scale: 2 }),
  // Exceção sem motivo escrito é exceção esquecida.
  observacao: text("observacao").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("rem_taxa_setor_idx").on(t.orgId, t.remTaxaId, t.setorId),
]);

export type RemTaxaSetor = typeof remTaxaSetor.$inferSelect;

/** Substitui as exceções INTEIRAS de uma vigência de uma vez — mesmo padrão de audFaixasReplaceSchema. */
export const remTaxaSetorReplaceSchema = z.object({
  excecoes: z.array(z.object({
    setorId: z.string().uuid(),
    valorFixoHa: z.union([z.string(), z.number()]).nullish().transform(v => (v == null ? null : String(v))),
    valorVariavelHa: z.union([z.string(), z.number()]).nullish().transform(v => (v == null ? null : String(v))),
    observacao: z.string().min(1, "Observação é obrigatória — exceção sem motivo escrito é exceção esquecida"),
  })),
});

/**
 * A REGRA do auxílio recorrente (ex.: combustível). O lançamento DAQUELE mês
 * é gerado como `tarefa_adicional` (tipo='AUXILIO') — nunca uma linha aqui.
 * Editar o valor de um mês já gerado não altera esta regra.
 */
export const remAuxilioRecorrente = pgTable("rem_auxilio_recorrente", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  funcionarioId: varchar("funcionario_id").notNull().references(() => funcionarios.id),
  descricao: text("descricao").notNull(), // 'Auxílio combustível'
  valor: decimal("valor", { precision: 10, scale: 2 }).notNull(),
  ativo: boolean("ativo").notNull().default(true),
  observacao: text("observacao"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("rem_auxilio_recorrente_ativo_idx").on(t.orgId, t.funcionarioId, t.descricao).where(sql`ativo`),
]);

export type RemAuxilioRecorrente = typeof remAuxilioRecorrente.$inferSelect;
export const insertRemAuxilioRecorrenteSchema = createInsertSchema(remAuxilioRecorrente).omit({
  id: true, orgId: true, createdAt: true,
});
export const updateRemAuxilioRecorrenteSchema = insertRemAuxilioRecorrenteSchema.partial();

export const salarioModoValues = ["FIXO", "DERIVADO"] as const;
export type SalarioModo = (typeof salarioModoValues)[number];

// Relations
export const usersRelations = relations(users, ({ many }) => ({
  mangaras: many(mangaras),
  colheitas: many(colheitas),
  perdas: many(perdas),
}));

export const variedadesRelations = relations(variedades, ({ many }) => ({
  setores: many(setores),
}));

export const setoresRelations = relations(setores, ({ one, many }) => ({
  variedade: one(variedades, {
    fields: [setores.variedadeId],
    references: [variedades.id],
  }),
  mangaras: many(mangaras),
  colheitas: many(colheitas),
  perdas: many(perdas),
}));

export const coresFitasRelations = relations(coresFitas, ({ many }) => ({
  ciclos: many(ciclos),
  mangaras: many(mangaras),
  colheitas: many(colheitas),
}));

export const ciclosRelations = relations(ciclos, ({ one, many }) => ({
  corFita: one(coresFitas, {
    fields: [ciclos.corFitaId],
    references: [coresFitas.id],
  }),
  mangaras: many(mangaras),
  colheitas: many(colheitas),
  perdas: many(perdas),
}));

export const mangarasRelations = relations(mangaras, ({ one }) => ({
  setor: one(setores, {
    fields: [mangaras.setorId],
    references: [setores.id],
  }),
  corFita: one(coresFitas, {
    fields: [mangaras.corFitaId],
    references: [coresFitas.id],
  }),
  ciclo: one(ciclos, {
    fields: [mangaras.cicloId],
    references: [ciclos.id],
  }),
  usuario: one(users, {
    fields: [mangaras.usuarioId],
    references: [users.id],
  }),
}));

export const colheitasRelations = relations(colheitas, ({ one }) => ({
  setor: one(setores, {
    fields: [colheitas.setorId],
    references: [setores.id],
  }),
  corFita: one(coresFitas, {
    fields: [colheitas.corFitaId],
    references: [coresFitas.id],
  }),
  ciclo: one(ciclos, {
    fields: [colheitas.cicloId],
    references: [ciclos.id],
  }),
  usuario: one(users, {
    fields: [colheitas.usuarioId],
    references: [users.id],
  }),
}));

export const motivosPerdasRelations = relations(motivosPerdas, ({ many }) => ({
  perdas: many(perdas),
}));

export const perdasRelations = relations(perdas, ({ one }) => ({
  ciclo: one(ciclos, {
    fields: [perdas.cicloId],
    references: [ciclos.id],
  }),
  setor: one(setores, {
    fields: [perdas.setorId],
    references: [setores.id],
  }),
  motivoPerda: one(motivosPerdas, {
    fields: [perdas.motivoPerdaId],
    references: [motivosPerdas.id],
  }),
  usuario: one(users, {
    fields: [perdas.usuarioId],
    references: [users.id],
  }),
}));

// Insert Schemas with Zod validation
export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  orgId: true,
  createdAt: true,
}).extend({
  // Continua OBRIGATÓRIO neste schema: ele é o cadastro de usuário feito por um
  // ADMIN dentro da org (tela Usuários), onde e-mail é o identificador e sempre
  // existiu. A conta sem e-mail é só a do corretor, que nasce pelo claim e não
  // passa por aqui.
  email: z.string().email("Email inválido"),
  password: z.string().min(6, "Senha deve ter no mínimo 6 caracteres"),
  name: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
});

export const insertVariedadeSchema = createInsertSchema(variedades).omit({
  id: true,
  orgId: true,
  createdAt: true,
}).extend({
  nome: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
  pesoMedioCacho: z.string().refine(val => parseFloat(val) > 0, "Peso deve ser maior que zero"),
  semanasMaturacaoPorMes: z.record(z.string(), z.number().int().positive()),
  culturaId: z.string().min(1, "Selecione a cultura"),
});

export const insertCulturaSchema = createInsertSchema(culturas).omit({
  id: true,
  orgId: true,
  slug: true,
  createdAt: true,
}).extend({
  nome: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
});
export const updateCulturaSchema = insertCulturaSchema.partial();

export const insertSetorSchema = createInsertSchema(setores).omit({
  id: true,
  orgId: true,
  createdAt: true,
}).extend({
  nome: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
  tipo: z.enum(["PRODUTIVO", "SERVICO"]).default("PRODUTIVO"),
  areaHectares: z.string().nullable().optional(),
  quantidadeMudas: z.number().int().min(0).default(0),
  dataPlantio: z.string().nullable().optional(),
  variedadeId: z.string().nullable().optional(),
}).superRefine((data, ctx) => {
  if ((data.tipo ?? "PRODUTIVO") === "PRODUTIVO") {
    if (!data.variedadeId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Variedade é obrigatória para setores produtivos", path: ["variedadeId"] });
    }
    if (!data.areaHectares || parseFloat(data.areaHectares) <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Área deve ser maior que zero", path: ["areaHectares"] });
    }
    if ((data.quantidadeMudas ?? 0) <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Quantidade de mudas deve ser maior que zero", path: ["quantidadeMudas"] });
    }
    if (!data.dataPlantio || isNaN(Date.parse(data.dataPlantio))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Data inválida", path: ["dataPlantio"] });
    }
  }
});

export const insertCorFitaSchema = createInsertSchema(coresFitas).omit({
  id: true,
  orgId: true,
  createdAt: true,
}).extend({
  nome: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
  hexColor: z.string().regex(/^#[0-9A-F]{6}$/i, "Cor hexadecimal inválida"),
  aliases: z.array(z.string()).default([]),
});

export const insertMangaraSchema = createInsertSchema(mangaras).omit({
  id: true,
  orgId: true,
  createdAt: true,
  cicloId: true,
  dataPrevistaColheita: true,
}).extend({
  dataCorteMangara: z.string().refine(val => !isNaN(Date.parse(val)), "Data inválida"),
  quantidadeCachos: z.number().int().positive("Quantidade deve ser maior que zero"),
});

export const insertColheitaSchema = createInsertSchema(colheitas).omit({
  id: true,
  orgId: true,
  createdAt: true,
  cicloId: true,
  status: true,
  // Carimbo derivado do setor pelo servidor (createColheita) — o client não escolhe a
  // variedade da colheita, senão o "histórico imune a replantio" vira campo editável.
  variedadeId: true,
}).extend({
  corFitaId: z.string().nullish(), // Allow null for "sem fita"
  dataColheita: z.string().refine(val => !isNaN(Date.parse(val)), "Data inválida"),
  quantidadeCachos: z.number().int().positive("Quantidade deve ser maior que zero"),
  semFita: z.boolean().optional().default(false), // Toggle para colheitas sem fita
});

export const insertMotivoPerdaSchema = createInsertSchema(motivosPerdas).omit({
  id: true,
  orgId: true,
  createdAt: true,
}).extend({
  nome: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
  descricao: z.string().optional(),
});

export const insertParametroSchema = createInsertSchema(parametros).omit({
  id: true,
  orgId: true,
  updatedAt: true,
}).extend({
  chave: z.string().min(2, "Chave deve ter no mínimo 2 caracteres"),
  valor: z.string().min(1, "Valor é obrigatório"),
  tipo: z.enum(["INTEGER", "STRING", "BOOLEAN", "DECIMAL"]).default("STRING"),
});

export const insertPerdaSchema = createInsertSchema(perdas).omit({
  id: true,
  orgId: true,
  createdAt: true,
}).extend({
  quantidadeCachos: z.number().int().positive("Quantidade deve ser maior que zero"),
  motivoPerdaId: z.string().min(1, "Motivo da perda é obrigatório"),
  observacoes: z.string().optional(),
});

export const updateCorFitaSchema = insertCorFitaSchema.partial();

export const updateMangaraSchema = insertMangaraSchema.partial().extend({
  dataPrevistaColheita: z.string().optional(),
});

// Herda o `.omit` de variedadeId do insert: o carimbo NUNCA vem do body, nem em create nem
// em update. Ele é derivado do setor pelo servidor — na criação, e de novo se (e só se) a
// colheita for movida de setor (updateColheita). Aceitar do client transformaria o
// "histórico imune a replantio" da migração 043 num campo editável qualquer.
export const updateColheitaSchema = insertColheitaSchema.partial().extend({
  corFitaId: z.string().nullish(), // Allow null/undefined for "sem fita"
  semFita: z.boolean().optional(), // Permitir alterar status de sem fita
});

export const updateMotivoPerdaSchema = insertMotivoPerdaSchema.partial();

export const updateParametroSchema = insertParametroSchema.partial();

// Login Schema
/**
 * Login por e-mail OU telefone (Mercado C2).
 *
 * `identificador` é o campo novo — uma caixa só, porque o usuário não deveria ter
 * que escolher a aba certa antes de digitar. `email` continua aceito para não
 * quebrar nenhum client que ainda mande o formato antigo; quem manda os dois, manda
 * `identificador` valendo.
 *
 * A validação de FORMATO saiu daqui de propósito: com um campo polimórfico, um
 * "Email inválido" na tela seria mentira metade das vezes. Quem classifica é
 * `classificarIdentificador` (shared/mercado.ts), e identificador que não é nem
 * e-mail nem telefone cai no mesmo "Credenciais inválidas" genérico — não se conta
 * ao atacante em que passo ele errou.
 */
export const loginSchema = z.object({
  identificador: z.string().min(1).optional(),
  email: z.string().min(1).optional(),
  password: z.string().min(1, "Senha é obrigatória"),
  // "Manter conectado neste aparelho": sessão de 30 dias em vez de 24 h.
  manterConectado: z.boolean().optional(),
}).refine(d => !!(d.identificador ?? d.email), {
  message: "Informe seu e-mail ou telefone",
  path: ["identificador"],
});

// TypeScript Types
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

export type InsertVariedade = z.infer<typeof insertVariedadeSchema>;
export type Variedade = typeof variedades.$inferSelect;

export type InsertSetor = z.infer<typeof insertSetorSchema>;
export type Setor = typeof setores.$inferSelect;

export type InsertCorFita = z.infer<typeof insertCorFitaSchema>;
export type CorFita = typeof coresFitas.$inferSelect;

export type InsertMangara = z.infer<typeof insertMangaraSchema>;
export type Mangara = typeof mangaras.$inferSelect;

export type InsertColheita = z.infer<typeof insertColheitaSchema>;
export type Colheita = typeof colheitas.$inferSelect;

export type InsertPerda = z.infer<typeof insertPerdaSchema>;
export type Perda = typeof perdas.$inferSelect;

export type InsertMotivoPerda = z.infer<typeof insertMotivoPerdaSchema>;
export type MotivoPerda = typeof motivosPerdas.$inferSelect;

export type InsertParametro = z.infer<typeof insertParametroSchema>;
export type Parametro = typeof parametros.$inferSelect;

export type AppVersion = typeof appVersion.$inferSelect;

export type UpdateCorFita = z.infer<typeof updateCorFitaSchema>;
export type UpdateMangara = z.infer<typeof updateMangaraSchema>;
export type UpdateColheita = z.infer<typeof updateColheitaSchema>;
export type UpdateMotivoPerda = z.infer<typeof updateMotivoPerdaSchema>;
export type UpdateParametro = z.infer<typeof updateParametroSchema>;

export type Ciclo = typeof ciclos.$inferSelect;

export type Login = z.infer<typeof loginSchema>;

// ─── Senha (PR 3): mínimo 8 nas rotas NOVAS (D13). O login continua aceitando as antigas.
export const SENHA_MINIMA = 8;
export const senhaSchema = z.string().min(SENHA_MINIMA, `A senha precisa de no mínimo ${SENHA_MINIMA} caracteres`).max(200);
export const esqueciSenhaSchema = z.object({ identificador: z.string().trim().min(1, 'Informe seu e-mail ou telefone') });
export const definirSenhaSchema = z.object({ token: z.string().min(16, 'Link inválido'), senha: senhaSchema });
export const trocarSenhaSchema = z.object({ atual: z.string().min(1, 'Informe a senha atual'), nova: senhaSchema });

/** Os módulos que um usuário pode ser restringido a ver (users.modulos). */
export const MODULOS_USUARIO = ['cacho', 'equipe', 'manejo', 'mercado'] as const;

export const createUsuarioSchema = z.object({
  // E-mail OU telefone (a main já aceita login por telefone desde a 055). O
  // `refine` abaixo garante pelo menos um — conta sem nenhum é conta em que é
  // impossível entrar, e o CHECK do banco recusaria de qualquer jeito.
  email: z.string().email('Email inválido').nullable().optional(),
  telefone: z.string().trim().min(8, 'Telefone inválido').nullable().optional(),
  name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  // OPCIONAL desde o PR 3: sem senha o usuário nasce CONVIDADO e recebe um link
  // para definir a dele. Com senha (o fallback "definir senha agora"), mínimo 8 (D13).
  password: z.string().min(8, 'Senha deve ter no mínimo 8 caracteres').optional(),
  // Módulos visíveis para ESTE usuário. `null`/ausente = todos os da org.
  modulos: z.array(z.enum(MODULOS_USUARIO)).nullable().optional(),
  // Os papéis CADASTRÁVEIS — os de `userRoleEnum` menos LEITURA, que existe na
  // matriz (é o papel do modo suporte) mas não se cadastra pela tela (D4).
  role: z.enum(['ADMIN', 'GESTOR', 'ENCARREGADO', 'CAMPO']).default('CAMPO'),
  // Qual funcionário este login é (migração 052). `null` = sem vínculo, que é o
  // default e um estado válido — ver users.funcionarioId.
  funcionarioId: z.string().uuid('Funcionário inválido').nullable().optional(),
}).refine(d => !!(d.email || d.telefone), {
  message: 'Informe e-mail ou telefone',
  path: ['email'],
});

export const updateUsuarioSchema = z.object({
  name: z.string().min(2).optional(),
  role: z.enum(['ADMIN', 'GESTOR', 'ENCARREGADO', 'CAMPO']).optional(),
  password: z.string().min(8, 'Senha deve ter no mínimo 8 caracteres').optional(),
  telefone: z.string().trim().min(8, 'Telefone inválido').nullable().optional(),
  modulos: z.array(z.enum(MODULOS_USUARIO)).nullable().optional(),
  // `null` DESLIGA o vínculo; campo ausente não mexe. Os dois precisam ser
  // distinguíveis — senão salvar o nome apagaria o vínculo sem ninguém pedir
  // (mesma armadilha do `mnjExecutorPadraoFuncionarioId` em organizations).
  funcionarioId: z.string().uuid('Funcionário inválido').nullable().optional(),
}).refine(data => Object.keys(data).length > 0, {
  message: 'Pelo menos um campo deve ser informado',
});

export const fecharCicloSchema = z.object({
  distribuirPerdas: z.boolean().default(false),
  // Fechar mesmo com a janela de colheita ainda aberta. Sem isto a rota devolve
  // 409 JANELA_ABERTA — ver o porquê em server/routes/ciclos.ts. Default false
  // de propósito: quem force tem que dizer que está forçando.
  forcar: z.boolean().default(false),
  // Tolera string vazia (normaliza para null) — requests antigos/concorrentes
  // que enviavam motivoPerdaId: "" não devem quebrar no uuid.
  motivoPerdaId: z.preprocess(
    v => (v === '' ? null : v),
    z.string().uuid().optional().nullable()
  ),
});

export const corOrdemSchema = z.object({
  targetId: z.string().uuid('targetId deve ser um UUID válido'),
});

export const periodoQuerySchema = z.object({
  periodo: z.enum(['semana', 'mes', 'mes-passado', 'proximo-mes', 'trimestre', 'semestre', 'ano', 'tudo', 'periodo-especifico']).optional().default('tudo'),
  dataInicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'dataInicio deve estar no formato YYYY-MM-DD').optional(),
  dataFim: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'dataFim deve estar no formato YYYY-MM-DD').optional(),
  setorId: z.string().uuid().optional(),
  variedadeId: z.string().uuid().optional(),
  status: z.string().optional(),
});

export const relatorioQuerySchema = periodoQuerySchema.omit({ status: true });

export const previsaoQuerySchema = z.object({
  variedadeId: z.string().uuid().optional(),
});

export const previstoRealizadoQuerySchema = z.object({
  ano: z.coerce.number().int().optional(),
  variedadeId: z.string().uuid().optional(),
  // Query params chegam como string; tratamos "true"/true como verdadeiro (evita o
  // gotcha do z.coerce.boolean, que converte qualquer string não-vazia em true).
  incluirAndamento: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform(v => v === true || v === 'true')
    .pipe(z.boolean().default(false)),
});

export const ciclosQuerySchema = z.object({
  status: z.string().optional(),
  setorId: z.string().uuid().optional(),
  variedadeId: z.string().uuid().optional(),
});

// Extended types with relations for frontend
export type VariedadeWithCultura = Variedade & {
  cultura: Cultura;
};

export type SetorWithVariedade = Setor & {
  variedade: VariedadeWithCultura | null;
  // Cultura derivada do setor (setor → variedade → cultura, ver
  // shared/cultura-do-setor.ts). NÃO é um campo do banco — setores nunca ganha
  // cultura_id próprio, pra não abrir espaço pra divergência entre o dado
  // duplicado e a variedade de verdade. null = setor sem variedade cadastrada.
  cultura: Cultura | null;
};

export type MangaraWithRelations = Mangara & {
  setor: SetorWithVariedade;
  corFita: CorFita;
  ciclo: Ciclo;
};

export type ColheitaWithRelations = Colheita & {
  setor: SetorWithVariedade;
  corFita?: CorFita | null;
  ciclo?: Ciclo | null;
};

export type CicloWithRelations = Ciclo & {
  corFita: CorFita;
  mangaras: MangaraWithRelations[];
  colheitas: ColheitaWithRelations[];
  perdas: Perda[];
};

// ═══════════════════════════════════════════════════════════════════════════
// MÓDULO CACHO — VENDAS
// ═══════════════════════════════════════════════════════════════════════════

export const vendaStatusEnum = pgEnum("venda_status", ["RASCUNHO", "EMITIDA", "PAGA", "CANCELADA"]);

// Compradores
export const compradores = pgTable("compradores", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  documento: text("documento"),
  telefone: text("telefone"),
  cidade: text("cidade"),
  observacoes: text("observacoes"),
  ativo: boolean("ativo").notNull().default(true),
  // ─── Mercado (migração 054) ────────────────────────────────────────────────
  // `telefone` é dado de CADASTRO, texto livre, digitado em qualquer formato.
  // `telefoneWhatsapp` é ENDEREÇO DE ENTREGA: só dígitos, com DDD, é o que vira
  // o link wa.me. Um não substitui o outro — reaproveitar `telefone` faria o
  // envio depender de um formato que nunca foi exigido do cadastro.
  telefoneWhatsapp: text("telefone_whatsapp"),
  // CORRETOR | DISTRIBUIDOR (migração 057) — como o PRODUTOR classifica este
  // comprador no cadastro dele. Nasce 'CORRETOR' porque é o que todo cadastro
  // existente é; o claim sobrescreve quando o comprador se declara distribuidor.
  perfil: text("perfil").notNull().default("CORRETOR"),
  // Vínculo comprador ↔ organização COMPRADORA. Fica NULL no C1 inteiro: conta do
  // comprador é o C2. A coluna nasce junto porque é a chave do vínculo, e criá-la
  // depois obrigaria a reprocessar os compartilhamentos já emitidos.
  orgVinculadaId: varchar("org_vinculada_id").references(() => organizations.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("compradores_org_nome_idx").on(t.orgId, t.nome),
]);

export type Comprador = typeof compradores.$inferSelect;

// Produtos de venda (catálogo: variedade × classificação, com peso padrão da caixa)
export const produtosVenda = pgTable("produtos_venda", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  variedadeId: varchar("variedade_id").references(() => variedades.id),
  classificacao: text("classificacao"),
  pesoCaixaKg: decimal("peso_caixa_kg", { precision: 5, scale: 2 }).notNull(),
  ordem: integer("ordem").notNull().default(0),
  ativo: boolean("ativo").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("produtos_venda_org_nome_idx").on(t.orgId, t.nome),
]);

export type ProdutoVenda = typeof produtosVenda.$inferSelect;

// Vendas (a carga; o Romaneio é o PDF desta entidade — PR 2)
// `numero` é nullable: só é atribuído na emissão (rascunho não consome número).
export const vendas = pgTable("vendas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  numero: integer("numero"),
  ano: integer("ano").notNull(),
  data: date("data").notNull(),
  compradorId: varchar("comprador_id").notNull().references(() => compradores.id),
  motorista: text("motorista"),
  placa: text("placa"),
  status: vendaStatusEnum("status").notNull().default("RASCUNHO"),
  colheitaInicio: date("colheita_inicio"),
  colheitaFim: date("colheita_fim"),
  // Denormalizados — recalculados no servidor a cada mutação de item, nunca confiar no client.
  totalCaixas: integer("total_caixas").notNull().default(0),
  pesoTotalKg: decimal("peso_total_kg", { precision: 10, scale: 2 }).notNull().default("0"),
  valorTotal: decimal("valor_total", { precision: 12, scale: 2 }).notNull().default("0"),
  observacoes: text("observacoes"),
  /**
   * QUANDO O DINHEIRO ENTRA — a data do romaneio, não da formalização (061).
   *
   * ⚠️ Morava em `mercado_compartilhamentos` até a 061, e por isso 15 dos 16
   * romaneios em aberto estavam sem prazo: sem formalizar não havia LINHA onde
   * gravar. Vencimento e formalização são independentes — dá para ter prazo sem
   * formalizar, e formalizar sem prazo (nota quitada).
   *
   * NULL é estado normal ("a definir"), e limpar é permitido. Pode estar no
   * passado: dívida atrasada é fato, não erro de digitação. O que o servidor
   * recusa é vencimento ANTES da data da venda — ver `vencimentoValido()`.
   */
  vencimento: date("vencimento"),
  /**
   * PRODUTOR | COMPRADOR — o único campo que os DOIS lados escrevem, sem aceite:
   * vale o último que escreveu (regra 6 da spec do corretor). Com
   * `vencimentoAlteradoEm`, é o que dá ao outro lado o que reclamar.
   */
  vencimentoDefinidoPor: text("vencimento_definido_por"),
  vencimentoAlteradoEm: timestamp("vencimento_alterado_em", { withTimezone: true }),
  emitidaEm: timestamp("emitida_em"),
  /**
   * QUANDO O DINHEIRO ENTROU — informado por quem confirma, não `now()`.
   *
   * ⚠️ É por esta data que "Recebido no mês" soma. Gravar o instante do clique
   * jogava para setembro um dinheiro que entrou em 28 de agosto, e o produtor só
   * descobria ao fechar o mês. O servidor recusa data futura (recebimento é fato
   * passado) e data anterior à venda — ver `recebimentoValido()`.
   */
  pagaEm: timestamp("paga_em"),
  pagaPorUserId: varchar("paga_por_user_id").references(() => users.id),
  usuarioId: varchar("usuario_id").notNull().references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("vendas_org_ano_numero_idx").on(t.orgId, t.ano, t.numero),
]);

export type Venda = typeof vendas.$inferSelect;

// Itens da venda
export const vendaItens = pgTable("venda_itens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  vendaId: varchar("venda_id").notNull().references(() => vendas.id, { onDelete: "cascade" }),
  produtoVendaId: varchar("produto_venda_id").notNull().references(() => produtosVenda.id),
  // CAIXA_PADRAO: a caixa tem peso conhecido, conta-se caixa e o kg é derivado.
  // PESAGEM: cada caixa é pesada e o que se compra é KG — a caixa NÃO é grandeza da venda.
  modoPeso: text("modo_peso").notNull().default("CAIXA_PADRAO"),
  quantidadeCaixas: integer("quantidade_caixas").notNull(), // 0 em PESAGEM (legítimo, não "vazio")
  pesoCaixaKg: decimal("peso_caixa_kg", { precision: 5, scale: 2 }), // snapshot do produto; NULL em PESAGEM
  // ⚠️ Em CAIXA_PADRAO é DERIVADO (caixas × pesoCaixa, no servidor). Em PESAGEM é o valor
  // INFORMADO — foi medido na balança e NUNCA é recalculado: reconstruir por
  // (kg ÷ caixas × caixas) inventaria diferença num número que vai pro documento de
  // pagamento do comprador.
  pesoTotalKg: decimal("peso_total_kg", { precision: 10, scale: 2 }).notNull(),
  precoKg: decimal("preco_kg", { precision: 8, scale: 2 }).notNull(), // digitado por carga
  valorTotal: decimal("valor_total", { precision: 12, scale: 2 }).notNull(), // pesoTotal × preço (server)
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type VendaItem = typeof vendaItens.$inferSelect;

export type ModoPeso = "CAIXA_PADRAO" | "PESAGEM";
export const MODO_PESO: Record<ModoPeso, ModoPeso> = { CAIXA_PADRAO: "CAIXA_PADRAO", PESAGEM: "PESAGEM" };

// Associação revisável venda ↔ colheita, granularidade (venda, data, setor) — não por fita.
// Substitui, para vendas novas, o range cego `vendas.colheitaInicio`/`colheitaFim` (deprecated,
// mantidos só para leitura de vendas antigas). Ver Rizoma_Cacho_AssociacaoColheitaVenda_Spec.md.
export const vendaColheitaAssociacoes = pgTable("venda_colheita_associacoes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  vendaId: varchar("venda_id").notNull().references(() => vendas.id, { onDelete: "cascade" }),
  data: date("data").notNull(),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  cachosNoMomento: integer("cachos_no_momento"), // snapshot informativo — não é fonte de verdade
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("venda_colheita_org_venda_data_setor_idx").on(t.orgId, t.vendaId, t.data, t.setorId),
]);

export type VendaColheitaAssociacao = typeof vendaColheitaAssociacoes.$inferSelect;

// Rateio materializado venda ↔ colheita (migração 044) — o FATO que faltava: quantos cachos
// de cada (dia, setor) foram em cada venda.
//
// `venda_colheita_associacoes` é a INTENÇÃO do usuário; esta tabela é o RESULTADO CALCULADO
// dessa intenção (server/storage/rateio.ts). Duas tabelas, dois papéis — não fundir: fundidas,
// um recálculo automático sobrescreveria o que o usuário marcou, que é a mesma classe de
// mistura que produziu o bug da associação fantasma (PR #142).
//
// ⚠️ `cachosAlocados` é DECIMAL de propósito: é uma fração de rateio, não uma contagem
// física. Arredondar antes de somar quebra a conservação Σ aloc = C(g) — arredonda só na
// renderização. Ver Rizoma_Cacho_Rendimento_Rateio_Colheita_Venda_Spec.md §4.
export const vendaColheitaRateio = pgTable("venda_colheita_rateio", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  vendaId: varchar("venda_id").notNull().references(() => vendas.id, { onDelete: "cascade" }),
  data: date("data").notNull(),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  // Coluna, NÃO chave: o setor já determina a variedade, então incluí-la na chave não
  // separaria nada e abriria espaço para duas linhas divergentes do mesmo fato. Existe
  // para filtrar/agrupar a analítica sem JOIN por setor em toda consulta.
  variedadeId: varchar("variedade_id").references(() => variedades.id),
  cachosGrupo: integer("cachos_grupo").notNull(), // C(g) no momento do cálculo
  cachosAlocados: decimal("cachos_alocados", { precision: 12, scale: 4 }).notNull(),
  kgAlocados: decimal("kg_alocados", { precision: 12, scale: 2 }).notNull(),
  kgPorCacho: decimal("kg_por_cacho", { precision: 10, scale: 4 }).notNull(),
  metodo: text("metodo").notNull().default("PROPORCIONAL"), // espaço p/ MANUAL na Fase 4
  calculadoEm: timestamp("calculado_em").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("venda_colheita_rateio_uniq_idx").on(t.orgId, t.vendaId, t.data, t.setorId),
  index("venda_colheita_rateio_grupo_idx").on(t.orgId, t.data, t.setorId),
  index("venda_colheita_rateio_analitico_idx").on(t.orgId, t.variedadeId, t.data),
]);

export type VendaColheitaRateio = typeof vendaColheitaRateio.$inferSelect;

// Relations
export const compradoresRelations = relations(compradores, ({ many }) => ({
  vendas: many(vendas),
}));

export const produtosVendaRelations = relations(produtosVenda, ({ one, many }) => ({
  variedade: one(variedades, {
    fields: [produtosVenda.variedadeId],
    references: [variedades.id],
  }),
  itens: many(vendaItens),
}));

export const vendasRelations = relations(vendas, ({ one, many }) => ({
  comprador: one(compradores, {
    fields: [vendas.compradorId],
    references: [compradores.id],
  }),
  itens: many(vendaItens),
  colheitaAssociacoes: many(vendaColheitaAssociacoes),
}));

export const vendaItensRelations = relations(vendaItens, ({ one }) => ({
  venda: one(vendas, {
    fields: [vendaItens.vendaId],
    references: [vendas.id],
  }),
  produtoVenda: one(produtosVenda, {
    fields: [vendaItens.produtoVendaId],
    references: [produtosVenda.id],
  }),
}));

export const vendaColheitaAssociacoesRelations = relations(vendaColheitaAssociacoes, ({ one }) => ({
  venda: one(vendas, {
    fields: [vendaColheitaAssociacoes.vendaId],
    references: [vendas.id],
  }),
  setor: one(setores, {
    fields: [vendaColheitaAssociacoes.setorId],
    references: [setores.id],
  }),
}));

// Insert / Update Schemas com Zod

export const insertCompradorSchema = createInsertSchema(compradores).omit({
  id: true,
  orgId: true,
  createdAt: true,
}).extend({
  nome: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
  // C3 — o cadastro do produtor classifica quem compra dele. `optional` porque o
  // banco tem default: cliente antigo que não manda o campo continua criando.
  perfil: z.enum(["CORRETOR", "DISTRIBUIDOR"]).optional(),
});

export const updateCompradorSchema = insertCompradorSchema.partial();

export const insertProdutoVendaSchema = createInsertSchema(produtosVenda).omit({
  id: true,
  orgId: true,
  createdAt: true,
}).extend({
  nome: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
  variedadeId: z.string().nullish(),
  classificacao: z.string().nullish(),
  pesoCaixaKg: z.string().refine(val => parseFloat(val) > 0, "Peso da caixa deve ser maior que zero"),
  ordem: z.number().int().default(0),
});

export const updateProdutoVendaSchema = insertProdutoVendaSchema.partial();

// Item de venda enviado pelo client.
//   • CAIXA_PADRAO: `pesoTotalKg` é DERIVADO no servidor (quantidadeCaixas × pesoCaixaKg).
//   • PESAGEM: `pesoTotalKg` é o valor MEDIDO na balança e o servidor NÃO o recalcula —
//     reconstruí-lo inventaria diferença num número que vai pro documento de pagamento.
// `valorTotal` continua sempre do servidor, nos dois modos (pesoTotalKg × precoKg).
const decimalPositivo = (mensagem: string) => z.union([z.string(), z.number()])
  .transform(v => String(v).replace(",", "."))
  .refine(v => !isNaN(parseFloat(v)) && parseFloat(v) > 0, mensagem);

// UNIÃO DISCRIMINADA, não um objeto com tudo opcional — e isso é o ponto, não estilo.
//
// Um objeto único com `quantidadeCaixas?` e `pesoTotalKg?` deixaria passar um payload PESAGEM
// COM caixas, e aí o SERVIDOR decidiria o que ignorar em silêncio: exatamente a forma do bug
// da associação fantasma da 033. Rejeitar é mais barato que decidir.
const itemCaixaPadraoSchema = z.object({
  modoPeso: z.literal("CAIXA_PADRAO"),
  produtoVendaId: z.string().uuid("Produto inválido"),
  quantidadeCaixas: z.number().int().positive("Quantidade de caixas deve ser maior que zero"),
  precoKg: decimalPositivo("Preço por kg deve ser maior que zero"),
  // Override opcional do peso da caixa desta venda (compradores diferentes usam caixas de
  // peso diferente — ex.: 18kg, 20kg). Quando ausente, o servidor usa o peso do produto.
  pesoCaixaKg: decimalPositivo("Peso da caixa deve ser maior que zero").optional(),
}).strict();

const itemPesagemSchema = z.object({
  modoPeso: z.literal("PESAGEM"),
  produtoVendaId: z.string().uuid("Produto inválido"),
  // O dado PRIMÁRIO deste modo: veio da balança. `.strict()` acima e a ausência de
  // `quantidadeCaixas`/`pesoCaixaKg` aqui é o que faz o payload errado ser recusado.
  pesoTotalKg: decimalPositivo("Peso em kg deve ser maior que zero"),
  precoKg: decimalPositivo("Preço por kg deve ser maior que zero"),
}).strict();

// `modoPeso` ausente = CAIXA_PADRAO, para não quebrar nenhum cliente antigo.
export const vendaItemInputSchema = z.preprocess(
  v => (v && typeof v === "object" && !(v as any).modoPeso ? { ...(v as any), modoPeso: "CAIXA_PADRAO" } : v),
  z.discriminatedUnion("modoPeso", [itemCaixaPadraoSchema, itemPesagemSchema]),
);

/** Carga mista não existe hoje (0 em 32 vendas de produção) — e se um dia existir, que seja
 *  decisão consciente com tela própria, não acidente de payload. */
function todosNoMesmoModo(itens: Array<{ modoPeso?: string }> | undefined): boolean {
  if (!itens || itens.length === 0) return true;
  const primeiro = itens[0].modoPeso ?? "CAIXA_PADRAO";
  return itens.every(i => (i.modoPeso ?? "CAIXA_PADRAO") === primeiro);
}

export const MENSAGEM_CARGA_MISTA =
  "Todos os produtos da carga precisam estar no mesmo modo (caixa padrão ou peso variável)";

/**
 * "Marcar como paga" com a DATA em que o dinheiro entrou (061).
 *
 * Opcional: sem ela vale hoje, que é o caso de quem confirma no próprio dia — e
 * é o que mantém o cliente antigo funcionando. As REGRAS da data (nem futura,
 * nem antes da venda) ficam em `checarDataRecebimento`, compartilhada com a rota
 * `recebi` do Mercado: o mesmo ato por dois caminhos não pode ter duas regras.
 */
export const pagarVendaSchema = z.object({
  data: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data inválida").optional(),
});

// Recálculo em lote do rateio (rota admin). `dryRun` devolve o que MUDARIA sem escrever —
// é como a tabela é conferida antes de ser preenchida pela primeira vez.
export const recalcularRateioSchema = z.object({
  de: z.string().refine(v => !isNaN(Date.parse(v)), "Data inicial inválida"),
  ate: z.string().refine(v => !isNaN(Date.parse(v)), "Data final inválida"),
  dryRun: z.boolean().optional().default(false),
  // `proporcional` é o DEFAULT e o comportamento de hoje, byte a byte. `comum` é o rendimento
  // único por componente conexa (shared/rateio-colheita.ts::calcularAlocacaoComum) — entra
  // atrás de dry-run e a troca do default é decisão própria, em PR próprio.
  modo: z.enum(["proporcional", "comum"]).optional().default("proporcional"),
});

// Par (dia, setor) marcado no form de venda — grava em `venda_colheita_associacoes`.
export const vendaColheitaAssociacaoInputSchema = z.object({
  data: z.string().refine(v => !isNaN(Date.parse(v)), "Data inválida"),
  setorId: z.string().uuid("Setor inválido"),
});

export const insertVendaSchema = z.object({
  data: z.string().refine(val => !isNaN(Date.parse(val)), "Data inválida"),
  compradorId: z.string().uuid("Comprador inválido"),
  /**
   * Quando o dinheiro entra (061). OPCIONAL: o prazo nem sempre está combinado
   * na hora do carregamento, e travar a venda por causa dele seria travar o
   * caminhão. Em branco fica "a definir", e se define depois na venda ou na
   * linha de "A receber". As regras da data ficam em `checarVencimento`.
   */
  vencimento: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Vencimento inválido").nullish(),
  motorista: z.string().nullish(),
  placa: z.string().nullish(),
  colheitaInicio: z.string().nullish(),
  colheitaFim: z.string().nullish(),
  observacoes: z.string().nullish(),
  itens: z.array(vendaItemInputSchema).min(1, "Adicione ao menos um item"),
  colheitaAssociacoes: z.array(vendaColheitaAssociacaoInputSchema).optional(),
}).refine(v => todosNoMesmoModo(v.itens as Array<{ modoPeso?: string }>), {
  message: MENSAGEM_CARGA_MISTA, path: ["itens"],
});

export const updateVendaSchema = z.object({
  data: z.string().refine(val => !isNaN(Date.parse(val)), "Data inválida").optional(),
  compradorId: z.string().uuid("Comprador inválido").optional(),
  /** Ver `insertVendaSchema`. AUSENTE não mexe no prazo; `null` LIMPA. */
  vencimento: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Vencimento inválido").nullish(),
  motorista: z.string().nullish(),
  placa: z.string().nullish(),
  colheitaInicio: z.string().nullish(),
  colheitaFim: z.string().nullish(),
  observacoes: z.string().nullish(),
  itens: z.array(vendaItemInputSchema).min(1, "Adicione ao menos um item").optional(),
  colheitaAssociacoes: z.array(vendaColheitaAssociacaoInputSchema).optional(),
}).refine(v => todosNoMesmoModo(v.itens as Array<{ modoPeso?: string }> | undefined), {
  message: MENSAGEM_CARGA_MISTA, path: ["itens"],
});

// "EM_ABERTO_PAGAS" é um preset (não um status real da venda) — mapeia pra
// status IN ('EMITIDA', 'PAGA') no storage. É o default da tela Vendas.
// "COM_DIVERGENCIA" também: emitida/paga com conferência válida do comprador que
// não bate com o emitido (ver `condicoesDeStatus` no storage).
// `dataInicio`/`dataFim` só são usados quando periodo === "personalizado" (ver resolvePeriodo).
export const vendasQuerySchema = z.object({
  periodo: z.string().optional(),
  dataInicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "dataInicio deve estar no formato YYYY-MM-DD").optional(),
  dataFim: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "dataFim deve estar no formato YYYY-MM-DD").optional(),
  compradorId: z.string().uuid().optional(),
  status: z.enum(["RASCUNHO", "EMITIDA", "PAGA", "CANCELADA", "EM_ABERTO_PAGAS", "COM_DIVERGENCIA"]).optional(),
  // Recorta ITENS, não vendas: venda sem item da variedade sai do recorte; venda mista entra
  // só com a parte daquela variedade. Ver shared/vendas-itens.ts.
  variedadeId: z.string().uuid().optional(),
});

// O topo TEM que descrever a tabela. Antes só `periodo` chegava aqui, então três cards
// ignoravam comprador e situação enquanto o card "Vendas" (vindo de `vendas.length`) seguia o
// filtro — a pior combinação, porque um card certo faz os errados parecerem confiáveis.
export const resumoVendasQuerySchema = z.object({
  periodo: z.string().optional(),
  dataInicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "dataInicio deve estar no formato YYYY-MM-DD").optional(),
  dataFim: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "dataFim deve estar no formato YYYY-MM-DD").optional(),
  // MESMA validação da lista, de propósito: o topo e a tabela recebem o mesmo objeto de
  // filtro, e schemas que divergem são o primeiro passo para números que divergem.
  compradorId: z.string().uuid().optional(),
  status: z.enum(["RASCUNHO", "EMITIDA", "PAGA", "CANCELADA", "EM_ABERTO_PAGAS", "COM_DIVERGENCIA"]).optional(),
  variedadeId: z.string().uuid().optional(),
});

export const contextoColheitaQuerySchema = z.object({
  inicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "inicio deve estar no formato YYYY-MM-DD"),
  fim: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "fim deve estar no formato YYYY-MM-DD"),
});

// Relatório de Rendimento por Setor (Fase 3). `de`/`ate` recortam a data da COLHEITA —
// `venda_colheita_rateio.data` — e não a da venda: são calendários diferentes, e trocá-los
// é a armadilha do #149.
export const rendimentoQuerySchema = z.object({
  de: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "de deve estar no formato YYYY-MM-DD"),
  ate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "ate deve estar no formato YYYY-MM-DD"),
  variedadeId: z.string().uuid().optional(),
  setorId: z.string().uuid().optional(),
});

export type InsertComprador = z.infer<typeof insertCompradorSchema>;
export type UpdateComprador = z.infer<typeof updateCompradorSchema>;
export type InsertProdutoVenda = z.infer<typeof insertProdutoVendaSchema>;
export type UpdateProdutoVenda = z.infer<typeof updateProdutoVendaSchema>;
export type VendaItemInput = z.infer<typeof vendaItemInputSchema>;
export type VendaColheitaAssociacaoInput = z.infer<typeof vendaColheitaAssociacaoInputSchema>;
export type InsertVenda = z.infer<typeof insertVendaSchema>;
export type UpdateVenda = z.infer<typeof updateVendaSchema>;

export type VendaWithRelations = Venda & {
  comprador: Comprador;
  itens: (VendaItem & { produtoVenda: ProdutoVenda })[];
  colheitaAssociacoes: (VendaColheitaAssociacao & { setor: { id: string; nome: string } })[];
  // Só presente na lista (GET /api/vendas) — lido de `venda_colheita_rateio` num IN só,
  // ver agruparPesoMedioPorVenda em server/storage/vendas.ts. Ausente em outros endpoints
  // que devolvem VendaWithRelations (detalhe da venda usa GET /:id/rateio à parte).
  // `avisoPesoImplausivel` usa a banda APRENDIDA da variedade (shared/rendimento.ts), com a
  // faixa fixa 5–40 como fallback declarado.
  pesoMedioPorVariedade?: Array<{ variedadeId: string; variedadeNome: string; kgPorCacho: number; cachos: number; avisoPesoImplausivel: boolean }>;
};

// ═══════════════════════════════════════════════════════════════════════════
// MÓDULO MANEJO — Fase 0 / Ciclo 1 + Sprint 1 (modelo canônico)
// ═══════════════════════════════════════════════════════════════════════════
//
// Regra estrutural inviolável: a quantidade mora EXCLUSIVAMENTE em
// mnj_operacao_item (qtdPrevista / qtdRealizada, na UNIDADE DO PRODUTO). mnj_lote
// e mnj_operacao NÃO têm coluna de quantidade — é a forma exata do bug que dobrou
// a folha no módulo Equipe (4 remendos). Dose/ha e dose/100 L nunca são
// armazenadas: são derivadas a partir da área APLICADA (areaHaSnapshot ×
// percentualAplicado / 100, nunca da área total) e do volume de calda.
//
// ⚠️ Operações v2 (08/2026): o módulo perdeu a integração com o ERP Aegro e o
// controle de estoque. O que mudou aqui — migração 052:
//   • `status` virou text+CHECK com o ciclo backlog→planejada→em_andamento→
//     concluida/cancelada; "revisar" deixou de existir.
//   • `data_prevista` nullable — NULL é o backlog.
//   • `mnj_operacao_evento` é a linha do tempo, e o alicerce do canal WhatsApp.
//   • `kg_total_*` virou `qtd_*`; a unidade é a do produto.
// A definição completa está em Rizoma_Manejo_Operacoes_v2_Spec.md.
//
// Sprint 1 (Rizoma_Arquitetura_Sync_Aegro.md §4.2/§4.3/§5.4) eliminou as
// duplicações estruturais do Ciclo 1:
// - Setor: `setores` (Cacho) é o objeto físico canônico — `mnj_talhao_ref`
//   não existe mais. "Talhão" é vocabulário do Aegro, só usado na fronteira com
//   ele (erp-connector.ts); no Rizoma o nome é sempre Setor. Único campo
//   trazido pelo Manejo: `setores.fertirrigavel`.
// - Atividade × Operação: `mnj_card` virou `mnj_operacao` (a INSTÂNCIA
//   executada, com o detalhe técnico) — "card" era linguagem de UI vazando
//   pro domínio. `mnj_operacao.atividade_id` aponta pro MESMO catálogo
//   `atividades` que o Equipe já usa (substitui o enum `mnj_aplicacao_tipo`,
//   que duplicava esse catálogo). Fertirrigação em A1, A2 e A3 = 1 Atividade,
//   3 Operações.
// - Operação → Setor é 1:N (mnj_operacao_setor), não 1:1: uma operação pode
//   cobrir vários setores com a MESMA dose por hectare (kg/ha × área total —
//   é o Aegro quem faz essa conta, e é a regra que torna o agrupamento
//   coerente: setores com doses diferentes NUNCA podem ficar na mesma
//   operação). kg_total continua a fonte da verdade no nível da OPERAÇÃO
//   (mnj_operacao_item); por setor é sempre derivado
//   (kg_setor = kg_total × area_setor/area_total), nunca armazenado.
// - Executor é N:N (mnj_operacao_executor), join PURO — nunca carrega valor,
//   nunca dispara diária. O vínculo com o Equipe é MOLE: atribuir quem fez
//   uma operação não tem efeito financeiro nenhum; associar ao dia de
//   trabalho (tarefa) é ato separado, opcional, feito no Equipe.

export const mnjLoteOrigemEnum = pgEnum("mnj_lote_origem", ["texto", "whatsapp", "whatsapp_audio", "manual"]);
export const mnjProgramaStatusEnum = pgEnum("mnj_programa_status", ["ativo", "arquivado"]);

// Ciclo de vida da operação (Rizoma_Manejo_Operacoes_v2_Spec.md §5):
//
//   backlog ──(ganha data)──▶ planejada ──(Iniciar)──▶ em_andamento ──(Concluir)──▶ concluida
//      ▲                          │
//      └──(tira a data)───────────┘
//   qualquer estado não-concluído ──(Cancelar, com motivo)──▶ cancelada
//
// `text` + CHECK no Postgres, união no TS — nunca pgEnum. Mesmo precedente da
// coluna `origem` e das tabelas `agent_*`: `ALTER TYPE ... ADD VALUE` não pode ser
// referenciado na mesma transação do runner de migrações, e um status novo é
// exatamente o tipo de mudança que vai acontecer de novo.
export const MNJ_OPERACAO_STATUS = ["backlog", "planejada", "em_andamento", "concluida", "cancelada"] as const;
export type MnjOperacaoStatus = (typeof MNJ_OPERACAO_STATUS)[number];

export const MNJ_OPERACAO_PRIORIDADE = ["alta", "normal", "baixa"] as const;
export type MnjOperacaoPrioridade = (typeof MNJ_OPERACAO_PRIORIDADE)[number];

// Papel do executor. `atribuido` = planejado para; `executou` = registrado na
// conclusão. Continua JOIN PURO — nenhum dos dois cria tarefa ou diária.
export const MNJ_EXECUTOR_PAPEL = ["atribuido", "executou"] as const;
export type MnjExecutorPapel = (typeof MNJ_EXECUTOR_PAPEL)[number];

// Unidade do produto — levada a sério a partir da v2: a quantidade da operação é
// lida NESTA unidade, e é o que impede 9,95 L de virarem 9,95 kg numa soma.
export const MNJ_PRODUTO_UNIDADE = ["kg", "L", "g", "mL", "un"] as const;
export type MnjProdutoUnidade = (typeof MNJ_PRODUTO_UNIDADE)[number];

// Só filtra o seletor de insumos — produto sem categoria é estado válido.
export const MNJ_PRODUTO_CATEGORIA = ["fertilizante", "defensivo", "foliar", "biologico", "outro"] as const;
export type MnjProdutoCategoria = (typeof MNJ_PRODUTO_CATEGORIA)[number];

// ─── Referência ─────────────────────────────────────────────────────────────

export const mnjProduto = pgTable("mnj_produto", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  // Dicionário determinístico de apelidos ("SA","S. amônio","sulfato amonio").
  // Resolução de alias NUNCA é adivinhação do LLM — alias novo é pergunta ao
  // usuário e, ao confirmar, persiste aqui.
  aliases: json("aliases").$type<string[]>().notNull().default(sql`'[]'::json`),
  // CHECK no banco a partir da 052 — a unidade passou a ser levada a sério: é
  // nela que `mnj_operacao_item.qtd_prevista` é lida.
  unidade: text("unidade").notNull().default("kg").$type<MnjProdutoUnidade>(),
  // Só filtra o seletor de insumos. Null = não classificado, estado válido.
  categoria: text("categoria").$type<MnjProdutoCategoria>(),
  composicao: json("composicao").$type<Record<string, number>>(), // {N:20, S:24, ...} — opcional na Fase 0
  ativo: boolean("ativo").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("mnj_produto_org_nome_idx").on(t.orgId, t.nome),
]);

export type MnjProduto = typeof mnjProduto.$inferSelect;
export type InsertMnjProduto = typeof mnjProduto.$inferInsert;
export const insertMnjProdutoSchema = createInsertSchema(mnjProduto).omit({
  id: true, orgId: true, createdAt: true, updatedAt: true,
}).extend({
  nome: z.string().min(1, "Nome do produto é obrigatório"),
  aliases: z.array(z.string()).optional(),
});
export const updateMnjProdutoSchema = insertMnjProdutoSchema.partial();

// ─── Masterplan ──────────────────────────────────────────────────────────────

export const mnjPrograma = pgTable("mnj_programa", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  safraKey: text("safra_key"), // chave da safra no Aegro (Fase 0) — Safra não é entidade core aqui
  nome: text("nome").notNull(),
  periodoInicio: date("periodo_inicio"),
  periodoFim: date("periodo_fim"),
  status: mnjProgramaStatusEnum("status").notNull().default("ativo"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type MnjPrograma = typeof mnjPrograma.$inferSelect;
export type InsertMnjPrograma = typeof mnjPrograma.$inferInsert;
export const insertMnjProgramaSchema = createInsertSchema(mnjPrograma).omit({
  id: true, orgId: true, createdAt: true, updatedAt: true,
}).extend({
  nome: z.string().min(1, "Nome do programa é obrigatório"),
});
export const updateMnjProgramaSchema = insertMnjProgramaSchema.partial();

export const mnjProgramaItem = pgTable("mnj_programa_item", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  mnjProgramaId: varchar("mnj_programa_id").notNull().references(() => mnjPrograma.id, { onDelete: "cascade" }),
  mnjProdutoId: varchar("mnj_produto_id").notNull().references(() => mnjProduto.id),
  kgPorHa: decimal("kg_por_ha", { precision: 12, scale: 4 }).notNull(), // alvo
  frequenciaDias: integer("frequencia_dias"),
  ordem: integer("ordem").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type MnjProgramaItem = typeof mnjProgramaItem.$inferSelect;
export type InsertMnjProgramaItem = typeof mnjProgramaItem.$inferInsert;
export const insertMnjProgramaItemSchema = createInsertSchema(mnjProgramaItem).omit({
  id: true, orgId: true, createdAt: true, updatedAt: true,
});
export const updateMnjProgramaItemSchema = insertMnjProgramaItemSchema.partial();

// Payload composto de criação (programa + itens numa chamada só) — espelha o formulário
// da tela de CRUD do masterplan.
export const mnjProgramaItemInputSchema = z.object({
  mnjProdutoId: z.string().uuid("Produto inválido"),
  kgPorHa: z.union([z.string(), z.number()])
    .transform(v => String(v))
    .refine(v => !isNaN(parseFloat(v)) && parseFloat(v) > 0, "kg/ha deve ser maior que zero"),
  frequenciaDias: z.number().int().positive().nullish(),
  ordem: z.number().int().optional(),
});

export const mnjProgramaCreateSchema = z.object({
  setorId: z.string().uuid("Setor inválido"),
  safraKey: z.string().nullish(),
  nome: z.string().min(1, "Nome do programa é obrigatório"),
  periodoInicio: z.string().nullish(),
  periodoFim: z.string().nullish(),
  itens: z.array(mnjProgramaItemInputSchema).min(1, "Programa precisa de ao menos um item"),
});

export const mnjProgramaItensReplaceSchema = z.object({
  itens: z.array(mnjProgramaItemInputSchema).min(1, "Informe ao menos um item"),
});

export const mnjProgramaStatusPatchSchema = z.object({
  status: z.enum(["ativo", "arquivado"]),
});

export type MnjProgramaItemInput = z.infer<typeof mnjProgramaItemInputSchema>;
export type MnjProgramaCreateInput = z.infer<typeof mnjProgramaCreateSchema>;

// ─── Commit do programa em lote — payload de entrada da rota                    ──
// ─── POST /api/manejo/programas/confirmar-lote (capacidade                     ──
// ─── manejo.definir_programa_em_lote, Sprint 3 §3.1) ────────────────────────────
// Espelha a `PropostaProgramaEmLote` já resolvida pelo usuário no card de
// confirmação (shared/manejo-proposta.ts) — nada chega aqui sem ter passado pelo
// portão. `modo` por setor resolve a pergunta "programa_ativo_existente" quando ela
// existiu; ausente = não havia programa ativo pra esse setor (cria direto).

export const mnjProgramaLoteSetorCommitSchema = z.object({
  setorId: z.string().uuid("Setor inválido"),
  modo: z.enum(["sobrescrever", "novo_periodo"]).optional(),
});

export const mnjProgramaLoteCommitSchema = z.object({
  nomePrograma: z.string().min(1, "Nome do programa é obrigatório"),
  periodoInicio: z.string().nullish(),
  periodoFim: z.string().nullish(),
  itens: z.array(mnjProgramaItemInputSchema).min(1, "Programa precisa de ao menos um item"),
  setores: z.array(mnjProgramaLoteSetorCommitSchema).min(1, "Informe ao menos um setor"),
});

export type MnjProgramaLoteSetorCommit = z.infer<typeof mnjProgramaLoteSetorCommitSchema>;
export type MnjProgramaLoteCommit = z.infer<typeof mnjProgramaLoteCommitSchema>;

// ─── Execução — lote e operações ────────────────────────────────────────────

// Agrupador da entrada colada/dita. NÃO carrega valor — é só o "recibo" da
// interpretação (para auditoria e para o usuário revisar a semana inteira).
export const mnjLote = pgTable("mnj_lote", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  origem: mnjLoteOrigemEnum("origem").notNull().default("texto"),
  entradaRaw: text("entrada_raw").notNull(),
  entradaMidiaUrl: text("entrada_midia_url"), // áudio original — erro de transcrição só é auditável contra a mídia
  interpretacao: json("interpretacao").$type<Record<string, unknown>>(), // intenção + entidades resolvidas
  criadoPorUserId: varchar("criado_por_user_id").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type MnjLote = typeof mnjLote.$inferSelect;
export type InsertMnjLote = typeof mnjLote.$inferInsert;

// Frente de trabalho (migração 079): agrupa as pendências do Manejo — "Água e
// Irrigação", "Fazenda/Geral". É o eixo da Lista de pendências e do lembrete diário
// no WhatsApp. `encarregado` é campo da frente, independente do papel de usuário.
// `agent_audiencia_id` sem `.references()` no Drizzle porque `agent_audiencia` é
// declarada mais abaixo (FK existe no banco, ON DELETE SET NULL).
export const mnjFrente = pgTable("mnj_frente", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  emoji: text("emoji"),
  responsavelFuncionarioId: varchar("responsavel_funcionario_id").references(() => funcionarios.id),
  encarregadoFuncionarioId: varchar("encarregado_funcionario_id").references(() => funcionarios.id),
  agentAudienciaId: varchar("agent_audiencia_id"),
  // Nasce DESLIGADO: nenhuma frente manda mensagem sem alguém ligar.
  lembreteAtivo: boolean("lembrete_ativo").notNull().default(false),
  ordem: integer("ordem").notNull().default(0),
  ativo: boolean("ativo").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("mnj_frente_org_nome_idx").on(t.orgId, t.nome),
]);

export type MnjFrente = typeof mnjFrente.$inferSelect;
export type InsertMnjFrente = typeof mnjFrente.$inferInsert;

// A INSTÂNCIA executada — "card" era linguagem de UI vazando pro domínio.
// Atividade = o tipo de trabalho ("Fertirrigação"), catálogo `atividades`
// (compartilhado com o Equipe). Operação = esta instância, com o detalhe
// técnico. 1 Atividade : N Operações; 1 Operação : N Setores (mnj_operacao_setor).
export const mnjOperacao = pgTable("mnj_operacao", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  // Nullable a partir da v2 (migração 052): operação criada na tela não nasce de
  // um lote de interpretação. O lote continua sendo o recibo do Comando.
  mnjLoteId: varchar("mnj_lote_id").references(() => mnjLote.id, { onDelete: "cascade" }),
  // Nullable desde a 079: item da Lista de pendências nasce só com título. O CHECK
  // `mnj_operacao_atividade_ou_titulo` garante que sempre existe uma das duas.
  atividadeId: varchar("atividade_id").references(() => atividades.id),
  // Opcional com atividade — o default na tela é "nome da atividade + setores".
  // Obrigatório sem atividade.
  titulo: text("titulo"),
  // Frente de trabalho, local (texto livre) e posição na fila da frente (079).
  frenteId: varchar("frente_id").references(() => mnjFrente.id),
  local: text("local"),
  ordem: integer("ordem"),
  // 🔴 NULL = BACKLOG. Não é um filtro nem uma flag: backlog é a AUSÊNCIA de data,
  // e o CHECK da 052 amarra os dois (`status = 'backlog'` ⇔ `data_prevista IS NULL`,
  // com `cancelada` de fora — cancelar item de backlog é legítimo).
  dataPrevista: date("data_prevista"),
  // Fim do período planejado (migração 039). NULL = sem período, e aí o atraso é
  // medido pela `data_prevista`. Uma operação de 10/08 a 15/08 não está atrasada no dia 11.
  dataFimPrevista: date("data_fim_prevista"),
  status: text("status").notNull().default("planejada").$type<MnjOperacaoStatus>(),
  prioridade: text("prioridade").notNull().default("normal").$type<MnjOperacaoPrioridade>(),
  // De onde a operação nasceu (recorrência). A FK para `mnj_receita` chega no
  // ciclo 4, junto com a tabela — a coluna já existe para o payload carregá-la.
  receitaId: varchar("receita_id"),
  // Pulverização/foliar: total de calda em LITROS. "Tanques" (600/2.000 L) é
  // conversão de TELA, nunca unidade de banco.
  volumeCaldaL: decimal("volume_calda_l", { precision: 12, scale: 2 }),
  // Instrução para quem executa.
  observacao: text("observacao"),
  // ⚠️ ESPELHO do último evento correspondente em `mnj_operacao_evento`, escrito na
  // mesma transação da transição — existe só para a consulta ficar barata. A linha
  // do tempo continua sendo a fonte da verdade de QUANDO e POR QUEM.
  iniciadoEm: timestamp("iniciado_em", { withTimezone: true }),
  concluidoEm: timestamp("concluido_em", { withTimezone: true }),
  concluidoPorUserId: varchar("concluido_por_user_id").references(() => users.id),
  // O que o operador escreveu ao concluir.
  observacaoConclusao: text("observacao_conclusao"),
  createdByUserId: varchar("created_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  // Vistas Hoje/Semana/Backlog: sempre (org, janela de data, status).
  index("mnj_operacao_org_data_status_idx").on(t.orgId, t.dataPrevista, t.status),
]);

// ⚠️ As colunas da borda Aegro (`sync_status`, `sync_tentativas`, `sync_erro`,
// `aegro_planning_key`, `aegro_plan_key`, `aegro_realization_key`,
// `aegro_activity_key`, `pull_setor_id`, `origem`, `safra_key`, `confirmado_em`,
// `aprovado_em`, `dedup_key`) CONTINUAM NO BANCO depois da migração 052 — o Drizzle
// simplesmente parou de vê-las. É o contrato do expand: o PR seguinte (053) as
// dropa, e só então o banco e este arquivo voltam a dizer a mesma coisa.

export type MnjOperacao = typeof mnjOperacao.$inferSelect;
export type InsertMnjOperacao = typeof mnjOperacao.$inferInsert;

export const mnjOperacaoStatusPatchSchema = z.object({
  status: z.enum(MNJ_OPERACAO_STATUS),
  // Acompanha a transição quando ela MUDA a data: sair do backlog exige data; ir
  // pro backlog exige que ela NÃO venha. Os dois cruzamentos incoerentes viram 409
  // na rota (ver `validarStatusEData` em server/lib/manejo/kanban.ts).
  dataPrevista: z.string().refine(v => !isNaN(Date.parse(v)), "Data inválida").nullish(),
  // Só para `cancelada` — a spec (§5) exige motivo curto. Vai para o payload do
  // evento, nunca para uma coluna própria.
  motivo: z.string().min(1).max(280).optional(),
});

export const mnjOperacaoItensPatchSchema = z.object({
  itens: z.array(z.object({
    mnjProdutoId: z.string().uuid("Produto inválido"),
    // Na UNIDADE DO PRODUTO (kg, L, g, mL, un) — não mais em kg presumido.
    qtdRealizada: z.union([z.string(), z.number()])
      .transform(v => String(v))
      .refine(v => !isNaN(parseFloat(v)) && parseFloat(v) >= 0, "Quantidade inválida"),
  })).min(1, "Informe ao menos um item"),
});

// Setores cobertos por uma operação — 1:N (Sprint 1 §1.2c). Regra que torna
// isso coerente: UMA OPERAÇÃO = UMA DOSE ÚNICA POR HECTARE (kg/ha em
// mnj_operacao_item, multiplicada pela área TOTAL da operação) — nunca
// agrupar setores que precisem de doses diferentes. `area_ha_snapshot` e
// `percentual_aplicado` são POR SETOR (congelados no commit), diferente do
// Ciclo 1 onde viviam na operação inteira (que só cobria 1 setor).
export const mnjOperacaoSetor = pgTable("mnj_operacao_setor", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  mnjOperacaoId: varchar("mnj_operacao_id").notNull().references(() => mnjOperacao.id, { onDelete: "cascade" }),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  areaHaSnapshot: decimal("area_ha_snapshot", { precision: 10, scale: 4 }).notNull(), // congela a área no commit
  percentualAplicado: decimal("percentual_aplicado", { precision: 5, scale: 2 }).notNull().default("100"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("mnj_operacao_setor_idx").on(t.mnjOperacaoId, t.setorId),
]);

export type MnjOperacaoSetor = typeof mnjOperacaoSetor.$inferSelect;
export type InsertMnjOperacaoSetor = typeof mnjOperacaoSetor.$inferInsert;

// Quem executou — N:N, join PURO (Sprint 1 §1.2d). NUNCA carrega valor/quantidade,
// NUNCA dispara criação de tarefa/diária — vínculo com o Equipe é MOLE. Atribuir
// executor é fato operacional (quem fez), sem efeito financeiro nenhum; associar
// ao dia de trabalho é ato separado, opcional, feito de dentro do Equipe.
export const mnjOperacaoExecutor = pgTable("mnj_operacao_executor", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  mnjOperacaoId: varchar("mnj_operacao_id").notNull().references(() => mnjOperacao.id, { onDelete: "cascade" }),
  funcionarioId: varchar("funcionario_id").notNull().references(() => funcionarios.id),
  // `atribuido` (planejado para) × `executou` (registrado na conclusão) —
  // migração 052. A mesma pessoa pode ter os dois papéis: são DUAS linhas, e é
  // por isso que `papel` entra no índice único. O default no banco é 'executou',
  // mas nenhum caminho de escrita o exercita: todos passam `papel` explícito.
  papel: text("papel").notNull().default("executou").$type<MnjExecutorPapel>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("mnj_operacao_executor_idx").on(t.mnjOperacaoId, t.funcionarioId, t.papel),
]);

export type MnjOperacaoExecutor = typeof mnjOperacaoExecutor.$inferSelect;
export type InsertMnjOperacaoExecutor = typeof mnjOperacaoExecutor.$inferInsert;

// Fonte da verdade da quantidade — NO NÍVEL DA OPERAÇÃO (kg TOTAL, aplicado à
// área somada de todos os setores cobertos). Por setor é sempre DERIVADO
// (kg_setor = kg_total × area_setor/area_total_da_operação), nunca armazenado.
// kg/ha também nunca é armazenado — é kg_total / área_total_aplicada.
export const mnjOperacaoItem = pgTable("mnj_operacao_item", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  mnjOperacaoId: varchar("mnj_operacao_id").notNull().references(() => mnjOperacao.id, { onDelete: "cascade" }),
  mnjProdutoId: varchar("mnj_produto_id").notNull().references(() => mnjProduto.id),
  // Na UNIDADE DO PRODUTO (mnjProduto.unidade) — renomeadas de kg_total_* na
  // migração 052. O nome antigo era o que fazia 9,95 L de óleo mineral serem
  // somados como quilos no card.
  qtdPrevista: decimal("qtd_prevista", { precision: 12, scale: 4 }).notNull(),
  qtdRealizada: decimal("qtd_realizada", { precision: 12, scale: 4 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("mnj_operacao_item_org_operacao_produto_idx").on(t.orgId, t.mnjOperacaoId, t.mnjProdutoId),
]);

export type MnjOperacaoItem = typeof mnjOperacaoItem.$inferSelect;
export type InsertMnjOperacaoItem = typeof mnjOperacaoItem.$inferInsert;

// ─── Linha do tempo da operação (v2, migração 052) ─────────────────────────────
//
// Iniciar e concluir são UM TOQUE, e cada um vira um evento com hora, autor e
// ORIGEM. É o alicerce do ciclo 2 (tela do operador) e do WhatsApp: o agente vai
// chamar os mesmos endpoints com `origem: 'whatsapp'`, e o boletim vai ler daqui
// "ontem: 3 concluídas, 1 atrasada" em vez de inferir de colunas-espelho.
//
// ⚠️ `mnjOperacao.iniciadoEm`/`concluidoEm`/`concluidoPorUserId` são ESPELHO desta
// tabela, escritos na mesma transação da transição — existem para a consulta ficar
// barata. Divergiu? A linha do tempo é quem manda.

export const MNJ_EVENTO_TIPO = [
  "criada", "planejada", "iniciada", "pausada", "retomada", "concluida",
  "cancelada", "reagendada", "reaberta", "comentario", "editada",
] as const;
export type MnjEventoTipo = (typeof MNJ_EVENTO_TIPO)[number];

// `sistema` = geração por receita / cron — o único que não tem `userId`.
export const MNJ_EVENTO_ORIGEM = ["app", "whatsapp", "sistema"] as const;
export type MnjEventoOrigem = (typeof MNJ_EVENTO_ORIGEM)[number];

export const mnjOperacaoEvento = pgTable("mnj_operacao_evento", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  mnjOperacaoId: varchar("mnj_operacao_id").notNull().references(() => mnjOperacao.id, { onDelete: "cascade" }),
  tipo: text("tipo").notNull().$type<MnjEventoTipo>(),
  userId: varchar("user_id").references(() => users.id),
  origem: text("origem").notNull().default("app").$type<MnjEventoOrigem>(),
  // Texto do comentário, data antiga/nova de um reagendamento, motivo do
  // cancelamento, foto_url. Forma livre de propósito: cada `tipo` carrega o que
  // precisa, e nenhum consumidor lê payload de um tipo que não conhece.
  payload: json("payload").$type<Record<string, unknown> | null>(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  index("mnj_operacao_evento_operacao_idx").on(t.mnjOperacaoId, t.createdAt),
]);

export type MnjOperacaoEvento = typeof mnjOperacaoEvento.$inferSelect;
export type InsertMnjOperacaoEvento = typeof mnjOperacaoEvento.$inferInsert;

// ─── Verbos de estado (spec §7) — a API que o WhatsApp vai chamar ─────────────
//
// Todos aceitam `origem`: o agente manda `whatsapp`, o cron manda `sistema`, a
// tela omite e cai em `app`. É o que permite auditar DE ONDE veio cada toque sem
// um endpoint duplicado por canal.

export const mnjOrigemSchema = z.enum(MNJ_EVENTO_ORIGEM).optional();

/**
 * PATCH /operacoes/:id — edição de CAMPO, sem mexer no ciclo de vida (spec §7).
 *
 * Hoje só `prioridade`, que é a ação inline do Backlog (§6.3). Deliberadamente
 * mínimo: campo aqui sem tela que o edite é API especulativa, e o resto do
 * formulário de edição chega junto da tela que o pede.
 *
 * Mudança de DATA não passa por aqui — ela é uma transição (sair/entrar no
 * backlog) e mora no `PATCH /status`, que já grava `dataAnterior`/`dataNova`.
 */
export const mnjOperacaoEditarSchema = z.object({
  prioridade: z.enum(MNJ_OPERACAO_PRIORIDADE).optional(),
  // `null`/vazio limpa o título — vale com atividade (o card cai no nome dela); sem
  // atividade o servidor recusa (o CHECK `atividade_id OR titulo`).
  titulo: z.string().trim().max(200).nullish(),
  local: z.string().trim().max(120).nullish(),
  frenteId: z.string().uuid("Frente inválida").nullish(),
  // Substitui os executores com papel `atribuido`. Join puro — nunca cria tarefa/diária.
  atribuidos: z.array(z.string().uuid("Funcionário inválido")).max(20).optional(),
  // ─── Editar = criar: o que a tela de criação pede, a edição também aceita ──────
  atividadeId: z.string().uuid("Atividade inválida").nullish(),
  observacao: z.string().trim().max(2000).nullish(),
  // SUBSTITUI o conjunto de setores. Os que já estavam mantêm a área e o percentual
  // congelados; o novo recebe a área atual.
  setores: z.array(z.object({ setorId: z.string().uuid("Setor inválido") })).max(60).optional(),
  // SUBSTITUI os itens previstos (unidade do produto). Dose/ha é derivada, nunca gravada.
  itens: z.array(z.object({
    mnjProdutoId: z.string().uuid("Produto inválido"),
    qtdPrevista: z.union([z.string(), z.number()])
      .transform(v => String(v))
      .refine(v => !isNaN(parseFloat(v)) && parseFloat(v) > 0, "Quantidade deve ser maior que zero"),
  })).max(60).optional(),
  origem: mnjOrigemSchema,
}).refine(
  d => d.prioridade !== undefined || d.titulo !== undefined || d.local !== undefined
    || d.frenteId !== undefined || d.atribuidos !== undefined || d.atividadeId !== undefined
    || d.observacao !== undefined || d.setores !== undefined || d.itens !== undefined,
  { message: "Informe ao menos um campo para editar" },
);

export type MnjOperacaoEditarInput = z.infer<typeof mnjOperacaoEditarSchema>;

export const mnjIniciarSchema = z.object({ origem: mnjOrigemSchema });
export const mnjPausarSchema = z.object({ origem: mnjOrigemSchema });
export const mnjReabrirSchema = z.object({ origem: mnjOrigemSchema });

export const mnjCancelarSchema = z.object({
  motivo: z.string().min(1, "Cancelar exige um motivo curto").max(280),
  origem: mnjOrigemSchema,
});

/**
 * CONCLUIR — tudo numa chamada só.
 *
 * Todos os campos são opcionais de propósito: o caso comum no campo é confirmar o
 * previsto com um toque, e um POST de corpo vazio tem que funcionar. Item omitido
 * mantém o que já estava gravado; `qtdRealizada: 0` é zero de verdade (não
 * aplicou), e por isso o mínimo é 0 e não 1.
 */
export const mnjConcluirSchema = z.object({
  itens: z.array(z.object({
    mnjProdutoId: z.string().uuid("Produto inválido"),
    qtdRealizada: z.union([z.string(), z.number()])
      .transform(v => String(v))
      .refine(v => !isNaN(parseFloat(v)) && parseFloat(v) >= 0, "Quantidade inválida"),
  })).optional(),
  executores: z.array(z.string().uuid("Funcionário inválido")).optional(),
  observacao: z.string().max(2000).nullish(),
  origem: mnjOrigemSchema,
});

export type MnjConcluirInput = z.infer<typeof mnjConcluirSchema>;

// ─── Frentes e Lista de pendências (migração 079) ─────────────────────────────

export const mnjFrenteCriarSchema = z.object({
  nome: z.string().trim().min(1, "Informe o nome da frente").max(80),
  emoji: z.string().trim().max(8).nullish(),
  responsavelFuncionarioId: z.string().uuid("Responsável inválido").nullish(),
  encarregadoFuncionarioId: z.string().uuid("Encarregado inválido").nullish(),
  agentAudienciaId: z.string().uuid("Grupo inválido").nullish(),
  lembreteAtivo: z.boolean().optional(),
  ordem: z.number().int().min(0).max(1000).optional(),
  ativo: z.boolean().optional(),
});
export const mnjFrenteEditarSchema = mnjFrenteCriarSchema.partial();
export type MnjFrenteCriarInput = z.infer<typeof mnjFrenteCriarSchema>;
export type MnjFrenteEditarInput = z.infer<typeof mnjFrenteEditarSchema>;

/** POST /api/manejo/operacoes — um item de lista (sem lote, sem insumo, sem setor). */
export const mnjItemListaCriarSchema = z.object({
  titulo: z.string().trim().min(1, "Informe o título do item").max(200).optional(),
  // Opcional; se vier, tem de ser de manutenção (a regra de setor mora em /lotes).
  atividadeId: z.string().uuid("Atividade inválida").optional(),
  frenteId: z.string().uuid("Frente inválida").nullish(),
  local: z.string().trim().max(120).nullish(),
  prioridade: z.enum(MNJ_OPERACAO_PRIORIDADE).optional(),
  dataPrevista: z.string().refine(v => !isNaN(Date.parse(v)), "Data inválida").nullish(),
  atribuidos: z.array(z.string().uuid("Funcionário inválido")).max(20).optional(),
  observacao: z.string().trim().max(2000).nullish(),
  origem: mnjOrigemSchema,
}).refine(d => !!d.titulo || !!d.atividadeId, { message: "Informe o título do item (ou uma atividade)", path: ["titulo"] });
export type MnjItemListaCriarInput = z.infer<typeof mnjItemListaCriarSchema>;

export const mnjItensEmMassaSchema = z.object({
  frenteId: z.string().uuid("Frente inválida"),
  titulos: z.array(z.string().trim().min(1).max(200)).min(1, "Nenhum item informado").max(50, "No máximo 50 itens por vez"),
  origem: mnjOrigemSchema,
});

export const mnjOrdemSchema = z.object({
  frenteId: z.string().uuid("Frente inválida"),
  ids: z.array(z.string().uuid("Item inválido")).min(1).max(300),
});

// ─── Relations (necessário para os JOINs de kanban/detalhe via db.query.*) ──

export const mnjProdutoRelations = relations(mnjProduto, ({ many }) => ({
  programaItens: many(mnjProgramaItem),
  operacaoItens: many(mnjOperacaoItem),
}));

export const mnjProgramaRelations = relations(mnjPrograma, ({ one, many }) => ({
  setor: one(setores, {
    fields: [mnjPrograma.setorId],
    references: [setores.id],
  }),
  itens: many(mnjProgramaItem),
}));

export const mnjProgramaItemRelations = relations(mnjProgramaItem, ({ one }) => ({
  programa: one(mnjPrograma, {
    fields: [mnjProgramaItem.mnjProgramaId],
    references: [mnjPrograma.id],
  }),
  produto: one(mnjProduto, {
    fields: [mnjProgramaItem.mnjProdutoId],
    references: [mnjProduto.id],
  }),
}));

export const mnjLoteRelations = relations(mnjLote, ({ many }) => ({
  operacoes: many(mnjOperacao),
}));

export const mnjOperacaoRelations = relations(mnjOperacao, ({ one, many }) => ({
  lote: one(mnjLote, {
    fields: [mnjOperacao.mnjLoteId],
    references: [mnjLote.id],
  }),
  atividade: one(atividades, {
    fields: [mnjOperacao.atividadeId],
    references: [atividades.id],
  }),
  frente: one(mnjFrente, {
    fields: [mnjOperacao.frenteId],
    references: [mnjFrente.id],
  }),
  setores: many(mnjOperacaoSetor),
  executores: many(mnjOperacaoExecutor),
  itens: many(mnjOperacaoItem),
  eventos: many(mnjOperacaoEvento),
}));

export const mnjOperacaoEventoRelations = relations(mnjOperacaoEvento, ({ one }) => ({
  operacao: one(mnjOperacao, {
    fields: [mnjOperacaoEvento.mnjOperacaoId],
    references: [mnjOperacao.id],
  }),
  usuario: one(users, {
    fields: [mnjOperacaoEvento.userId],
    references: [users.id],
  }),
}));

export const mnjOperacaoSetorRelations = relations(mnjOperacaoSetor, ({ one }) => ({
  operacao: one(mnjOperacao, {
    fields: [mnjOperacaoSetor.mnjOperacaoId],
    references: [mnjOperacao.id],
  }),
  setor: one(setores, {
    fields: [mnjOperacaoSetor.setorId],
    references: [setores.id],
  }),
}));

export const mnjOperacaoExecutorRelations = relations(mnjOperacaoExecutor, ({ one }) => ({
  operacao: one(mnjOperacao, {
    fields: [mnjOperacaoExecutor.mnjOperacaoId],
    references: [mnjOperacao.id],
  }),
  funcionario: one(funcionarios, {
    fields: [mnjOperacaoExecutor.funcionarioId],
    references: [funcionarios.id],
  }),
}));

export const mnjOperacaoItemRelations = relations(mnjOperacaoItem, ({ one }) => ({
  operacao: one(mnjOperacao, {
    fields: [mnjOperacaoItem.mnjOperacaoId],
    references: [mnjOperacao.id],
  }),
  produto: one(mnjProduto, {
    fields: [mnjOperacaoItem.mnjProdutoId],
    references: [mnjProduto.id],
  }),
}));

// ─── Commit da operação — payload de entrada da rota POST /api/manejo/lotes ──
// Espelha exatamente a `proposta` já confirmada pelo usuário no card de
// confirmação (ver server/lib/manejo/interpretar.ts). Nada chega aqui sem
// ter passado pelo portão de confirmação.

export const mnjOperacaoSetorCommitSchema = z.object({
  setorId: z.string().uuid("Setor inválido"),
  percentualAplicado: z.number().min(0.01).max(100).default(100),
});

export const mnjOperacaoCommitInputSchema = z.object({
  // Atividade DESTA operação (catálogo `atividades`, o mesmo do Equipe). Opcional
  // porque o caminho do agente/interpretador só fala de fertirrigação e deixa o
  // servidor resolver a atividade pelo nome; a criação manual (formulário de "Nova
  // operação") manda explicitamente a que o usuário escolheu. Quem checa se o id é
  // da organização é `createLote` (server/storage/manejo.ts) — nunca só o schema.
  atividadeId: z.string().uuid("Atividade inválida").optional(),
  // 🔴 `null` = a operação nasce no BACKLOG. O par status⇔data é validado no
  // servidor e no banco (CHECK da 052) — nunca só aqui.
  dataPrevista: z.string().refine(v => !isNaN(Date.parse(v)), "Data inválida").nullish(),
  prioridade: z.enum(MNJ_OPERACAO_PRIORIDADE).optional(),
  titulo: z.string().min(1).max(200).nullish(),
  observacao: z.string().max(2000).nullish(),
  // Os MESMOS padrões da Lista (frente, responsável, local, ordem na fila): qualquer
  // porta de criação aplica-os pelo mesmo helper do servidor. Todos opcionais — o
  // agente/interpretador continua mandando o que sempre mandou.
  frenteId: z.string().uuid("Frente inválida").nullish(),
  atribuidos: z.array(z.string().uuid("Funcionário inválido")).max(20).optional(),
  local: z.string().trim().max(120).nullish(),
  // Litros. Só faz sentido em pulverização/foliar; a tela é quem pergunta.
  volumeCaldaL: z.number().positive("Volume de calda deve ser maior que zero").nullish(),
  // 🔴 Pode ser VAZIO quando a atividade é de `manutencao` (spec §9.5: sede,
  // L1/L2, conserto de bomba não têm setor produtivo). Para todas as outras
  // categorias o servidor exige ao menos um — `createLote` é quem sabe a
  // categoria da atividade, então a regra mora lá, não no schema.
  setores: z.array(mnjOperacaoSetorCommitSchema).default([]),
  // Zero insumos é estado válido (roçagem, desbaste, limpeza de filtro).
  itens: z.array(z.object({
    mnjProdutoId: z.string().uuid("Produto inválido"),
    // Na UNIDADE DO PRODUTO. Dose/ha e dose/100 L são DERIVADAS na leitura,
    // nunca gravadas.
    qtdPrevista: z.number().positive("Quantidade deve ser maior que zero"),
    preenchidoPeloPrograma: z.boolean().optional(),
  })).default([]),
});

export const mnjLoteCommitSchema = z.object({
  origem: z.enum(["texto", "whatsapp", "whatsapp_audio", "manual"]).default("texto"),
  entradaRaw: z.string().min(1, "Entrada original é obrigatória"),
  entradaMidiaUrl: z.string().url().nullish(),
  interpretacao: z.record(z.string(), z.unknown()).optional(),
  operacoes: z.array(mnjOperacaoCommitInputSchema).min(1, "Lote precisa de ao menos uma operação"),
});

export type MnjOperacaoSetorCommit = z.infer<typeof mnjOperacaoSetorCommitSchema>;
export type MnjOperacaoCommitInput = z.infer<typeof mnjOperacaoCommitInputSchema>;
export type MnjLoteCommit = z.infer<typeof mnjLoteCommitSchema>;
export type MnjOperacaoStatusPatch = z.infer<typeof mnjOperacaoStatusPatchSchema>;
export type MnjOperacaoItensPatch = z.infer<typeof mnjOperacaoItensPatchSchema>;

export type MnjOperacaoWithItens = MnjOperacao & {
  // `null` = item da Lista de pendências (só título). Toda leitura trata os dois casos.
  atividade: Atividade | null;
  frente: MnjFrente | null;
  setores: (MnjOperacaoSetor & { setor: Setor })[];
  executores: (MnjOperacaoExecutor & { funcionario: { id: string; nome: string; apelido: string | null } })[];
  itens: (MnjOperacaoItem & { produto: MnjProduto })[];
  // Só no DETALHE (GET /operacoes/:id) — a lista não carrega a linha do tempo.
  // Ausente ≠ vazia: `undefined` é "não veio nesta consulta".
  eventos?: MnjOperacaoEvento[];
};

// ─── Interpretador — payload de entrada/saída de POST /api/manejo/interpretar
// Definido aqui (não em server/lib/manejo/interpretar.ts) para que o mesmo
// tipo de `proposta` seja importável pelo client (card de confirmação) sem
// duplicar a forma do dado.

export const interpretarInputSchema = z.object({
  texto: z.string().min(1, "Texto é obrigatório"),
});
export type InterpretarInput = z.infer<typeof interpretarInputSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// MÓDULO AGENT — Fase A0 (chat dentro do app)
// ═══════════════════════════════════════════════════════════════════════════
//
// Serviço de plataforma (Rizoma_Agente_Whatsapp_Arquitetura.md §6.1). Fase A0 é
// só o chat dentro do app — sem identidade multi-canal (telefone/WhatsApp/
// grupos), que é a Fase A, ciclo separado. `org_id`/`user_id` vêm da sessão.
//
// Escopo mínimo: 1 conversa "ativa" por (org, user) — sem threading de
// conversas paralelas. Toda atualização é upsert na mesma linha via o
// UNIQUE (org_id, user_id) abaixo.

export const agentConversa = pgTable("agent_conversa", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  userId: varchar("user_id").notNull().references(() => users.id),
  // {capacidade, parametros, resultadoResumo} do último turno — não é histórico
  // completo, só o suficiente pra resolver referências ("cria as tarefas DESSE programa").
  turnoAnterior: json("turno_anterior").$type<Record<string, unknown> | null>(),
  // PropostaManejo aguardando confirmação — nullable, a maioria das conversas
  // não tem nada pendente. Tipado como objeto genérico aqui (schema.ts não
  // depende de shared/manejo-proposta.ts); o agente faz o cast na leitura.
  propostaPendente: json("proposta_pendente").$type<Record<string, unknown> | null>(),
  // Pendência vence — não confirmar às cegas algo de um contexto antigo.
  expiraEm: timestamp("expira_em"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("agent_conversa_org_user_idx").on(t.orgId, t.userId),
]);

export type AgentConversa = typeof agentConversa.$inferSelect;
export type InsertAgentConversa = typeof agentConversa.$inferInsert;

// ═══════════════════════════════════════════════════════════════════════════
// MÓDULO AGENT — Ciclo 2 (identidade, agenda e entrega)
// ═══════════════════════════════════════════════════════════════════════════
//
// Rizoma_Agente_Etapa1_Boletim_Spec.md §3. Quatro tabelas: QUEM recebe
// (`agent_contato`), PARA ONDE vai (`agent_audiencia`), QUANDO e o quê
// (`agent_assinatura`) e O QUE JÁ SAIU (`agent_mensagem`).
//
// Nenhum enum do Postgres aqui de propósito: `text` + união de tipos no
// TypeScript. Enum de banco exige DDL para cada valor novo — e o Ciclo 3
// acrescenta pelo menos `whatsapp` em `canal` e `entregue` em `status`.

/** Papel do contato — hoje só documenta a intenção; o filtro por papel é da Etapa 2. */
export type AgentContatoPapel = "gestor" | "operador" | "observador";

export const agentContato = pgTable("agent_contato", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  // Email e telefone são os dois destinos possíveis, os dois opcionais na
  // coluna: neste ciclo o canal é email, no Ciclo 3 entra o WhatsApp. O CHECK da
  // migração garante que pelo menos um dos dois existe — contato sem nenhum
  // destino não é contato, é anotação.
  email: text("email"),
  /** E.164 (+5574...). Nullable neste ciclo — quem preenche é o Ciclo 3. */
  telefone: text("telefone"),
  funcionarioId: varchar("funcionario_id").references(() => funcionarios.id),
  userId: varchar("user_id").references(() => users.id),
  papel: text("papel").$type<AgentContatoPapel>().notNull().default("gestor"),
  // null = ainda não fez opt-in. Só importa no WhatsApp (a Meta exige que a
  // pessoa escreva primeiro); por email não há essa exigência.
  optinEm: timestamp("optin_em"),
  ativo: boolean("ativo").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  index("agent_contato_org_idx").on(t.orgId),
  // GLOBAL, não por org: no WhatsApp o telefone é o que RESOLVE a organização de
  // quem escreveu — se o mesmo número existisse em duas orgs, não haveria como
  // saber de quem é a mensagem. Parcial porque telefone é nullable neste ciclo.
  uniqueIndex("agent_contato_telefone_idx").on(t.telefone).where(sql`telefone IS NOT NULL`),
]);

export type AgentContato = typeof agentContato.$inferSelect;
export type InsertAgentContato = typeof agentContato.$inferInsert;

/** Destino de entrega: uma pessoa ou um grupo. Grupo é Ciclo 3+. */
export type AgentAudienciaTipo = "contato" | "grupo";

export const agentAudiencia = pgTable("agent_audiencia", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  tipo: text("tipo").$type<AgentAudienciaTipo>().notNull().default("contato"),
  /** Email, telefone E.164 ou group id — o que o canal da assinatura souber usar. */
  destino: text("destino").notNull(),
  // Desvio anotado da spec §3: a audiência de tipo `contato` guarda de QUEM ela
  // é. Sem isso, trocar o email do contato exigiria caçar a audiência pelo
  // destino ANTIGO — que é exatamente o dado que está mudando.
  agentContatoId: varchar("agent_contato_id").references(() => agentContato.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  index("agent_audiencia_org_idx").on(t.orgId),
  uniqueIndex("agent_audiencia_contato_idx").on(t.agentContatoId).where(sql`agent_contato_id IS NOT NULL`),
]);

export type AgentAudiencia = typeof agentAudiencia.$inferSelect;
export type InsertAgentAudiencia = typeof agentAudiencia.$inferInsert;

/**
 * Capacidade agendável. `boletim.semanal` entrega desde 08/2026 — sai no sábado
 * pela MESMA assinatura do diário, com a capacidade trocada (não existe linha
 * `boletim.semanal` em `agent_assinatura`; ver server/routes/cron.ts).
 * `boletim.teste` (Enviar teste, aba Boletim) é DELIBERADAMENTE
 * uma capacidade própria, nunca `boletim.diario` com uma flag: é o que garante
 * que um teste jamais compete pela mesma vaga de idempotência do envio
 * agendado — e o índice único de `agent_mensagem` (migração 038) exclui
 * `boletim.teste` de propósito, pra que testes repetidos do mesmo dia também
 * não colidam entre si.
 */
export type AgentCapacidade =
  | "boletim.diario" | "boletim.semanal" | "boletim.teste"
  // Avisos do número do Rizoma (migração 078): um prefixo TIPADO pela lista de
  // modelos de shared/avisos.ts, nunca string livre. `aviso.teste` é o "Enviar
  // teste para mim" do editor — capacidade própria pelo mesmo motivo do
  // `boletim.teste`: um teste não pode ocupar a vaga de um envio real.
  | `aviso.${AvisoChave}` | "aviso.teste"
  // Lembrete diário da frente do Manejo (migração 080). `.teste` = Prévia/Enviar
  // agora da ⚙ da frente, fora do índice de idempotência.
  | "frente.lista" | "frente.lista.teste";
/**
 * Canal de entrega. Duas chaves de WhatsApp, nunca uma genérica `whatsapp`
 * (Ciclo 3a): `whatsapp_gzappy` é o número compartilhado com o marketplace,
 * ligado por token, sem exigência de opt-in; `whatsapp_cloud` é a Meta Cloud
 * API oficial — ainda não implementada, aguardando verificação da empresa.
 * Migrar uma assinatura de um caminho pro outro é trocar este campo, e o
 * histórico continua dizendo por qual caminho cada mensagem saiu.
 */
export type AgentCanal = "email" | "whatsapp_gzappy" | "whatsapp_cloud";

export const agentAssinatura = pgTable("agent_assinatura", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  agentAudienciaId: varchar("agent_audiencia_id").notNull().references(() => agentAudiencia.id),
  capacidade: text("capacidade").$type<AgentCapacidade>().notNull(),
  canal: text("canal").$type<AgentCanal>().notNull().default("email"),
  /** Cron de 5 campos em horário LOCAL da `timezone` abaixo — '0 6 * * *' / '0 6 * * 1'. */
  agendaCron: text("agenda_cron").notNull().default("0 6 * * *"),
  timezone: text("timezone").notNull().default("America/Bahia"),
  ativo: boolean("ativo").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  index("agent_assinatura_org_idx").on(t.orgId),
  // Uma assinatura por (tipo de boletim, CANAL) por audiência — não só por tipo de
  // boletim. Sem o canal aqui, ligar o WhatsApp da mesma audiência que já recebe
  // por email faria o UPSERT (Ciclo 2) achar a linha existente e TROCAR o canal
  // dela, desligando o email em silêncio — exatamente o arranjo que o Ciclo 3a
  // depende de manter (email como rede de segurança se o número for banido).
  // Migração 036.
  uniqueIndex("agent_assinatura_audiencia_capacidade_idx").on(t.orgId, t.agentAudienciaId, t.capacidade, t.canal),
]);

export type AgentAssinatura = typeof agentAssinatura.$inferSelect;
export type InsertAgentAssinatura = typeof agentAssinatura.$inferInsert;

export type AgentMensagemDirecao = "out" | "in";
/**
 * `pulado_vazio` é status de primeira classe, não ausência de linha: dia sem
 * movimento não vira email, mas PRECISA constar no histórico — senão o Hugo não
 * distingue "não teve movimento" de "o cron não rodou".
 */
export type AgentMensagemStatus = "enviada" | "falhou" | "pulado_vazio";

export const agentMensagem = pgTable("agent_mensagem", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  direcao: text("direcao").$type<AgentMensagemDirecao>().notNull(),
  canal: text("canal").$type<AgentCanal>().notNull(),
  destino: text("destino").notNull(),
  texto: text("texto").notNull(),
  capacidade: text("capacidade").$type<AgentCapacidade>(),
  agentAudienciaId: varchar("agent_audiencia_id").references(() => agentAudiencia.id),
  // PERÍODO do boletim, não a data do envio. Duas colunas, e não uma `data_alvo`:
  // com uma só, o semanal de 27/07–02/08 e o diário de 02/08 colidiriam na
  // idempotência (ou exigiriam uma convenção implícita). No diário são iguais.
  periodoInicio: date("periodo_inicio"),
  periodoFim: date("periodo_fim"),
  status: text("status").$type<AgentMensagemStatus>().notNull(),
  // Frente do Manejo a que a mensagem se refere (migração 080) — só `frente.lista*`.
  // Sem `.references()`: `mnj_frente` é declarada acima, mas a FK é ON DELETE SET NULL no banco.
  mnjFrenteId: varchar("mnj_frente_id"),
  erro: text("erro"),
  /** Id do provedor: hoje o do Resend; no Ciclo 3, o `wamid` da Meta. */
  providerId: text("provider_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  index("agent_mensagem_org_criado_idx").on(t.orgId, t.createdAt),
  // 🔴 ESTE ÍNDICE É A IDEMPOTÊNCIA, não decoração. A Vercel pode reexecutar um
  // cron; sem ele o boletim sai duas vezes. O caminho de envio RESERVA a linha
  // primeiro (INSERT ... ON CONFLICT DO NOTHING) e só manda se a reserva criou a
  // linha. Reservar depois de mandar não protege de reexecução concorrente.
  //
  // 🔴 CANAL entrou no índice no Ciclo 3a (migração 036). Sem ele, ligar email E
  // WhatsApp na mesma audiência faria o primeiro canal a rodar reservar a linha
  // do período e o segundo receber "já enviada" sem nunca mandar — furo do #111
  // que derrubaria justamente a rede de segurança (email) que torna seguro
  // compartilhar o número do WhatsApp com o marketplace.
  //
  // 🔴 Restrito a `boletim.diario`/`boletim.semanal` desde a migração 038 —
  // `boletim.teste` (Enviar teste) fica de FORA de propósito. A vaga de um
  // período nunca é a mesma (capacidade diferente já bastava pra isso), mas
  // sem excluir `boletim.teste` daqui, o SEGUNDO teste do mesmo dia pra mesma
  // pessoa colidiria com o PRIMEIRO — e "mandar de novo pra conferir" é
  // exatamente o caso de uso do botão.
  uniqueIndex("agent_mensagem_idempotencia_idx")
    .on(t.orgId, t.capacidade, t.periodoInicio, t.periodoFim, t.agentAudienciaId, t.canal)
    .where(sql`direcao = 'out' AND capacidade IN ('boletim.diario', 'boletim.semanal')`),
  // Lembrete da frente (migração 080): uma por (frente, dia, canal).
  uniqueIndex("agent_mensagem_frente_lista_idx")
    .on(t.orgId, t.mnjFrenteId, t.periodoInicio, t.canal)
    .where(sql`direcao = 'out' AND capacidade = 'frente.lista'`),
]);

export type AgentMensagem = typeof agentMensagem.$inferSelect;
export type InsertAgentMensagem = typeof agentMensagem.$inferInsert;

// ═══════════════════════════════════════════════════════════════════════════
// MÓDULO MANEJO — a borda Aegro NÃO EXISTE MAIS (Operações v2, 08/2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// `erpCredencial`, `erpLink`, `erpSyncLog`, `estoqueSaldoCache` e os enums
// `erp_sync_direcao`/`erp_sync_log_status` saíram deste arquivo. O Manejo foi
// reconstruído sem integração com ERP e sem controle de estoque — o estoque mora
// no Aegro, por fora, e quem quiser conciliar faz de lá.
//
// ⚠️ As TABELAS ainda existem no banco: este PR é o expand (migração 052), o DROP
// é a 053. Nada as lê a partir daqui.
//
// Histórico completo da integração: Rizoma_Arquitetura_Sync_Aegro.md e a skill
// `rizoma-farm-expert` (references/integracao-aegro-erp.md), marcada como
// aposentada em 08/2026 — o histórico não é apagado, só deixa de valer.

// ═══════════════════════════════════════════════════════════════════════════
// Módulo Auditoria — nota do critério, sem dinheiro
// Migrations: 031_auditoria_fase1.sql, 032_auditoria_remuneracao.sql,
//             033_auditoria_nota_por_criterio.sql, 034_auditoria_sem_dinheiro.sql
//
// ⚠️ Regras estruturais invioláveis (mesma família do bug que dobrou a folha):
//   • Quem recebe nota é o CRITÉRIO (audAuditoriaItemCriterio.nota). Atividade só
//     agrupa: sem peso, sem nota própria. Nota do setor = média SIMPLES de todas
//     as notas de critério lançadas (soma ÷ quantidade). Sem peso em lugar nenhum.
//   • Critério sem nota é linha AUSENTE, nunca linha com nota nula — nunca conta
//     como zero na média.
//   • Nota por funcionário é SEMPRE derivada por JOIN via audAuditoriaResponsavel
//     — nunca coluna.
//   • Responsáveis são da auditoria inteira (não por atividade), pré-preenchidos
//     do cadastro (funcionario_setor, encarregados com fim IS NULL) e editáveis
//     pontualmente sem nunca escrever de volta no cadastro.
//   • A Auditoria NÃO calcula reais. `aud_faixa` publica dois PERCENTUAIS (quanto
//     do variável e quanto do fixo a pessoa recebe naquela nota) — quem
//     transforma em dinheiro é o módulo Financeiro, que lê /api/auditoria/resultados.
// ═══════════════════════════════════════════════════════════════════════════

export const audGrupoEnum = pgEnum("aud_grupo", ["trato", "especializada"]);
export const audStatusEnum = pgEnum("aud_status", ["rascunho", "fechada"]);
// orientativa = direciona o time, não vai para o Financeiro. fechamento = é a
// nota da competência daquele setor — no máx. uma por setor por mês (índice
// parcial da 035). GET /resultados lê só as de fechamento.
export const audTipoEnum = pgEnum("aud_tipo", ["orientativa", "fechamento"]);

// ─── Catálogo: atividades auditáveis e critérios ────────────────────────────

export const audAtividade = pgTable("aud_atividade", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  // Vínculo com a atividade do cadastro do Equipe. Nulo = atividade só de
  // auditoria (ex.: Descapa, Destoca) — não é tarefa de diária, não entra em `atividades`.
  atividadeId: varchar("atividade_id").references(() => atividades.id),
  nome: text("nome").notNull(),
  grupo: audGrupoEnum("grupo").notNull().default("trato"),
  ordem: integer("ordem").notNull().default(0),
  ativo: boolean("ativo").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("aud_atividade_org_nome_idx").on(t.orgId, t.nome),
]);

export type AudAtividade = typeof audAtividade.$inferSelect;
export type InsertAudAtividade = typeof audAtividade.$inferInsert;
export const insertAudAtividadeSchema = createInsertSchema(audAtividade).omit({
  id: true, orgId: true, createdAt: true, updatedAt: true,
}).extend({
  nome: z.string().min(1, "Nome da atividade é obrigatório"),
});
export const updateAudAtividadeSchema = insertAudAtividadeSchema.partial();

export const audCriterio = pgTable("aud_criterio", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  audAtividadeId: varchar("aud_atividade_id").notNull().references(() => audAtividade.id, { onDelete: "cascade" }),
  nome: text("nome").notNull(),
  // `descricao` e `critico` sem uso na Fase 1 — critérios agora são itens curtos
  // de avaliação, não material didático. Deixadas no schema para não exigir
  // migração de volta caso a descrição volte a ser útil na tela.
  descricao: text("descricao"),
  critico: boolean("critico").notNull().default(false),
  ordem: integer("ordem").notNull().default(0),
  ativo: boolean("ativo").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  // Único só entre ATIVOS (migração 055) — um critério removido (soft
  // delete, ativo=false) não reserva mais o nome. Sem o `.where()`, um
  // "Novo critério" desativado travava toda criação seguinte com aquele
  // nome na mesma atividade.
  uniqueIndex("aud_criterio_org_ativ_nome_idx").on(t.orgId, t.audAtividadeId, t.nome).where(sql`ativo`),
]);

export type AudCriterio = typeof audCriterio.$inferSelect;
export type InsertAudCriterio = typeof audCriterio.$inferInsert;
export const insertAudCriterioSchema = createInsertSchema(audCriterio).omit({
  id: true, orgId: true, createdAt: true, updatedAt: true,
}).extend({
  nome: z.string().min(1, "Nome do critério é obrigatório"),
});
export const updateAudCriterioSchema = insertAudCriterioSchema.partial();

// ─── A auditoria ────────────────────────────────────────────────────────────

export const audAuditoria = pgTable("aud_auditoria", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  data: date("data").notNull(),
  auditorUserId: varchar("auditor_user_id").references(() => users.id),
  // Auditor pode ser terceiro (técnico contratado) sem login no sistema.
  auditorNome: text("auditor_nome").notNull(),
  metodologia: text("metodologia").notNull().default("Caminhamento livre — julgamento do auditor"),
  // Nota do setor = média SIMPLES das notas de critério lançadas. NÃO é
  // digitada: o servidor calcula e grava aqui, congelada, no fechamento
  // (fecharAuditoria). É o que a remuneração lê. Nenhuma rota de PATCH aceita
  // este campo — a única escrita é a do fechamento.
  notaFinal: decimal("nota_final", { precision: 4, scale: 2 }),
  observacao: text("observacao"),
  status: audStatusEnum("status").notNull().default("rascunho"),
  // orientativa por default: o caminho seguro é o que NÃO mexe em pagamento.
  // Marcar como 'fechamento' é ato deliberado. Máx. uma de fechamento por
  // setor por mês (índice parcial da 035, defesa contra corrida de verdade —
  // a checagem em createAuditoria/patchAuditoria é a mensagem amigável).
  tipo: audTipoEnum("tipo").notNull().default("orientativa"),
  fechadaEm: timestamp("fechada_em"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type AudAuditoria = typeof audAuditoria.$inferSelect;
export type InsertAudAuditoria = typeof audAuditoria.$inferInsert;

// Responsáveis da AUDITORIA INTEIRA (não por atividade — a matriz setor ×
// atividade → funcionário saiu no lugar de `funcionario_setor`). Pré-preenchidos
// dos encarregados ativos do setor na criação; ajuste no formulário é pontual e
// nunca escreve de volta em `funcionario_setor`.
export const audAuditoriaResponsavel = pgTable("aud_auditoria_responsavel", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  audAuditoriaId: varchar("aud_auditoria_id").notNull().references(() => audAuditoria.id, { onDelete: "cascade" }),
  funcionarioId: varchar("funcionario_id").notNull().references(() => funcionarios.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("aud_auditoria_resp_idx").on(t.orgId, t.audAuditoriaId, t.funcionarioId),
]);

export type AudAuditoriaResponsavel = typeof audAuditoriaResponsavel.$inferSelect;

export const audAuditoriaItem = pgTable("aud_auditoria_item", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  audAuditoriaId: varchar("aud_auditoria_id").notNull().references(() => audAuditoria.id, { onDelete: "cascade" }),
  audAtividadeId: varchar("aud_atividade_id").notNull().references(() => audAtividade.id),
  // Observação da atividade. Sem nota e sem peso — quem recebe nota é o critério
  // (audAuditoriaItemCriterio.nota); a média da atividade é derivada, só exibida.
  observacao: text("observacao"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("aud_auditoria_item_idx").on(t.orgId, t.audAuditoriaId, t.audAtividadeId),
]);

export type AudAuditoriaItem = typeof audAuditoriaItem.$inferSelect;
export type InsertAudAuditoriaItem = typeof audAuditoriaItem.$inferInsert;

// A NOTA mora aqui. Linha existe só quando o critério foi avaliado — ausência
// nunca é nota zero, fica de fora da média (ver comentário da 033 no banco).
export const audAuditoriaItemCriterio = pgTable("aud_auditoria_item_criterio", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  audAuditoriaItemId: varchar("aud_auditoria_item_id").notNull().references(() => audAuditoriaItem.id, { onDelete: "cascade" }),
  audCriterioId: varchar("aud_criterio_id").notNull().references(() => audCriterio.id),
  nota: decimal("nota", { precision: 4, scale: 2 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("aud_auditoria_item_criterio_idx").on(t.orgId, t.audAuditoriaItemId, t.audCriterioId),
]);

export type AudAuditoriaItemCriterio = typeof audAuditoriaItemCriterio.$inferSelect;

// Payloads compostos
const notaSchema = z.union([z.string(), z.number()])
  .transform(v => String(v))
  .refine(v => !isNaN(parseFloat(v)) && parseFloat(v) >= 0 && parseFloat(v) <= 10, "Nota deve estar entre 0 e 10");

export const audAuditoriaCreateSchema = z.object({
  setorId: z.string().uuid("Setor inválido"),
  data: z.string().min(1, "Data é obrigatória"),
  auditorNome: z.string().min(1, "Nome do auditor é obrigatório"),
  auditorUserId: z.string().uuid().nullish(),
  metodologia: z.string().nullish(),
  // Default 'orientativa' — o caminho seguro é o que não mexe em pagamento.
  tipo: z.enum(["orientativa", "fechamento"]).optional(),
  responsavelIds: z.array(z.string().uuid()).optional(),
  atividadeIds: z.array(z.string().uuid()).min(1, "Escolha ao menos uma atividade"),
});

// Sem `notaFinal` de propósito: a nota do setor é calculada e gravada só no
// fechamento (fecharAuditoria), nunca por PATCH.
export const audAuditoriaPatchSchema = z.object({
  data: z.string().nullish(),
  auditorNome: z.string().min(1).nullish(),
  observacao: z.string().nullish(),
  tipo: z.enum(["orientativa", "fechamento"]).optional(),
  responsavelIds: z.array(z.string().uuid()).optional(),
});

// PUT /atividades/ordem — grava a ordem de exibição inteira de uma vez.
export const audOrdemAtividadesSchema = z.object({
  ordens: z.array(z.object({
    id: z.string().uuid(),
    ordem: z.number().int(),
  })).min(1, "Informe ao menos uma atividade"),
});

// `nota: null` num item apaga a linha do critério — não grava nota nula.
export const audItemPatchSchema = z.object({
  observacao: z.string().nullish(),
  criterios: z.array(z.object({
    audCriterioId: z.string().uuid(),
    nota: notaSchema.nullable(),
  })).optional(),
});

export type AudAuditoriaCreateInput = z.infer<typeof audAuditoriaCreateSchema>;
export type AudItemPatchInput = z.infer<typeof audItemPatchSchema>;

// ─── Publicação para o Financeiro: faixa de nota → dois percentuais ─────────
// A Auditoria não calcula reais. Esta tabela é a ÚNICA coisa que sobrevive da
// remuneração — republicada em percentual, para o módulo Financeiro (a ainda
// não construído) aplicar sobre o fixo e o variável de cada contrato.

// A faixa é [notaMin, próxima notaMin). Guardar só o piso torna impossível
// criar buraco ou sobreposição — erro clássico de tabela de faixas com min+max.
export const audFaixa = pgTable("aud_faixa", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  vigenciaInicio: date("vigencia_inicio").notNull(),
  notaMin: decimal("nota_min", { precision: 4, scale: 2 }).notNull(),
  // Os dois significam quanto a pessoa RECEBE (não quanto perde) — mantém a
  // conta uniforme (valor × percentual ÷ 100) nos dois casos. Ex.: nota 3,0 →
  // percentualVariavel=0 (sem variável) e percentualFixo=60 (40% de desconto).
  percentualVariavel: decimal("percentual_variavel", { precision: 5, scale: 2 }).notNull(),
  percentualFixo: decimal("percentual_fixo", { precision: 5, scale: 2 }).notNull().default("100"),
  rotulo: text("rotulo"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("aud_faixa_vigencia_min_idx").on(t.orgId, t.vigenciaInicio, t.notaMin),
]);

export type AudFaixa = typeof audFaixa.$inferSelect;
export const insertAudFaixaSchema = createInsertSchema(audFaixa).omit({
  id: true, orgId: true, createdAt: true, updatedAt: true,
});
export const audFaixasReplaceSchema = z.object({
  vigenciaInicio: z.string().min(1, "Vigência é obrigatória"),
  faixas: z.array(z.object({
    notaMin: z.union([z.string(), z.number()]).transform(v => String(v)),
    percentualVariavel: z.union([z.string(), z.number()]).transform(v => String(v)),
    percentualFixo: z.union([z.string(), z.number()]).transform(v => String(v)),
    rotulo: z.string().nullish(),
  })).min(1, "Informe ao menos uma faixa")
    .refine(fs => fs.some(f => parseFloat(f.notaMin) === 0), "Precisa existir uma faixa começando em 0"),
});

// ─── Irrigação (ciclo 1 — ingestão) ───────────────────────────────────────────
//
// Contexto próprio, prefixo `irr_*` (precedente do `erp_*`). A feature é AGNÓSTICA à
// fonte: nada aqui conhece Rain Bird. A lâmina (mm) e o L/planta NUNCA são colunas —
// derivam do volume calibrado com a área e as plantas do setor (shared/irrigacao.ts).
// Migração 047.

export const irrConector = pgTable("irr_conector", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  provider: text("provider").notNull().default("rainbird_iq4"),
  // Endereço de recebimento POR ORGANIZAÇÃO (irr-<slug>@in.rizoma.farm). É o
  // `received_for` do webhook que resolve a org — nunca um endereço global.
  enderecoRecebimento: text("endereco_recebimento"),
  remetenteEsperado: text("remetente_esperado"),
  ativo: boolean("ativo").notNull().default(true),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  uniqueIndex("irr_conector_org_provider_idx").on(t.orgId, t.provider),
  // ⚠️ Existe também `irr_conector_endereco_idx` — único sobre `lower(endereco_recebimento)`,
  // parcial (`WHERE endereco_recebimento IS NOT NULL`). O Drizzle não modela índice sobre
  // expressão; a FONTE DA VERDADE é a migração 047. Um `drizzle-kit generate` futuro vai
  // querer derrubá-lo — não deixe.
]);

export const irrEstacao = pgTable("irr_estacao", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  // Chave estável da estação — a numeração tem buracos e a ORDEM das linhas do
  // relatório não vale como identidade.
  numero: integer("numero").notNull(),
  nome: text("nome").notNull(),
  ativo: boolean("ativo").notNull().default(true),
  // Abre AO MESMO TEMPO que as outras simultâneas do mesmo setor (migração 051).
  //
  // Quando um setor é coberto por várias estações, o tempo dele é a soma das sequenciais
  // mais o MAIOR entre as simultâneas — somar tudo contaria o mesmo minuto de relógio
  // duas vezes. Foi o que pôs a Laranja com 17:25 num dia de 8:42, porque L1 e L2 abrem
  // juntas. O VOLUME nunca é afetado: ele soma sempre.
  //
  // O relatório traz DURAÇÕES, não janelas — não há como inferir sobreposição do
  // documento, então isto é cadastro. Nasce `false`, e sem nenhuma marcada o cálculo é
  // idêntico ao de antes.
  simultanea: boolean("simultanea").notNull().default(false),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  uniqueIndex("irr_estacao_org_numero_idx").on(t.orgId, t.numero),
]);

export const irrEstacaoSetor = pgTable("irr_estacao_setor", {
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  estacaoId: varchar("estacao_id").notNull().references(() => irrEstacao.id, { onDelete: "cascade" }),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  index("irr_estacao_setor_setor_idx").on(t.orgId, t.setorId),
  // ⚠️ A PRIMARY KEY (estacao_id, setor_id) está só na migração 047 — o Drizzle não a
  // modela aqui. FONTE DA VERDADE: a migração. É ela que impede vínculo duplicado, e o
  // "Aceitar todas as sugestões" conta com isso.
]);

export const irrEstacaoVazao = pgTable("irr_estacao_vazao", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  estacaoId: varchar("estacao_id").notNull().references(() => irrEstacao.id, { onDelete: "cascade" }),
  vazaoM3h: decimal("vazao_m3h", { precision: 10, scale: 2 }).notNull(),
  vigenciaInicio: date("vigencia_inicio").notNull(),
  // NULL = vigente. Índice único PARCIAL no SQL (a fonte da verdade é a migração 047).
  vigenciaFim: date("vigencia_fim"),
  // false = seed vindo do próprio relatório. Enquanto false, a validação de ±30% fica
  // DESLIGADA para esta estação — o sistema não valida o dado contra ele mesmo.
  confirmada: boolean("confirmada").notNull().default(false),
  observacao: text("observacao"),
  criadoPor: varchar("criado_por").references(() => users.id),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
  // ⚠️ `irr_estacao_vazao_vigente_idx` — único PARCIAL em (estacao_id) WHERE
  // vigencia_fim IS NULL — está só na migração 047. FONTE DA VERDADE: a migração. É ele
  // que garante "uma vazão vigente por estação" enquanto o histórico fechado fica livre.
});

export const irrLeitura = pgTable("irr_leitura", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  dataReferencia: date("data_referencia").notNull(),
  revisao: integer("revisao").notNull().default(1),
  vigente: boolean("vigente").notNull().default(true),
  janelaInicio: timestamp("janela_inicio", { withTimezone: true }),
  janelaFim: timestamp("janela_fim", { withTimezone: true }),
  volumeMedidoM3: decimal("volume_medido_m3", { precision: 12, scale: 3 }),
  volumeEstimadoTotalM3: decimal("volume_estimado_total_m3", { precision: 12, scale: 3 }).notNull().default("0"),
  fatorCalibracao: decimal("fator_calibracao", { precision: 8, scale: 4 }),
  origem: text("origem").notNull().$type<IrrOrigem>(),
  arquivoNome: text("arquivo_nome"),
  arquivoHash: text("arquivo_hash").notNull(),
  textoBruto: text("texto_bruto"),
  recebidoEm: timestamp("recebido_em", { withTimezone: true }).defaultNow().notNull(),
  status: text("status").notNull().$type<IrrStatusLeitura>(),
  motivo: text("motivo"),
  manutencao: boolean("manutencao").notNull().default(false),
  manutencaoMotivo: text("manutencao_motivo"),
  criadoPor: varchar("criado_por").references(() => users.id),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  uniqueIndex("irr_leitura_org_data_revisao_idx").on(t.orgId, t.dataReferencia, t.revisao),
  index("irr_leitura_hash_idx").on(t.orgId, t.arquivoHash),
  // ⚠️ `irr_leitura_vigente_idx` — único PARCIAL em (org_id, data_referencia) WHERE
  // vigente — está só na migração 047. FONTE DA VERDADE: a migração. É ele que garante
  // uma leitura vigente por dia.
]);

export const irrLeituraEstacao = pgTable("irr_leitura_estacao", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  leituraId: varchar("leitura_id").notNull().references(() => irrLeitura.id, { onDelete: "cascade" }),
  estacaoNumero: integer("estacao_numero").notNull(),
  nomeRelatorio: text("nome_relatorio"),
  runtimeProgramadoS: integer("runtime_programado_s").notNull().default(0),
  runtimeManualS: integer("runtime_manual_s").notNull().default(0),
  runtimeTotalS: integer("runtime_total_s").notNull().default(0),
  // O que a FONTE diz…
  volumeRelatorioM3: decimal("volume_relatorio_m3", { precision: 12, scale: 3 }),
  vazaoRelatorioM3h: decimal("vazao_relatorio_m3h", { precision: 10, scale: 2 }),
  // …e o que o Rizoma ADOTA (vazão do cadastro). Os dois, sempre.
  volumeAdotadoM3: decimal("volume_adotado_m3", { precision: 12, scale: 3 }),
  vazaoAdotadaM3h: decimal("vazao_adotada_m3h", { precision: 10, scale: 2 }),
}, (t) => [
  uniqueIndex("irr_leitura_estacao_uniq_idx").on(t.leituraId, t.estacaoNumero),
]);

export const irrLeituraSetor = pgTable("irr_leitura_setor", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  leituraId: varchar("leitura_id").notNull().references(() => irrLeitura.id, { onDelete: "cascade" }),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  // Snapshots CONGELADOS na ingestão — corrigir o cadastro depois não reescreve histórico.
  areaHaSnapshot: decimal("area_ha_snapshot", { precision: 6, scale: 2 }),
  plantasSnapshot: integer("plantas_snapshot"),
  runtimeTotalS: integer("runtime_total_s").notNull().default(0),
  volumeEstimadoM3: decimal("volume_estimado_m3", { precision: 12, scale: 3 }).notNull().default("0"),
  volumeCalibradoM3: decimal("volume_calibrado_m3", { precision: 12, scale: 3 }).notNull().default("0"),
}, (t) => [
  uniqueIndex("irr_leitura_setor_uniq_idx").on(t.leituraId, t.setorId),
  index("irr_leitura_setor_setor_idx").on(t.orgId, t.setorId),
]);

// União no TS em vez de pgEnum no Postgres — mesmo precedente de `mnj_operacao.origem`
// e das tabelas agent_*: evita ALTER TYPE, que o runner de migração não consegue
// referenciar na mesma transação.
export type IrrOrigem = "upload" | "email";
export type IrrStatusLeitura = "ok" | "divergente" | "rejeitada";

export type IrrConector = typeof irrConector.$inferSelect;
export type IrrEstacao = typeof irrEstacao.$inferSelect;
export type IrrEstacaoVazao = typeof irrEstacaoVazao.$inferSelect;
export type IrrLeitura = typeof irrLeitura.$inferSelect;
export type IrrLeituraEstacao = typeof irrLeituraEstacao.$inferSelect;
export type IrrLeituraSetor = typeof irrLeituraSetor.$inferSelect;

// PUT /api/manejo/irrigacao/vazoes/:estacaoId — editar NUNCA faz UPDATE de valor:
// fecha a vigência da linha atual e insere outra (regra em storage/irrigacao.ts).
export const irrVazaoUpsertSchema = z.object({
  vazaoM3h: z.union([z.string(), z.number()])
    .transform(v => String(v).replace(",", "."))
    .refine(v => !isNaN(parseFloat(v)) && parseFloat(v) > 0, "Vazão precisa ser maior que zero"),
  observacao: z.string().nullish(),
});

// PUT /api/manejo/irrigacao/estacoes/:estacaoId/setores — o mapeamento estação↔setor.
// Lista vazia é resposta legítima ("essa estação não rega setor nenhum que eu controle").
export const irrEstacaoSetoresSchema = z.object({
  setorIds: z.array(z.string().uuid("Setor inválido")),
  // Ausente preserva o que está gravado — o mesmo critério do PUT do conector: chave que
  // não veio não é chave apagada.
  simultanea: z.boolean().optional(),
});

export const irrLeituraManutencaoSchema = z.object({
  manutencao: z.boolean(),
  motivo: z.string().nullish(),
});

// ─── Irrigação · metas de lâmina (ciclo 2) ────────────────────────────────────
//
// A meta é por MÊS DO ANO (1–12), não por data: é sazonalidade, não calendário. A meta
// de agosto vale para todo agosto, todo ano, até alguém trocar — e a aderência de um
// período compara CADA DIA com a meta do mês daquele dia.
//
// `valor` está na UNIDADE DA CULTURA do setor (banana em mm/dia, citros em L/planta/dia).
// Não existe conversão em lugar nenhum: em gotejo, mm sobre a área total não significa
// nada. Ver `unidadeDaCultura()` em shared/irrigacao.ts.
//
// Editar NUNCA faz UPDATE de valor — fecha a vigência e insere outra linha, igual a
// `irr_estacao_vazao`, e pelo mesmo motivo: é a base de comparação do futuro KPI, e
// mudança de base precisa ser rastreável. Migração 049.
export const irrMeta = pgTable("irr_meta", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().references(() => organizations.id),
  setorId: varchar("setor_id").notNull().references(() => setores.id),
  mes: integer("mes").notNull(),
  valor: decimal("valor", { precision: 8, scale: 2 }).notNull(),
  vigenciaInicio: date("vigencia_inicio").notNull(),
  vigenciaFim: date("vigencia_fim"),
  criadoPor: varchar("criado_por").references(() => users.id),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  index("irr_meta_org_idx").on(t.orgId, t.setorId),
  // ⚠️ `irr_meta_vigente_idx` — único PARCIAL em (setor_id, mes) WHERE vigencia_fim IS
  // NULL — está só na migração 049. FONTE DA VERDADE: a migração.
]);

export type IrrMeta = typeof irrMeta.$inferSelect;

// PUT /api/manejo/irrigacao/metas — grava uma ou várias células da grade de uma vez.
// `valor: null` APAGA a meta daquele mês (fecha a vigência sem abrir outra) — é
// diferente de meta 0, que seria um alvo de "não regar".
export const irrMetasPatchSchema = z.object({
  metas: z.array(z.object({
    setorId: z.string().uuid("Setor inválido"),
    mes: z.number().int().min(1, "Mês inválido").max(12, "Mês inválido"),
    valor: z.union([z.string(), z.number(), z.null()])
      .transform(v => (v === null || v === "" ? null : String(v).replace(",", ".")))
      .refine(v => v === null || (!isNaN(parseFloat(v)) && parseFloat(v) >= 0), "Meta inválida"),
  })).min(1, "Informe ao menos uma meta").max(200, "Muitas metas de uma vez"),
});

// ═══════════════════════════════════════════════════════════════════════════
// MÓDULO MERCADO — Ciclo 1 (formalizar romaneio + link público)
// ═══════════════════════════════════════════════════════════════════════════
//
// VOCABULÁRIO (não inventar sinônimo): *romaneio* = a nota de venda do produtor,
// que é a tabela `vendas` e já existia. *Compartilhamento* = o lado do COMPRADOR
// desse romaneio — nasce aqui. *Corretor* = quem compra, negocia e paga. Em
// nenhum lugar da UI se escreve "comissão".
//
// ⚠️ FORMALIZAR É SEMPRE ATO EXPLÍCITO. Nada nesta tabela pode nascer como efeito
// colateral de salvar ou emitir uma venda: é a mesma classe de acoplamento que
// dobrou a folha no módulo Equipe. Só o POST /api/mercado/compartilhamentos
// escreve aqui, e só para venda EMITIDA.
//
// ⚠️ Formalizar NÃO altera a venda. Nenhuma coluna de `vendas` é escrita neste
// fluxo — o estado do corretor mora inteiro aqui.

export const mercadoCompartilhamentos = pgTable("mercado_compartilhamentos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  vendaId: varchar("venda_id").notNull().references(() => vendas.id),
  orgProdutorId: varchar("org_produtor_id").notNull().references(() => organizations.id),
  compradorId: varchar("comprador_id").notNull().references(() => compradores.id),
  // A organização de quem COMPRA este romaneio. NULL até ela existir (o claim do
  // C2 é que a cria). Chamava-se `org_compradora_id` até a 057: quem compra pode ser
  // corretor OU distribuidor, e o papel é "comprador" — ver `ORG_TIPO`.
  orgCompradoraId: varchar("org_compradora_id").references(() => organizations.id),
  destinoTelefone: text("destino_telefone").notNull(),
  token: text("token").notNull(),
  tokenExpiraEm: timestamp("token_expira_em", { withTimezone: true }),
  revogadoEm: timestamp("revogado_em", { withTimezone: true }),
  versaoVenda: integer("versao_venda").notNull().default(1),
  statusCarga: text("status_carga").notNull().default("ENVIADO"),
  statusPagamento: text("status_pagamento").notNull().default("A_PAGAR"),
  // SNAPSHOT: o total que o corretor viu na hora de formalizar. Recalculado no
  // SERVIDOR a partir dos itens da venda, nunca lido do client, e nunca reescrito
  // por edição posterior — para isso existe `versaoVenda`.
  valorTotalSnapshot: decimal("valor_total_snapshot", { precision: 14, scale: 2 }).notNull(),
  /**
   * ⚠️ AS TRÊS MORTAS desde a 061: o vencimento mora na VENDA; dropar na próxima
   * limpeza.
   *
   * Sem escritor e sem leitor. Elas amarravam o prazo à FORMALIZAÇÃO, e o
   * resultado apareceu com dado real: 15 dos 16 romaneios em aberto sem
   * vencimento, porque sem compartilhamento não existia linha onde gravar. A
   * regra 6 (os dois lados escrevem, vale o último) não mudou — mudou de casa,
   * com `definido_por` e `alterado_em` junto.
   *
   * Ficam: este PR não precisa delas dropadas para ficar correto, e o backfill
   * da 061 é mais barato de conferir com a origem ainda no banco.
   */
  vencimento: date("vencimento"),
  vencimentoDefinidoPor: text("vencimento_definido_por"),
  vencimentoAlteradoEm: timestamp("vencimento_alterado_em", { withTimezone: true }),
  enviadoEm: timestamp("enviado_em", { withTimezone: true }),
  vistoEm: timestamp("visto_em", { withTimezone: true }),
  criadoPorUserId: varchar("criado_por_user_id").notNull(),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  // Um compartilhamento por (venda, comprador). O UNIQUE é a defesa de verdade
  // contra formalizar duas vezes; o 409 da rota é a mensagem bonita em cima dele.
  uniqueIndex("uniq_compartilhamento_venda_comprador").on(t.vendaId, t.compradorId),
  uniqueIndex("mercado_compartilhamentos_token_key").on(t.token),
  index("idx_comp_produtor").on(t.orgProdutorId, t.criadoEm),
  index("idx_comp_compradora").on(t.orgCompradoraId, t.statusPagamento, t.vencimento),
]);

export type MercadoCompartilhamento = typeof mercadoCompartilhamentos.$inferSelect;
export type InsertMercadoCompartilhamento = typeof mercadoCompartilhamentos.$inferInsert;

// Append-only: nada aqui é atualizado nem apagado. Toda mudança de estado do
// compartilhamento grava uma linha — é o que permite responder "quando o corretor
// viu?" sem depender de coluna que alguém possa sobrescrever.
export const mercadoEventos = pgTable("mercado_eventos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  compartilhamentoId: varchar("compartilhamento_id").references(() => mercadoCompartilhamentos.id),
  // NULL no C1 — carga consolidada é C3.
  cargaId: varchar("carga_id"),
  /**
   * A OFERTA a que este evento pertence (migração 058). Nullable, como
   * `compartilhamentoId`: cada evento é de um OU de outro. A coluna existe
   * porque a oferta não é romaneio — guardar os eventos dela com
   * `compartilhamento_id` NULL e o id no payload faria um saco de linhas sem
   * como consultar por oferta.
   */
  ofertaId: varchar("oferta_id"),
  tipo: text("tipo").notNull(),
  payload: jsonb("payload"),
  atorOrgId: varchar("ator_org_id"),
  atorUserId: varchar("ator_user_id"),
  origem: text("origem").notNull().default("APP"),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  index("idx_eventos_comp").on(t.compartilhamentoId, t.criadoEm),
]);

export type MercadoEvento = typeof mercadoEventos.$inferSelect;
export type InsertMercadoEvento = typeof mercadoEventos.$inferInsert;

export const MERCADO_EVENTO_TIPO = {
  COMPARTILHAMENTO_CRIADO: "COMPARTILHAMENTO_CRIADO",
  ENVIADO: "ENVIADO",
  // "Marquei como enviado sem querer" é um caso real: o botão marca no mesmo toque
  // que abre o WhatsApp, e o produtor pode desistir na tela do WhatsApp. Desfazer
  // limpa `enviado_em` e grava ESTE evento — a linha do ENVIADO anterior fica, porque
  // `mercado_eventos` é append-only e apagar histórico esconderia o que aconteceu.
  ENVIO_DESFEITO: "ENVIO_DESFEITO",
  VISTO: "VISTO",
  REVOGADO: "REVOGADO",
  // C2 — o corretor criou a conta pelo link e adotou este romaneio. Uma linha por
  // compartilhamento adotado: é o rastro de como a caderneta dele foi montada.
  ADOTADO_POR_COMPRADOR: "ADOTADO_POR_COMPRADOR",
  // C3 — o comprador conferiu os kg (pelo app ou pelo link público). O payload
  // carrega kg do romaneio, kg conferido, valor antes/depois e a origem: é o que
  // permite reconstruir a divergência sem reabrir a conferência.
  CONFERENCIA_REGISTRADA: "CONFERENCIA_REGISTRADA",
  // C3 — o produtor editou uma venda JÁ compartilhada. Versiona o compartilhamento,
  // invalida a conferência existente e devolve `status_carga` para trás.
  VENDA_CORRIGIDA: "VENDA_CORRIGIDA",
  // C3 — vencimento renegociado. O único campo que os dois lados escrevem; o
  // payload guarda de/para e quem mexeu.
  VENCIMENTO_ALTERADO: "VENCIMENTO_ALTERADO",
  // PR 1 da UI do produtor — o produtor lançou o recebimento direto ("Recebi").
  // Hoje marca a venda como PAGA pelo caminho do Cacho e recomputa
  // `status_pagamento`; quando o C4 criar `mercado_pagamentos`, esta mesma ação
  // passa a inserir um lançamento já confirmado — o evento continua sendo este.
  RECEBIMENTO_LANCADO: "RECEBIMENTO_LANCADO",
  // O produtor desfez o "paga" no Cacho. Vira linha no histórico do comprador:
  // o romaneio que ele via pago voltou a dever, e a tela precisa dizer por quê.
  RECEBIMENTO_DESFEITO: "RECEBIMENTO_DESFEITO",
  // O COMPRADOR registrou um pagamento deste romaneio ("quem registra quita").
  // Vive no compartilhamento, e não só na carga: romaneio sem carga (o caso mais
  // comum) não tinha onde guardar o fato, e o histórico dos dois lados ficava
  // mudo sobre o único ato que mexe em dinheiro.
  PAGAMENTO_REGISTRADO: "PAGAMENTO_REGISTRADO",
  // ─── PR 3 — ofertas na praça ──────────────────────────────────────────────
  // Estes seis carregam `ofertaId`, não `compartilhamentoId`. O payload de
  // OFERTA_PUBLICADA guarda a data da cotação congelada e se ela estava vencida
  // no momento da publicação: é o que permite responder depois "a que preço essa
  // carga foi para a praça, e o produtor sabia que o preço era de sexta?".
  OFERTA_PUBLICADA: "OFERTA_PUBLICADA",
  OFERTA_RESPONDIDA: "OFERTA_RESPONDIDA",
  OFERTA_DETALHES_ABERTOS: "OFERTA_DETALHES_ABERTOS",
  OFERTA_DISPENSADA: "OFERTA_DISPENSADA",
  // Fechar N kg com um comprador — o único evento do módulo que cria venda.
  OFERTA_FECHADA: "OFERTA_FECHADA",
  OFERTA_ENCERRADA: "OFERTA_ENCERRADA",
  OFERTA_REENVIADA: "OFERTA_REENVIADA",
  // ─── C5 — a CARGA do comprador ────────────────────────────────────────────
  //
  // Estes carregam `cargaId` (a coluna existe desde a 054, reservada para isto),
  // não `compartilhamentoId`: a carga não é romaneio, e guardar os eventos dela
  // com `compartilhamento_id` NULL e o id no payload faria um saco de linhas sem
  // como consultar por carga — o mesmo motivo que criou `ofertaId`.
  //
  // ⚠️ Os eventos de/para (`_EDITADO`, `_ALTERADO`, `VENDA_DEFINIDA`) guardam o
  // ANTES e o DEPOIS no payload. É o que permite responder "por quanto essa
  // carga foi vendida antes de o corretor renegociar?" sem versionar a linha.
  CARGA_CRIADA: "CARGA_CRIADA",
  // Romaneios v2 — o registro próprio nasce SOZINHO, fora de qualquer carga
  // (073). `carga_id` do evento é NULL; o `itemId` do payload é o que liga o
  // evento ao romaneio (ver `eventosDoItem`).
  ROMANEIO_REGISTRADO: "ROMANEIO_REGISTRADO",
  ROMANEIO_EDITADO: "ROMANEIO_EDITADO",
  ROMANEIO_EXCLUIDO: "ROMANEIO_EXCLUIDO",
  CARGA_ITEM_ADICIONADO: "CARGA_ITEM_ADICIONADO",
  CARGA_ITEM_REMOVIDO: "CARGA_ITEM_REMOVIDO",
  CARGA_ITEM_EDITADO: "CARGA_ITEM_EDITADO",
  CARGA_DESPESA: "CARGA_DESPESA",
  // Cargas v2 — o corretor marcou que mandou o documento ao cliente, e o que ele
  // escolheu mostrar nele. O envio é EVENTO e não coluna: reenviar depois de
  // corrigir a placa é rotina, e uma coluna guardaria só o último.
  CARGA_DOCUMENTO_ENVIADO: "CARGA_DOCUMENTO_ENVIADO",
  CARGA_DOCUMENTO_CONFIG: "CARGA_DOCUMENTO_CONFIG",
  CARGA_VENDA_DEFINIDA: "CARGA_VENDA_DEFINIDA",
  CARGA_VENCIMENTO_ALTERADO: "CARGA_VENCIMENTO_ALTERADO",
  CARGA_FECHADA: "CARGA_FECHADA",
  // Reabrir é EVENTO, não silêncio: o documento já foi para o destino, e desfazer
  // o fechamento depois disso é o tipo de ato que alguém vai querer explicar.
  CARGA_REABERTA: "CARGA_REABERTA",
  CARGA_ENTREGUE: "CARGA_ENTREGUE",
  // O PRODUTOR conferiu o item pelo `/p/:token` (PR B). `bate: false` no payload
  // é a divergência que abre pendência para o comprador.
  ITEM_CONFERIDO: "ITEM_CONFERIDO",
  // O produtor criou conta pelo link e este item virou venda no Cacho dele (PR B).
  // O payload guarda `itens`/`valor` do registro próprio: quando o `formalizar`
  // troca a natureza do item, é aqui que o histórico do que o comprador havia
  // anotado sobrevive.
  ITEM_ADOTADO: "ITEM_ADOTADO",
  // ─── C4 / C8b — o dinheiro, nas duas direções ─────────────────────────────
  //
  // Informado ≠ confirmado, e quem RECEBE é quem confirma (regra 5). Os três
  // primeiros são o comprador pagando o produtor; os três últimos, o destino
  // pagando o comprador. Mesma máquina (`recomputarStatusDinheiro`), nomes
  // trocados — ver status-dinheiro.ts.
  PAGAMENTO_INFORMADO: "PAGAMENTO_INFORMADO",
  PAGAMENTO_CONFIRMADO: "PAGAMENTO_CONFIRMADO",
  PAGAMENTO_CONTESTADO: "PAGAMENTO_CONTESTADO",
  RECEBIMENTO_INFORMADO: "RECEBIMENTO_INFORMADO",
  RECEBIMENTO_CONFIRMADO: "RECEBIMENTO_CONFIRMADO",
  RECEBIMENTO_CONTESTADO: "RECEBIMENTO_CONTESTADO",
  // O link (da carga ou do item) foi para o WhatsApp. Um evento só para os dois:
  // o payload diz qual, e dois tipos separados só multiplicariam o `switch`.
  LINK_ENVIADO: "LINK_ENVIADO",
} as const;

// ─── Vínculos produtor ↔ corretor (C2) ───────────────────────────────────────
//
// Quem compra de quem. Nasce ATIVO no claim: o corretor provou posse do link e o
// produtor já tinha compartilhado com aquele número — não há o que aceitar.
// 'PENDENTE' existe para o convite no sentido inverso, que ainda não temos.
//
// ⚠️ O vínculo NÃO dá acesso a nada do Cacho. Ele responde duas perguntas: "de quais
// produtores este corretor recebe romaneio" e "quais corretores compram desta
// fazenda". O que o corretor lê continua sendo `mercado_compartilhamentos`, um a um,
// filtrado por `org_compradora_id`.
export const mercadoVinculos = pgTable("mercado_vinculos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgProdutorId: varchar("org_produtor_id").notNull().references(() => organizations.id),
  orgCompradoraId: varchar("org_compradora_id").notNull().references(() => organizations.id),
  status: text("status").notNull().default("ATIVO"),
  criadoPorOrgId: varchar("criado_por_org_id").notNull(),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
  aceitoEm: timestamp("aceito_em", { withTimezone: true }),
  encerradoEm: timestamp("encerrado_em", { withTimezone: true }),
  // NULL = o produtor ainda não viu o card "Fulano criou acesso e agora acompanha
  // suas cargas". Coluna, e não tabela de notificações: este é o único aviso do
  // módulo hoje, e criar o mecanismo genérico antes do segundo caso seria inventar
  // infraestrutura sem cliente.
  avisadoProdutorEm: timestamp("avisado_produtor_em", { withTimezone: true }),
}, (t) => [
  uniqueIndex("uniq_vinculo").on(t.orgProdutorId, t.orgCompradoraId),
  index("idx_vinculo_produtor").on(t.orgProdutorId, t.status),
  index("idx_vinculo_compradora").on(t.orgCompradoraId, t.status),
]);

export type MercadoVinculo = typeof mercadoVinculos.$inferSelect;
export type InsertMercadoVinculo = typeof mercadoVinculos.$inferInsert;

export const MERCADO_VINCULO_STATUS = {
  PENDENTE: "PENDENTE",
  ATIVO: "ATIVO",
  ENCERRADO: "ENCERRADO",
} as const;

/**
 * `organizations.tipo` — o PAPEL da organização no Mercado. PRODUTOR é o default
 * de toda org existente.
 *
 * ⚠️ O valor 'CORRETOR' NÃO EXISTE MAIS (migração 057). Quem compra do produtor
 * pode ser corretor ou distribuidor, e os dois usam a mesma visão, as mesmas
 * rotas e a mesma máquina de estados — a diferença é de APRESENTAÇÃO, não de
 * papel, e mora em `perfil_comprador`. Manter dois valores de `tipo` para a mesma
 * coisa duplicaria todo gate do módulo pela vida inteira do produto.
 */
export const ORG_TIPO = { PRODUTOR: "PRODUTOR", COMPRADOR: "COMPRADOR" } as const;
export type OrgTipo = keyof typeof ORG_TIPO;

/**
 * `organizations.perfil_comprador` e `compradores.perfil` — como quem compra se
 * apresenta. NÃO é papel: não gateia rota nenhuma, não muda tela. É o chip na
 * lista do produtor e, no C5, a base da conversa sobre margem (que é do corretor,
 * não do distribuidor).
 */
export const PERFIL_COMPRADOR = { CORRETOR: "CORRETOR", DISTRIBUIDOR: "DISTRIBUIDOR" } as const;
export type PerfilComprador = keyof typeof PERFIL_COMPRADOR;

export const PERFIS_COMPRADOR: PerfilComprador[] = ["CORRETOR", "DISTRIBUIDOR"];

/** "Corretor" · "Distribuidor" — o rótulo do chip. */
export function labelPerfilComprador(perfil: string | null | undefined): string {
  return perfil === "DISTRIBUIDOR" ? "Distribuidor" : "Corretor";
}

// ─── Conferência do comprador (migração 057 — Mercado C3) ────────────────────
//
// O que o COMPRADOR contou na balança dele. Uma linha por compartilhamento.
//
// ⚠️ NÃO TOCA NA VENDA. O romaneio continua sendo o que o produtor emitiu;
// conferir registra a contagem do outro lado. A plataforma REGISTRA a divergência,
// não a arbitra — não existe botão de aceite para o produtor (decisão 10/09: vale
// o kg do comprador, e o produtor decide o que fazer com isso fora do app).
//
// ⚠️ `precoKg` dentro de `itensReais` vem do ROMANEIO, sempre. O que se confere é
// KG. Aceitar preço do cliente aqui daria ao comprador uma via para reescrever o
// valor da nota sem passar pelo produtor — e `valorConferido` é calculado no
// servidor justamente para que essa via não exista nem por engano.
export const mercadoConferencias = pgTable("mercado_conferencias", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // UNIQUE: uma conferência por romaneio. Conferir de novo ATUALIZA (o comprador
  // recontou); o histórico de quem mudou o quê é `mercado_eventos`, append-only.
  compartilhamentoId: varchar("compartilhamento_id").notNull().unique().references(() => mercadoCompartilhamentos.id),
  itensReais: jsonb("itens_reais").$type<ItemConferido[]>().notNull(),
  valorConferido: decimal("valor_conferido", { precision: 14, scale: 2 }).notNull(),
  /** true = kg e R$/kg batem com o romaneio → `status_carga` CONFERIDO; false → AJUSTADO. */
  bate: boolean("bate").notNull(),
  observacao: text("observacao"),
  /** NULL quando a conferência veio do link público — lá não há sessão. */
  registradoPorUserId: varchar("registrado_por_user_id").references(() => users.id),
  /** `destino_telefone` do compartilhamento, quando a origem é PUBLICO. */
  registradoPorTelefone: text("registrado_por_telefone"),
  origem: text("origem").notNull().default("APP"),
  registradoEm: timestamp("registrado_em", { withTimezone: true }).defaultNow().notNull(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }),
  // Preenchida quando o produtor CORRIGE a venda depois da conferência: ela deixa
  // de ser base do valor e vira histórico. Não é DELETE de propósito — apagar
  // esconderia que houve divergência.
  invalidadaEm: timestamp("invalidada_em", { withTimezone: true }),
}, (t) => [
  index("idx_conferencias_comp").on(t.compartilhamentoId),
]);

export type MercadoConferencia = typeof mercadoConferencias.$inferSelect;
export type InsertMercadoConferencia = typeof mercadoConferencias.$inferInsert;

/**
 * Uma linha da conferência do comprador. `precoKg` é o que ELE registrou — o do
 * romaneio quando bate (sempre, antes de 24/09). Ver `shared/mercado-conferencia.ts`.
 */
export interface ItemConferido {
  variedade: string;
  qualidade: string | null;
  kg: number;
  precoKg: number;
}

export const CONFERENCIA_ORIGEM = { APP: "APP", PUBLICO: "PUBLICO" } as const;
export type ConferenciaOrigem = keyof typeof CONFERENCIA_ORIGEM;

// ─── Cotações da praça (migração 056 — Mercado PR 2) ─────────────────────────
//
// ⚠️ AS DUAS TABELAS ABAIXO NÃO TÊM `org_id`, E NÃO PODEM GANHAR UMA.
//
// A praça é UMA só e quem publica é o Rizoma (usuário de plataforma). Elas são a
// exceção nomeada do multi-tenant: leitura pública, escrita só de plataforma —
// ver o comentário em server/middleware/tenant.ts. Preço de corretor e preço por
// org são explicitamente fora de escopo; uma coluna de org aqui reabriria essa
// porta pelos fundos.

/** O mercado físico onde o preço se forma. `slug` é a chave — aparece na
 *  querystring pública (`?praca=irece`) e em `organizations.praca`. */
export const mercadoPracas = pgTable("mercado_pracas", {
  slug: text("slug").primaryKey(),
  nome: text("nome").notNull(),
  uf: text("uf").notNull(),
  ativa: boolean("ativa").notNull().default(true),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
});

export type MercadoPraca = typeof mercadoPracas.$inferSelect;
export type InsertMercadoPraca = typeof mercadoPracas.$inferInsert;

/**
 * Um preço por (praça, variedade, qualidade, data).
 *
 * `variedade` + `qualidade` são o PRODUTO: Prata 1ª/2ª/3ª e Nanica (qualidade
 * NULL). A tradução item de venda → produto mora em server/lib/mercado/produtos.ts,
 * num lugar só — nunca comparada no olho em cada agregação.
 *
 * ⚠️ O índice único usa `COALESCE(qualidade, '')`: dois NULLs nunca são iguais no
 * Postgres, então um UNIQUE direto deixaria a Nanica duplicar a cada republicação
 * do mesmo dia. Drizzle não expressa índice por expressão — a verdade é o SQL da
 * migração 056; o upsert (`ON CONFLICT`) referencia o índice por nome.
 */
export const mercadoCotacoes = pgTable("mercado_cotacoes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  praca: text("praca").notNull().references(() => mercadoPracas.slug),
  variedade: text("variedade").notNull(),
  qualidade: text("qualidade"),
  precoKg: decimal("preco_kg", { precision: 10, scale: 2 }).notNull(),
  data: date("data").notNull(),
  fonte: text("fonte"),
  observacao: text("observacao"),
  publicadoPorUserId: varchar("publicado_por_user_id").notNull().references(() => users.id),
  /**
   * Até quando o Rizoma considera este preço fresco (migração 058). Default de
   * 6 dias no upsert — a semana da praça.
   *
   * ⚠️ NÃO TIRA O PREÇO DO AR, e nenhuma leitura pode passar a filtrar por ela.
   * "Cotação da praça" é sempre a última com `data ≤ hoje` — nunca há buraco
   * (regra do Hugo, 11/09). Vencida é um CHIP no quadro e uma confirmação a mais
   * para publicar oferta; filtrar aqui criaria o buraco que a regra evita.
   */
  validaAte: date("valida_ate").notNull(),
  /**
   * O que a associação DIVULGOU: `PRODUTOR` (o que o produtor recebe) ou
   * `VENDA` (o valor da carga embalada, do qual o mercado desconta embalagem e
   * corretagem) — migração 059.
   *
   * ⚠️ NADA converte um no outro. Não existe campo de desconto e não existe
   * função que derive preço ao produtor de uma placa de venda: em Mirorós o
   * desconto varia de forma imprevisível (2ª a 1,10 na placa e 0,60 pagos; a
   * Nanica às vezes sem desconto nenhum). O preço ao produtor só existe quando
   * a associação publica com esse rótulo, e no ROMANEIO.
   *
   * É esta etiqueta que decide a FORMA da comparação nas telas: com PRODUTOR as
   * bases batem e o delta sai em %; com VENDA sai em R$/kg de diferença, sem
   * juízo de valor.
   */
  referencial: text("referencial").notNull().default("PRODUTOR"),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }),
}, (t) => [
  index("idx_cotacoes_praca_data").on(t.praca, t.data),
]);

export type MercadoCotacao = typeof mercadoCotacoes.$inferSelect;
export type InsertMercadoCotacao = typeof mercadoCotacoes.$inferInsert;

/**
 * O referencial de uma publicação de preço. Dois valores, e não três: "não sei
 * o que é" não existe no mundo — quem publica sabe o que está divulgando, e um
 * terceiro estado obrigaria toda tela a desenhar um caso que nunca acontece.
 */
export const REFERENCIAL_COTACAO = { PRODUTOR: "PRODUTOR", VENDA: "VENDA" } as const;
export type ReferencialCotacao = keyof typeof REFERENCIAL_COTACAO;

export function ehReferencial(v: unknown): v is ReferencialCotacao {
  return v === "PRODUTOR" || v === "VENDA";
}

/** "ao produtor" · "preço de venda" — o texto do chip, um lugar só. */
export function labelReferencial(r: string | null | undefined): string {
  return r === "VENDA" ? "preço de venda" : "ao produtor";
}

// ─── Ofertas na praça (migração 058 — Mercado PR 3) ──────────────────────────
//
// A carga que o produtor coloca na praça: ANÔNIMA, AO PREÇO DA PRAÇA, resposta
// só em KG. Não existe negociação de preço na v1 (`propostaPrecoKg` é v2 e não
// tem UI).
//
// ⚠️ OFERTA NÃO É VENDA. Nada nasce no Cacho enquanto o produtor não FECHAR N kg
// com um comprador — e aí é uma venda RASCUNHO (número só na emissão). A mesma
// regra do C1 para o compartilhamento: o ato explícito é que cria.
//
// ⚠️ `kgFechado` da oferta é DERIVADO (Σ respostas FECHADO), nunca coluna: duas
// verdades sobre "quanto já saiu" divergem no primeiro fechamento concorrente.
// O `status` é recomputado pela função única `recomputarStatusOferta`.

export const mercadoOfertas = pgTable("mercado_ofertas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgProdutorId: varchar("org_produtor_id").notNull().references(() => organizations.id),
  /** 'OF-0001', sequencial POR ORG — ver `proximoIdentificador`. */
  identificador: text("identificador").notNull(),
  praca: text("praca").notNull().references(() => mercadoPracas.slug),
  variedade: text("variedade").notNull(),
  /**
   * O volume ESTIMADO da carga (059). "Estimado" é conteúdo, não ressalva: o kg
   * real sai da pesagem, e prometer kg exato numa carga que ainda vai ser
   * classificada cria atrito no carregamento.
   */
  volumeKg: integer("volume_kg").notNull(),
  /**
   * % de PRIMEIRA (059) — um número, não três faixas. É assim que se oferta
   * banana na praça: "oito mil quilos, setenta por cento de primeira". Os três
   * sliders existiam para fatiar o kg em itens de venda, e a venda agora nasce
   * sem itens.
   */
  pctPrimeira: decimal("pct_primeira", { precision: 5, scale: 2 }).notNull(),
  disponivelEm: date("disponivel_em").notNull(),
  /**
   * ⚠️ MORTA desde o PR `feat/mercado-identidade-vinculo`: identidade vem do
   * vínculo; dropar na próxima limpeza.
   *
   * Sem escritor e sem leitor. Ela guardava uma escolha por oferta ("mostrar
   * meu nome na chamada") que o canal já tomava: quem tem vínculo ativo recebe
   * romaneio do produtor e já sabe quem ele é. A condição da tela Praça passou
   * a ser só o vínculo, e a rota de criar parou de escrever — as linhas novas
   * ficam com o DEFAULT e as antigas com o valor histórico, que ninguém lê.
   *
   * A coluna fica: este PR não precisa dela dropada para ficar correto, e uma
   * coluna órfã coberta por teste é mais barata de reverter que uma dropada.
   */
  mostrarNomeVinculados: boolean("mostrar_nome_vinculados").notNull().default(false),
  /** Gerado na publicação — é o `/o/:token`. NULL enquanto rascunho. */
  token: text("token"),
  status: text("status").notNull().default("RASCUNHO"),
  publicadaEm: timestamp("publicada_em", { withTimezone: true }),
  encerradaEm: timestamp("encerrada_em", { withTimezone: true }),
  criadoPorUserId: varchar("criado_por_user_id").notNull(),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  uniqueIndex("mercado_ofertas_org_identificador_key").on(t.orgProdutorId, t.identificador),
  uniqueIndex("mercado_ofertas_token_key").on(t.token),
  index("idx_ofertas_org").on(t.orgProdutorId, t.criadoEm),
  index("idx_ofertas_praca").on(t.praca, t.status),
]);

export type MercadoOferta = typeof mercadoOfertas.$inferSelect;
export type InsertMercadoOferta = typeof mercadoOfertas.$inferInsert;

/**
 * Quem respondeu a uma oferta. A chave é o TELEFONE, não a conta: a chamada vai
 * para o grupo da praça e para números soltos, e quem responde normalmente não
 * tem conta no Rizoma.
 *
 * `orgCompradoraId` e `compradorId` são RESOLVIDOS a partir do telefone quando
 * dá — é o que faz a resposta pelo link e a resposta pela tela Praça caírem na
 * MESMA linha (o UNIQUE (oferta, telefone) é quem garante).
 */
export const mercadoOfertaRespostas = pgTable("mercado_oferta_respostas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ofertaId: varchar("oferta_id").notNull().references(() => mercadoOfertas.id, { onDelete: "cascade" }),
  /** Só dígitos, sem o 55 — `chaveTelefone`, a mesma identidade do login. */
  telefone: text("telefone").notNull(),
  nomeInformado: text("nome_informado"),
  orgCompradoraId: varchar("org_compradora_id").references(() => organizations.id),
  compradorId: varchar("comprador_id").references(() => compradores.id),
  abertoEm: timestamp("aberto_em", { withTimezone: true }),
  /**
   * Quando ele disse que TEM INTERESSE. Não há mais kg nem "quero tudo" (059):
   * quanto e quando se acerta entre os dois, e a plataforma registra quem quis
   * — não negocia.
   */
  respondidoEm: timestamp("respondido_em", { withTimezone: true }),
  detalhesAbertosEm: timestamp("detalhes_abertos_em", { withTimezone: true }),
  dispensadoEm: timestamp("dispensado_em", { withTimezone: true }),
  fechadoEm: timestamp("fechado_em", { withTimezone: true }),
  vendaId: varchar("venda_id"),
  status: text("status").notNull().default("ABRIU"),
}, (t) => [
  uniqueIndex("uniq_resposta").on(t.ofertaId, t.telefone),
]);

export type MercadoOfertaResposta = typeof mercadoOfertaRespostas.$inferSelect;
export type InsertMercadoOfertaResposta = typeof mercadoOfertaRespostas.$inferInsert;

/**
 * O estado da oferta: `RASCUNHO → NA_PRACA → FECHADA | ENCERRADA`.
 *
 * ⚠️ PARCIAL SAIU (059). Ele existia porque o fechamento era em kg e sobrava
 * saldo; sem kg, fechar com um comprador fecha a carga — quem quiser dividir
 * publica duas ofertas, que é o que ele faria no WhatsApp de qualquer forma.
 *
 * ⚠️ Recomputado por `recomputarStatusOferta` (server/lib/mercado/status-oferta.ts),
 * nunca escrito à mão em rota: "já fechou" e "a data passou" são as duas
 * transições automáticas, e cada cópia esqueceria uma.
 */
export const OFERTA_STATUS = {
  RASCUNHO: "RASCUNHO",
  NA_PRACA: "NA_PRACA",
  FECHADA: "FECHADA",
  ENCERRADA: "ENCERRADA",
} as const;
export type OfertaStatus = keyof typeof OFERTA_STATUS;

/**
 * O estado de uma resposta. ABRIU é quem tocou no link e não disse quanto quer —
 * a linha existe para o produtor ver interesse sem pedido ("abriu dom 21:14 ·
 * sem resposta").
 */
export const OFERTA_RESPOSTA_STATUS = {
  ABRIU: "ABRIU",
  QUER: "QUER",
  DETALHES_ABERTOS: "DETALHES_ABERTOS",
  FECHADO: "FECHADO",
  DISPENSADO: "DISPENSADO",
} as const;
export type OfertaRespostaStatus = keyof typeof OFERTA_RESPOSTA_STATUS;

// ─── A carga do comprador (migração 062 — Mercado C5a) ───────────────────────
//
// A carga é O MESMO DOCUMENTO que o romaneio, um nível acima: o romaneio é "o
// que um produtor entregou", a carga é "o que subiu no caminhão", de 1..N
// produtores. Quem forma emite, o destino confere, o vencimento é bilateral,
// quem recebe confirma — a mesma máquina, com o papel de emissor trocado.
//
// ⚠️ OS DERIVADOS NÃO TÊM COLUNA. Custo, kg líquido, despesas, margem e saldo
// saem de server/lib/mercado/comprador/derivados.ts a cada leitura. As únicas
// exceções são os snapshots tirados ao FECHAR (`valorVenda`, `pagamentoSnapshot`),
// e reabrir os limpa.

/** De quem o comprador compra — com ou sem conta no Rizoma. */
export const mercadoCompradorProdutores = pgTable("mercado_comprador_produtores", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgCompradoraId: varchar("org_compradora_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  /**
   * Como o comprador chama essa pessoa no caderno: "Zé do Alto", "compadre".
   * `resolverProdutor` procura aqui também — é o que evita o terceiro cadastro do
   * mesmo produtor sob o terceiro apelido, e com ele a pergunta "quanto devo ao
   * Zé" com três respostas.
   */
  apelidos: text("apelidos").array().notNull().default(sql`'{}'::text[]`),
  /**
   * A CHAVE de deduplicação (`chaveTelefone`, sem o 55) — e NULLABLE de propósito:
   * o comprador sabe o nome antes do número, e exigir telefone para registrar uma
   * carga é o campo obrigatório que faz o usuário digitar "00000000".
   */
  telefone: text("telefone"),
  cidade: text("cidade"),
  /** Preenchido quando o produtor tem conta: pelo vínculo, ou pela adoção (PR B). */
  orgProdutorId: varchar("org_produtor_id").references(() => organizations.id),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }),
}, (t) => [
  index("idx_comprador_produtores").on(t.orgCompradoraId, t.nome),
]);

export type MercadoCompradorProdutor = typeof mercadoCompradorProdutores.$inferSelect;
export type InsertMercadoCompradorProdutor = typeof mercadoCompradorProdutores.$inferInsert;

/**
 * Para quem o comprador vende — o espelho de `compradores`, do outro lado do
 * balcão. `kgCaixa` é do DESTINO e não da carga: cada distribuidor conta em caixa
 * de um peso, e é o peso dele que tem de sair no PDF (PR B) para a conferência
 * fechar sem conta de cabeça.
 */
export const mercadoCompradorDestinos = pgTable("mercado_comprador_destinos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgCompradoraId: varchar("org_compradora_id").notNull().references(() => organizations.id),
  nome: text("nome").notNull(),
  cidade: text("cidade"),
  telefone: text("telefone"),
  kgCaixa: integer("kg_caixa"),
  /** Preenchido quando o distribuidor cria conta pelo `/c/:token` (PR B). */
  orgDestinoId: varchar("org_destino_id").references(() => organizations.id),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  index("idx_comprador_destinos").on(t.orgCompradoraId),
]);

export type MercadoCompradorDestino = typeof mercadoCompradorDestinos.$inferSelect;
export type InsertMercadoCompradorDestino = typeof mercadoCompradorDestinos.$inferInsert;

export const mercadoCargas = pgTable("mercado_cargas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orgFormadoraId: varchar("org_formadora_id").notNull().references(() => organizations.id),
  /** 'CG-0001', sequencial POR ORG — ver `proximoIdentificadorCarga`. */
  identificador: text("identificador").notNull(),
  /**
   * EM_FORMACAO | FECHADA | ENTREGUE.
   *
   * ⚠️ Eixo SEPARADO de `statusRecebimento` (PR D). "Entregue e não paga" é o
   * estado mais comum de todos; um status único teria que enumerar o produto
   * cartesiano dos dois eixos.
   */
  status: text("status").notNull().default("EM_FORMACAO"),
  /** NULL = carga MISTA, não "não informado" — Prata e Nanica no mesmo caminhão é rotina. */
  variedade: text("variedade"),
  praca: text("praca").references(() => mercadoPracas.slug),
  dataCarregamento: date("data_carregamento"),
  alvoKg: integer("alvo_kg"),
  /** O que a balança do destino acusou. A diferença com o líquido é informação, não erro. */
  kgBruto: integer("kg_bruto"),
  placa: text("placa"),
  motoristaNome: text("motorista_nome"),
  motoristaTelefone: text("motorista_telefone"),
  observacao: text("observacao"),
  destinoContatoId: varchar("destino_contato_id").references(() => mercadoCompradorDestinos.id),
  precoVendaKg: decimal("preco_venda_kg", { precision: 10, scale: 2 }),
  /**
   * SNAPSHOT ao fechar: `precoVendaKg × kg líquido`. NULL enquanto EM_FORMACAO —
   * lá o valor é DERIVADO e a tela o marca "estimado", porque os kg finais saem
   * da pesagem. Reabrir limpa.
   */
  valorVenda: decimal("valor_venda", { precision: 14, scale: 2 }),
  /** Bilateral (regra 6): os dois lados escrevem, vale o último. */
  vencimentoVenda: date("vencimento_venda"),
  vencimentoVendaDefinidoPor: text("vencimento_venda_definido_por"),
  vencimentoVendaAlteradoEm: timestamp("vencimento_venda_alterado_em", { withTimezone: true }),
  /** Cópia de `organizations.dadosPagamento` ao fechar — trocar a chave PIX amanhã
   *  não pode reescrever o que o distribuidor leu no documento de ontem. */
  pagamentoSnapshot: jsonb("pagamento_snapshot"),
  /**
   * Libera as DESPESAS no link do destino, e NADA MAIS. Custo e margem jamais vão
   * para o `/c/:token` — ver `CHAVES_DTO_CARGA_PUBLICA`.
   */
  mostrarDespesas: boolean("mostrar_despesas").notNull().default(false),
  /**
   * O que aparece no DOCUMENTO da carga (074) — escolha POR CARGA, não da org.
   *
   * ⚠️ `mostrarProdutores` nasce TRUE: esconder por padrão seria decidir pelo
   * corretor que ele tem algo a esconder, e a procedência é o que faz o cliente
   * conferir a carga na chegada. Desligado, o documento diz "Produtor 1, 2, 3…"
   * na ordem da tabela — rastreável na conferência sem entregar a carteira.
   *
   * ⚠️ Por carga e não por organização porque o mesmo corretor mostra os
   * produtores para o cliente de sempre e esconde para o comprador novo.
   */
  mostrarProdutores: boolean("mostrar_produtores").notNull().default(true),
  /** Peso da balança do destino e a diferença para o líquido (074). */
  mostrarBruto: boolean("mostrar_bruto").notNull().default(true),
  token: text("token"),
  tokenExpiraEm: timestamp("token_expira_em", { withTimezone: true }),
  /** Reservado (C9). Sem FK: a tabela não existe, e FK para tabela futura quebra o deploy. */
  pedidoId: varchar("pedido_id"),
  /**
   * O estado do dinheiro que ENTRA (migração 064) — o espelho de
   * `mercado_compartilhamentos.status_pagamento`, do lado de quem recebe.
   *
   * ⚠️ Eixo SEPARADO de `status`: "entregue e não paga" é o estado mais comum de
   * todos, e um status único teria que enumerar o produto cartesiano dos dois.
   *
   * ⚠️ MATERIALIZADO, com UM escritor só (`aplicarStatusRecebimento`).
   */
  statusRecebimento: text("status_recebimento").notNull().default("A_RECEBER"),
  fechadaEm: timestamp("fechada_em", { withTimezone: true }),
  entregueEm: timestamp("entregue_em", { withTimezone: true }),
  criadoPorUserId: varchar("criado_por_user_id").notNull(),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  uniqueIndex("mercado_cargas_org_identificador_key").on(t.orgFormadoraId, t.identificador),
  uniqueIndex("mercado_cargas_token_key").on(t.token),
  index("idx_cargas_org").on(t.orgFormadoraId, t.status, t.dataCarregamento),
]);

export type MercadoCarga = typeof mercadoCargas.$inferSelect;
export type InsertMercadoCarga = typeof mercadoCargas.$inferInsert;

/**
 * O item da carga, em DUAS NATUREZAS — e a segunda não é um caso degradado da
 * primeira:
 *
 *   1. `compartilhamentoId` — romaneio FORMALIZADO por um produtor do Rizoma.
 *   2. `itens` + `valor` — REGISTRO PRÓPRIO do comprador, para produtor sem conta.
 *
 * É a natureza 2 que faz a carga FECHAR. Uma carga de 8 produtores em que 1 usa o
 * app não é uma carga — é um romaneio com ruído em volta, e o comprador voltaria
 * para o caderno. As duas apontam para a MESMA carteira (`produtorContatoId`), e
 * é isso que deixa o produtor sem conta virar produtor com conta sem perder
 * histórico.
 */
export const mercadoCargaItens = pgTable("mercado_carga_itens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  /**
   * O VÍNCULO com a carga, não a identidade do item (migração 073).
   *
   * ⚠️ NULL é estado normal do REGISTRO PRÓPRIO: o corretor registra o romaneio
   * na beira do caminhão e decide a carga depois. Entrar, sair e trocar de carga
   * não apaga o romaneio — `removerItem` põe `carga_id = NULL` na natureza 2.
   *
   * ⚠️ Natureza 1 (romaneio formalizado) EXIGE carga, e o CHECK
   * `mercado_carga_itens_romaneio_em_carga` garante: fora de uma carga ela já
   * existe como venda no Cacho do produtor, e este item é só a linha de ligação.
   */
  cargaId: varchar("carga_id").references(() => mercadoCargas.id, { onDelete: "cascade" }),
  /**
   * A ORG DONA DO ITEM — sempre, nas duas naturezas, com e sem carga (073).
   *
   * ⚠️ É O FILTRO DE TENANT. Antes da 073 todo filtro chegava na org pela carga
   * (`mercado_cargas.org_formadora_id`), e item sem carga não teria por onde
   * chegar: um avulso da org A apareceria para a org B. NUNCA leia item por
   * `carga_id` esperando isolamento — leia por esta coluna.
   *
   * `notNull()` no tipo, CHECK `..._org_obrigatoria` no banco: a coluna nasceu
   * anulável para o backfill e a constraint é que a fecha.
   */
  orgCompradoraId: varchar("org_compradora_id").notNull().references(() => organizations.id),
  /**
   * A DATA DO DOCUMENTO do registro próprio (073) — o dia em que carregou.
   *
   * Era `mercado_cargas.data_carregamento` (a regra do `pagar.ts`), e agora é do
   * item: é ela que define "do mês" e "da semana" em toda leitura, e é ela que
   * `checarData` usa para recusar pagamento anterior ao documento.
   */
  dataDocumento: date("data_documento"),
  /** O número do talão de papel do corretor ("2214"). Opcional — nem todo mundo numera. */
  numeroBloco: text("numero_bloco"),
  /** SEMPRE preenchido, nas duas naturezas — ver o comentário da tabela. */
  produtorContatoId: varchar("produtor_contato_id").notNull().references(() => mercadoCompradorProdutores.id),
  compartilhamentoId: varchar("compartilhamento_id").references(() => mercadoCompartilhamentos.id),
  itens: jsonb("itens").$type<ItemRegistrado[]>(),
  /**
   * Σ kg × precoKg, calculado NO SERVIDOR (`valorDoRegistro`). GRAVADO, e não
   * derivado do JSONB a cada leitura, porque é a base do pagamento (PR C): o
   * número que o produtor conferiu no link não pode mudar porque uma leitura
   * arredondou diferente.
   */
  valor: decimal("valor", { precision: 14, scale: 2 }),
  vencimento: date("vencimento"),
  vencimentoDefinidoPor: text("vencimento_definido_por"),
  vencimentoAlteradoEm: timestamp("vencimento_alterado_em", { withTimezone: true }),
  /** `/p/:token` — o link do produtor SEM conta (PR B). */
  token: text("token"),
  tokenExpiraEm: timestamp("token_expira_em", { withTimezone: true }),
  conferidoEm: timestamp("conferido_em", { withTimezone: true }),
  conferenciaObs: text("conferencia_obs"),
  /**
   * PR B: o item virou venda no Cacho do produtor que criou conta pelo link. É o
   * fio que permite ao `formalizar` reconhecer a venda adotada e TROCAR a
   * natureza deste item, em vez de criar um segundo item para a mesma carga.
   */
  vendaAdotadaId: varchar("venda_adotada_id"),
  adotadoEm: timestamp("adotado_em", { withTimezone: true }),
  /**
   * O espelho de `mercado_compartilhamentos.status_pagamento` (migração 063),
   * do outro lado da carga.
   *
   * ⚠️ MATERIALIZADO, não fonte da verdade: quem decide é
   * `recomputarStatusDinheiro` sobre os lançamentos, e esta coluna existe para a
   * lista de "A pagar" não recomputar N status a cada leitura. UM escritor só
   * (`aplicarStatusPagamento`), sempre junto da escrita em `mercado_pagamentos`.
   */
  statusPagamento: text("status_pagamento").notNull().default("A_PAGAR"),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  // Regra 13: um romaneio pertence a NO MÁXIMO UMA carga. Global, e não por
  // carga: o mesmo romaneio em duas cargas contaria o custo duas vezes e o
  // produtor receberia dois documentos do mesmo caminhão.
  uniqueIndex("uniq_item_compartilhamento").on(t.compartilhamentoId),
  uniqueIndex("mercado_carga_itens_token_key").on(t.token),
  index("idx_carga_itens_carga").on(t.cargaId),
  index("idx_carga_itens_contato").on(t.produtorContatoId),
  // A lista de Romaneios filtra por (org, data do documento) em toda abertura.
  index("idx_itens_org_data").on(t.orgCompradoraId, t.dataDocumento),
]);

export type MercadoCargaItem = typeof mercadoCargaItens.$inferSelect;
export type InsertMercadoCargaItem = typeof mercadoCargaItens.$inferInsert;

/**
 * Despesa do CAMINHÃO, não do produtor.
 *
 * ⚠️ Rateio de frete com o produtor é fora de escopo, e a ausência é decisão:
 * enquanto não houver regra de rateio acordada, uma coluna aqui viraria desconto
 * silencioso no que o produtor recebe.
 */
export const mercadoCargaDespesas = pgTable("mercado_carga_despesas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  cargaId: varchar("carga_id").notNull().references(() => mercadoCargas.id, { onDelete: "cascade" }),
  tipo: text("tipo").notNull(),
  descricao: text("descricao"),
  valor: decimal("valor", { precision: 14, scale: 2 }).notNull(),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  index("idx_carga_despesas").on(t.cargaId),
]);

export type MercadoCargaDespesa = typeof mercadoCargaDespesas.$inferSelect;
export type InsertMercadoCargaDespesa = typeof mercadoCargaDespesas.$inferInsert;

/**
 * Um LANÇAMENTO de dinheiro (migração 063 — Mercado C4).
 *
 * ⚠️ QUEM REGISTRA QUITA (migração 076): a linha nasce com `confirmado_em`
 * preenchido, venha de quem paga ou de quem recebe, e baixa o saldo na hora.
 * Até 22/09 valia a regra 5 — `confirmado_em` só era preenchido pela org que
 * RECEBIA — e o efeito em produção foi um romaneio pago que ficou "Informado"
 * nas duas telas, esperando uma segunda ação que ninguém fazia.
 *
 * ⚠️ `confirmado_por_user_id` continua distinguindo os dois casos: preenchido
 * quando quem registrou foi a org que recebe ("Recebi", do produtor), nulo
 * quando foi quem paga. "Quitado por quem registrou" e "confirmado por Hugo" não
 * são o mesmo fato.
 *
 * ⚠️ `contestado_em` e `contestacao_motivo` são HISTÓRICO: ninguém contesta mais
 * (as rotas saíram), mas o que já foi contestado continua sendo lido pela máquina
 * pura, e continua fora do `pago`.
 *
 * ⚠️ O alvo é um XOR: o romaneio formalizado OU o item de registro próprio — as
 * duas naturezas da carga, as duas dívidas.
 *
 * ⚠️ O STATUS não mora aqui. Ele é RECOMPUTADO de todos os lançamentos por
 * `recomputarStatusDinheiro` (puro) e materializado pelo escritor único
 * `aplicarStatusPagamento`, na mesma transação de qualquer escrita nesta tabela.
 */
export const mercadoPagamentos = pgTable("mercado_pagamentos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  compartilhamentoId: varchar("compartilhamento_id").references(() => mercadoCompartilhamentos.id),
  cargaItemId: varchar("carga_item_id").references(() => mercadoCargaItens.id),
  valor: decimal("valor", { precision: 14, scale: 2 }).notNull(),
  /**
   * QUANDO O DINHEIRO SAIU — informado por quem pagou, nunca `now()`. Mesma
   * regra de `vendas.paga_em` (061): gravar o instante do clique jogaria para
   * setembro um dinheiro que saiu em 28 de agosto, e o produtor só descobriria
   * ao fechar o mês.
   */
  dataPagamento: date("data_pagamento").notNull(),
  forma: text("forma").notNull(),
  /** Sem UI neste ciclo — a coluna nasce junto para não reprocessar depois. */
  comprovanteUrl: text("comprovante_url"),
  informadoPorOrgId: varchar("informado_por_org_id").notNull(),
  informadoPorUserId: varchar("informado_por_user_id"),
  informadoEm: timestamp("informado_em", { withTimezone: true }).defaultNow().notNull(),
  /** ⚠️ SEMPRE um usuário da org que RECEBE — a regra 5 numa coluna. */
  confirmadoPorUserId: varchar("confirmado_por_user_id"),
  confirmadoEm: timestamp("confirmado_em", { withTimezone: true }),
  /**
   * Registro próprio: o produtor não tem conta, então não há quem confirme. O
   * lançamento nasce confirmado e MARCADO — o comprador sabe que aquele "pago"
   * é palavra dele, não do produtor. É exceção nomeada, não furo na regra 5.
   */
  semContraparte: boolean("sem_contraparte").notNull().default(false),
  contestadoEm: timestamp("contestado_em", { withTimezone: true }),
  contestacaoMotivo: text("contestacao_motivo"),
  origem: text("origem").notNull().default("APP"),
}, (t) => [
  index("idx_pag_comp").on(t.compartilhamentoId),
  index("idx_pag_item").on(t.cargaItemId),
]);

export type MercadoPagamento = typeof mercadoPagamentos.$inferSelect;
export type InsertMercadoPagamento = typeof mercadoPagamentos.$inferInsert;

/**
 * PIX | TRANSFERENCIA | DINHEIRO | OUTRO — como o dinheiro andou.
 *
 * ⚠️ `FormaPagamento` (sem "Lancamento") JÁ EXISTE neste arquivo e é outra coisa:
 * a periodicidade da folha do Equipe (MENSAL | QUINZENAL). O nome longo é para
 * que ninguém importe um pelo outro — um `FormaPagamento` num lançamento de
 * romaneio passaria no tipo e escreveria "MENSAL" na coluna `forma`.
 */
export const FORMA_LANCAMENTO = {
  PIX: "PIX",
  TRANSFERENCIA: "TRANSFERENCIA",
  DINHEIRO: "DINHEIRO",
  OUTRO: "OUTRO",
} as const;
export type FormaLancamento = keyof typeof FORMA_LANCAMENTO;
export const FORMAS_LANCAMENTO: FormaLancamento[] = ["PIX", "TRANSFERENCIA", "DINHEIRO", "OUTRO"];
export const FORMA_LANCAMENTO_LABEL: Record<FormaLancamento, string> = {
  PIX: "PIX",
  TRANSFERENCIA: "Transferência",
  DINHEIRO: "Dinheiro",
  OUTRO: "Outro",
};

/**
 * O dinheiro que ENTRA na carga (migração 064 — Mercado C8b).
 *
 * ⚠️ A MESMA máquina do C4, com os papéis trocados: o DISTRIBUIDOR paga, o
 * CORRETOR que formou a carga confirma. Informado não baixa saldo; quem recebe é
 * quem confirma. Nenhuma linha daquela lógica é copiada — ver `receber.ts`.
 *
 * ⚠️ O ALVO É A CARGA, não o item. Do lado do produtor cada um tem a própria
 * dívida; do lado do destino, o que foi vendido é o CAMINHÃO INTEIRO, por um
 * preço por kg só. Um recebimento por item inventaria uma composição que o
 * distribuidor nunca viu.
 */
export const mercadoCargaRecebimentos = pgTable("mercado_carga_recebimentos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  cargaId: varchar("carga_id").notNull().references(() => mercadoCargas.id),
  valor: decimal("valor", { precision: 14, scale: 2 }).notNull(),
  /** QUANDO O DINHEIRO ENTROU — informado, nunca `now()`. */
  dataRecebimento: date("data_recebimento").notNull(),
  forma: text("forma").notNull(),
  /** A org FORMADORA (destino sem conta) ou a org do DESTINO (com conta). */
  informadoPorOrgId: varchar("informado_por_org_id").notNull(),
  informadoPorUserId: varchar("informado_por_user_id"),
  informadoEm: timestamp("informado_em", { withTimezone: true }).defaultNow().notNull(),
  /** ⚠️ SEMPRE um usuário da org FORMADORA — é ela que recebe o dinheiro da carga. */
  confirmadoPorUserId: varchar("confirmado_por_user_id"),
  confirmadoEm: timestamp("confirmado_em", { withTimezone: true }),
  /** Destino sem conta: o corretor lança e a linha nasce confirmada, e MARCADA. */
  semContraparte: boolean("sem_contraparte").notNull().default(false),
  contestadoEm: timestamp("contestado_em", { withTimezone: true }),
  contestacaoMotivo: text("contestacao_motivo"),
  origem: text("origem").notNull().default("APP"),
}, (t) => [
  index("idx_receb_carga").on(t.cargaId),
]);

export type MercadoCargaRecebimento = typeof mercadoCargaRecebimentos.$inferSelect;
export type InsertMercadoCargaRecebimento = typeof mercadoCargaRecebimentos.$inferInsert;

export const mercadoVinculosRelations = relations(mercadoVinculos, ({ one }) => ({
  produtor: one(organizations, {
    fields: [mercadoVinculos.orgProdutorId],
    references: [organizations.id],
  }),
}));

export const mercadoCompartilhamentosRelations = relations(mercadoCompartilhamentos, ({ one, many }) => ({
  venda: one(vendas, {
    fields: [mercadoCompartilhamentos.vendaId],
    references: [vendas.id],
  }),
  comprador: one(compradores, {
    fields: [mercadoCompartilhamentos.compradorId],
    references: [compradores.id],
  }),
  eventos: many(mercadoEventos),
}));

export const mercadoEventosRelations = relations(mercadoEventos, ({ one }) => ({
  compartilhamento: one(mercadoCompartilhamentos, {
    fields: [mercadoEventos.compartilhamentoId],
    references: [mercadoCompartilhamentos.id],
  }),
}));

// ─── Contratos de entrada ─────────────────────────────────────────────────────

export { normalizarTelefoneBR, telefoneWhatsappValido };

export const formalizarCompartilhamentoSchema = z.object({
  vendaId: z.string().uuid("Venda inválida"),
  telefone: z.string().refine(telefoneWhatsappValido, "Telefone inválido — informe DDD + 9 dígitos"),
  /**
   * Opcional AQUI, obrigatório na rota quando a venda está em aberto.
   *
   * Quem sabe se a venda já foi paga é o servidor, não o Zod — por isso a regra mora
   * no handler (POST /api/mercado/compartilhamentos) e não neste schema. O motivo é
   * o backfill do C2: o produtor formaliza vendas antigas em silêncio, e ninguém
   * lembra o prazo de uma carga de junho que já foi quitada. Para nota em aberto o
   * prazo continua obrigatório — é o número que o corretor mais precisa.
   */
  vencimento: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Vencimento inválido").nullish(),
});

export type FormalizarCompartilhamento = z.infer<typeof formalizarCompartilhamentoSchema>;

export const tokenPublicoParamSchema = z.object({
  token: z.string().min(8, "Link inválido").max(64, "Link inválido"),
});

// ═══════════════════════════════════════════════════════════════════════════
// AVISOS PELO WHATSAPP — o número do Rizoma (migração 078)
// ═══════════════════════════════════════════════════════════════════════════
//
// Aviso é mensagem do SISTEMA, que sai do número do Rizoma (GZappy) para quem
// tem conta. A mensagem da RELAÇÃO (romaneio, oferta, documento da carga)
// continua saindo do WhatsApp da própria pessoa por wa.me — não é isto.
//
// Os MODELOS são da plataforma (um número só), não da fazenda: o código define
// cada um (server/lib/avisos/catalogo.ts) e o admin da plataforma só edita o
// texto, pausa e restaura versões. O log do que SAIU do número continua em
// `agent_mensagem` (capacidade `aviso.<chave>`) — um lugar só para essa pergunta.
//
// O aviso NASCE do `mercado_eventos` por um RECONCILIADOR no cron, não de um
// gancho síncrono: evento é gravado por três helpers e por sete inserts diretos
// dentro de `dbBatch.batch`, e um gancho esquecido em qualquer um deles seria
// aviso mudo sem ninguém perceber. Ver server/lib/avisos/eventos.ts.

/** Cada publicação do editor é uma versão — nunca se reescreve histórico. */
export const avisoModeloVersao = pgTable("aviso_modelo_versao", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chave: text("chave").$type<AvisoChave>().notNull(),
  versao: integer("versao").notNull(),
  texto: text("texto").notNull(),
  textoGrupo: text("texto_grupo"),
  criadoPorUserId: varchar("criado_por_user_id").references(() => users.id),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  uniqueIndex("aviso_modelo_versao_chave_versao_key").on(t.chave, t.versao),
]);

export type AvisoModeloVersao = typeof avisoModeloVersao.$inferSelect;

/**
 * O estado de cada modelo. SEM LINHA = texto padrão do código e o "pausado por
 * padrão" do catálogo — é o que deixa um modelo novo nascer sem migração.
 */
export const avisoModeloEstado = pgTable("aviso_modelo_estado", {
  chave: text("chave").$type<AvisoChave>().primaryKey(),
  /** NULL = o texto padrão do código. */
  versaoAtiva: integer("versao_ativa"),
  pausado: boolean("pausado").notNull().default(false),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).defaultNow().notNull(),
  atualizadoPorUserId: varchar("atualizado_por_user_id").references(() => users.id),
});

export type AvisoModeloEstado = typeof avisoModeloEstado.$inferSelect;

/**
 * A fila — UMA linha por (evento ou agenda, destinatário, modelo). É também o
 * registro de "por que não saiu": SUPRIMIDA, CANCELADA e SEGURADA carregam o
 * `motivo`. O que de fato saiu do número é a linha de `agent_mensagem` apontada
 * por `agent_mensagem_id`; num grupo, a ENVIADA e as AGRUPADAS apontam para a
 * mesma mensagem.
 */
export const avisoFila = pgTable("aviso_fila", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chave: text("chave").$type<AvisoChave>().notNull(),
  orgDestinoId: varchar("org_destino_id").notNull().references(() => organizations.id),
  userDestinoId: varchar("user_destino_id").notNull().references(() => users.id),
  /** O número NA HORA de enfileirar — trocar o telefone depois não redireciona o que já está na fila. */
  telefone: text("telefone").notNull(),
  /** NULL nos agendados (lembrete, resumos). */
  eventoId: varchar("evento_id").references(() => mercadoEventos.id),
  /**
   * Quem sai junto: destinatário + modelo + contraparte (AGRUPADO), o lote do
   * informar em lote, `<chave>:<data>` nos agendados, ou o próprio evento
   * (IMEDIATO, que nunca agrupa).
   */
  grupoChave: text("grupo_chave").notNull(),
  /** As variáveis JÁ FORMATADAS (`vars`) e os números para somar no grupo (`num`). */
  dados: jsonb("dados").$type<{ vars: Record<string, string>; num?: Record<string, number> }>().notNull(),
  enviarApos: timestamp("enviar_apos", { withTimezone: true }).notNull(),
  status: text("status").$type<AvisoFilaStatus>().notNull().default("PENDENTE"),
  motivo: text("motivo"),
  agentMensagemId: varchar("agent_mensagem_id").references(() => agentMensagem.id),
  /** Sobe a cada "Tentar de novo" — entra na chave da reserva em `agent_mensagem`. */
  tentativa: integer("tentativa").notNull().default(0),
  enviadaEm: timestamp("enviada_em", { withTimezone: true }),
  criadoEm: timestamp("criado_em", { withTimezone: true }).defaultNow().notNull(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  index("aviso_fila_status_enviar_idx").on(t.status, t.enviarApos),
  index("aviso_fila_grupo_idx").on(t.grupoChave, t.status),
  index("aviso_fila_criado_idx").on(t.criadoEm),
  index("aviso_fila_user_enviada_idx").on(t.userDestinoId, t.enviadaEm),
  // 🔴 A IDEMPOTÊNCIA do enfileirar: o reconciliador relê a sobreposição do
  // cursor a cada tique, e é este índice que faz a segunda leitura não duplicar.
  uniqueIndex("aviso_fila_evento_idx").on(t.eventoId, t.userDestinoId, t.chave).where(sql`evento_id IS NOT NULL`),
  // O mesmo para os agendados: um lembrete por pessoa por dia (`grupo_chave` = `<chave>:<data>`).
  uniqueIndex("aviso_fila_agendado_idx").on(t.chave, t.userDestinoId, t.grupoChave).where(sql`evento_id IS NULL`),
]);

export type AvisoFila = typeof avisoFila.$inferSelect;
export type InsertAvisoFila = typeof avisoFila.$inferInsert;

/** O que cada pessoa quer receber. SEM LINHA = ligado. */
export const avisoPreferencia = pgTable("aviso_preferencia", {
  userId: varchar("user_id").notNull().references(() => users.id),
  categoria: text("categoria").$type<AvisoCategoria>().notNull(),
  ativo: boolean("ativo").notNull(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  primaryKey({ columns: [t.userId, t.categoria] }),
]);

export type AvisoPreferencia = typeof avisoPreferencia.$inferSelect;

/**
 * Linha ÚNICA (`id = 'unica'`) com as regras globais e o cursor do
 * reconciliador. Nasce com o envio DESLIGADO: ligar é uma decisão (o número é
 * o mesmo do AgroPlat), tomada na tela Regras da Plataforma.
 */
export const avisoRegras = pgTable("aviso_regras", {
  id: text("id").primaryKey().default("unica"),
  envioLigado: boolean("envio_ligado").notNull().default(false),
  silencioInicio: text("silencio_inicio").notNull().default("21:00"),
  silencioFim: text("silencio_fim").notNull().default("06:30"),
  janelaMin: integer("janela_min").notNull().default(10),
  limiteDia: integer("limite_dia").notNull().default(10),
  timezone: text("timezone").notNull().default("America/Bahia"),
  /** Até onde o reconciliador já leu `mercado_eventos`. Nasce no instante da migração: o passado não vira aviso. */
  eventosLidosAte: timestamp("eventos_lidos_ate", { withTimezone: true }).defaultNow().notNull(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).defaultNow().notNull(),
  atualizadoPorUserId: varchar("atualizado_por_user_id").references(() => users.id),
});

export type AvisoRegrasLinha = typeof avisoRegras.$inferSelect;
